import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { useToast } from '../components/ui/use-toast';
import { confirm } from '../components/ui/confirm-dialog';
import { useT } from '../context/LanguageContext';
import { splitPrice, taxNames } from '../lib/taxMath';
import { Plus, Trash2, AlertTriangle, ImagePlus, X, UtensilsCrossed, Coffee, PlusCircle, Package, Flame, ChevronLeft, ChevronRight, ChevronDown, Timer, Wheat, Droplet, Soup, Upload, Search, RefreshCw, ShoppingBag, Croissant, Wine, Pencil } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { createDish, updateDish, getDish, getDishes } from '../services/menuService';
import { listTaxLabels, listTaxGroups } from '../services/taxRateService';
// eslint-disable-next-line no-unused-vars
import { dishMastersService } from '../services/mastersService';
import { locationsService } from '../services/locationsService';
import { createIngredient, listAllergenIngredients } from '../services/ingredientService';
import { formatApiError } from '../utils/errorMessage';
import {
  groupsForType, computeHealthScore, LEVEL_OPTIONS,
  FOOD_METER_BASIS, BEVERAGE_METER_BASIS,
} from '../lib/nutrition';
import { dietOf, effectiveVariantDiet, dietMeta } from '../lib/diet';

// Small pill-shaped icon button used next to Tags / Sizes / Units labels for
// the "+ add new master item" action.
function IconBtn({ icon: Icon, onClick, title, tone = 'primary', disabled = false }) {
  const palette = disabled
    ? 'border-slate-200 bg-slate-100 text-slate-300 cursor-not-allowed'
    : tone === 'subdued'
      ? 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 hover:border-slate-300'
      : 'border-primary/30 bg-accent text-accent-foreground hover:bg-accent/70 hover:border-primary/50';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`inline-flex items-center justify-center h-7 w-7 rounded-md border transition-colors ${palette}`}
    >
      <Icon size={14} />
    </button>
  );
}

function AddEditDish() {
  const navigate = useNavigate();
  const { restaurantId } = useParams();
  const location = useLocation();
  const editingDish = location.state?.dish || null;
  // Type (Dish / Beverage / Add-on / Combo) is chosen on the previous screen —
  // the "Add Menu Item" dropdown in All Menu Items passes both `kind` and
  // `dishType`. So the form always opens with a type already decided and the
  // in-form selector is read-only (for new and existing items alike); changing
  // it after the fact would orphan variants, prices and menu links. `typeChosen`
  // stays true throughout — there's no longer an in-form picker step.
  const [typeChosen] = useState(true);
  // Which section tab is open. The form is split into tabs (see TAB_DEFS below)
  // so the user lands on a short screen instead of one long scroll.
  const [activeTab, setActiveTab] = useState('basic');
  const initialKind = location.state?.kind || 'dish';
  const dishType = location.state?.dishType || 'food';
  // Drinks (soft beverages + liquor) default to a millilitre serving.
  const isDrinkType = dishType === 'beverage' || dishType === 'liquor';
  const defaultUnit = isDrinkType ? 'ml' : 'plate';
  const defaultQuantity = isDrinkType ? '180' : '1';
  const navigationState = {
    activeTab: 'menu',
    activeMenuTab: dishType === 'beverage' ? 'beverages' : 'dishes',
  };

  // Sizes & units come from master data (DB). Keep a static fallback so the
  // form is still usable before the first load completes.
  const [sizeOptions, setSizeOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  // Brands master — fetched/synced (loadBrands). Used by the resale brand value
  // and the on-form Liquor brand picker.
  const [brandOptions, setBrandOptions] = useState([]);
  const [masterDialog, setMasterDialog] = useState(null); // { kind, onCreated }
  // Inline "create add-on" dialog launched from the Variants tab's Add On
  // columns. null | { name, price }; mirrors the Add On Items matrix flow.
  const [addonQuickDlg, setAddonQuickDlg] = useState(null);
  const [addonQuickSaving, setAddonQuickSaving] = useState(false);

  const firstSize = sizeOptions[0]?.value || 'full';

  // Title-case a leftover raw value, dropping any random code suffix (full_dcc1
  // → Full) so a stale value still reads cleanly if no master matches.
  const _titleCaseLabel = (s) => String(s || '')
    .replace(/_[a-z0-9]{3,6}$/i, '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

  // Resolve a stored size/unit value to a current master option, tolerating
  // legacy values: exact code, case-insensitive code, a code carrying a random
  // suffix ('full' → 'full_dcc1'), or a name match.
  const matchMaster = (value, options) => {
    if (!value) return null;
    const v = String(value).toLowerCase();
    return options.find((o) =>
      o.value === value
      || String(o.value).toLowerCase() === v
      || String(o.value).toLowerCase().startsWith(v + '_')
      || String(o.label).toLowerCase() === v
    ) || null;
  };

  const getSizeLabel = (value) => {
    const match = sizeOptions.find((option) => option.value === value)
      || matchMaster(value, sizeOptions);
    return match ? match.label : (value ? _titleCaseLabel(value) : 'Custom');
  };

  const buildVariant = (overrides = {}) => ({
    size: firstSize,
    quantity: defaultQuantity,
    unit: defaultUnit,
    // Default display-name source is the dish name itself — the form mirrors
    // dishForm.name into the row automatically (see name-change handler).
    variant_name: '',
    variant_name_source: 'dish',
    // Two add-on dimensions beyond size — the *names* of the selected Add on 1 /
    // Add on 2 items. The same size can repeat with different add-ons; the
    // display name combines the size/label with them as "Size · Add-on · Add-on".
    variant_option: '',
    variant_option_2: '',
    // Per-variant serve temperature for beverages ('hot' | 'cold' | 'both' | '').
    serve_temperature: '',
    // Coffee / milk-beverage prep, fixed per variant (milk beverages only).
    // shots = number of espresso shots; ice_level ('less'|'normal'|'extra') only
    // applies when the variant is served cold; sugar_level ('0'|'25'|'50'|'75'|'normal').
    shots: '',
    ice_level: '',
    sugar_level: '',
    // Coffee roast level (master-backed name, e.g. 'Medium'); milk beverages only.
    roast: '',
    price: '',
    is_seasonal: false,
    calories: '',
    serves: 1,
    preparation_time: 15,
    spicy_level: 0,
    is_available: true,
    display_order: 0,
    // -- Parcel-specific overrides
    parcel_instructions: '',
    parcel_cutlery_ids: [],
    // -- Per-variant fields edited via the ComplimentaryMatrix.
    // Shape: [{ dish_id, variant_id?, qty }]. variant_id is set for Dishes
    // and Beverages (since those have multiple sizes); Add-ons and Combos
    // use just { dish_id, qty: 1 }.
    complimentary_items: [],
    // recipe rows are { ingredient_id, quantity, unit } objects
    recipe: [],
    // Per-variant allergens — a flat list of allergen ids (master_allergens).
    // Edited via the inline ALLERGENS column in the variant table; independent
    // of the dish-level allergen tags.
    allergens: [],
    // location-scoped pricing — rows of
    // { location_id, price, takeaway_price, delivery_price }. The dish-level
    // selected locations drive which rows appear in the Price matrix.
    location_prices: [],
    // Add-on specific — Free overrides the price to 0, Brand points at the
    // BrandMaster row (Coke / Pepsi / …). Both stay on every variant so the
    // shape is uniform; the UI hides them unless the dish kind is 'addon'.
    is_free: false,
    brand_id: '',
    // Liquor-only — the serving size this row sells (peg/shot/glass) and whether
    // it's a whole bottle. serving_size_id → LiquorServingSizeMaster (its ml
    // drives Bar Inventory deduction); is_full_bottle deducts a whole bottle.
    serving_size_id: '',
    is_full_bottle: false,
    barcode: '',
    // Beer-only — the serving format this row sells (Pint / Bottle / Glass /
    // Pitcher / Tower). Persisted in the nutrition JSON blob (no migration),
    // like the coffee prep fields. Only shown when the liquor type is Beer.
    beer_format: '',
    // Per-variant nutrition (flat key→value object — see lib/nutrition.js).
    // Half vs Full carry different calories/protein, so it lives on the variant.
    nutrition: {},
    // Per-variant priced add-ons — rows of { addon_id, price }. The same add-on
    // (e.g. Cheese) can cost differently per variant (50 with Full, 30 with Half).
    addons: [],
    ...overrides,
  });

  const [dishForm, setDishForm] = useState({
    name: '',
    description: '',
    dish_type: dishType,
    kind: initialKind,
    category_master_id: '',     // FK to DishCategoryMaster (now persisted)
    sub_category_master_id: '', // FK to DishSubCategoryMaster — filtered by category
    // Add-on convenience field: a single brand picked at the dish level that
    // we fan out to every variant.brand_id on submit. Stored here (not in
    // variants) so the picker can live in BASIC INFORMATION; backend still
    // owns brand on each DishVariant row.
    brand_master_id: '',
    sku: '',  // server-assigned on create; read-only in the form
    is_vegetarian: true,
    is_vegan: false,
    // Whether the user has explicitly picked a Diet. Add-ons require a deliberate
    // choice (a Cheese add-on being veg vs the same paneer being non-veg changes
    // every variant it's attached to), so we don't let the default "Veg" pass for
    // them silently — see collectValidationErrors + the Diet selector.
    diet_touched: false,
    contains_alcohol: dishType === 'liquor',  // drinks only — Alcoholic / Non-Alcoholic toggle; on by default for liquor
    is_extra_spicy: false,
    spicy_level: '',  // '' | 'less' | 'normal' | 'extra' — picks one of the spicy pills
    non_veg_type_ids: [],  // NonVegTypeMaster ids — a dish can be more than one (Chicken + Egg); only when diet is non-veg
    // Kitchen ops can be multiple — a dish can use several stations / plating
    // notes / vessels. Stored as arrays of id strings.
    cooking_station_ids: [],  // CookingStationMaster ids
    plating_note_ids: [],     // PlatingNoteMaster ids
    serving_vessel_ids: [],   // ServingVesselMaster ids
    kitchen_notes: '',       // Free-form kitchen notes (handling tips, prep reminders)
    gravy_style: '',  // '' | 'dry' | 'semi_gravy' | 'gravy' — only used when kind==='dish' && dish_type==='food'
    cooking_method: '',  // '' | 'fried' | 'baked' | 'steamed' | 'grilled' | 'sauteed' — food items only
    cooking_medium: '',  // '' | 'butter' | 'ghee' | 'oil' — only used when kind==='dish' && dish_type==='food'
    serve_temperature: '',  // 'hot' | 'cold' | 'both' — only used when dish_type === 'beverage'
    // Beverage type + milk cascade — only used when dish_type === 'beverage'.
    // beverage_type_id is a FK to BeverageTypeMaster; when the chosen type
    // allows milk (e.g. Coffee) the form reveals has_milk, and when that's on,
    // milk_type_id (FK to MilkTypeMaster).
    beverage_type_id: '',
    has_milk: false,
    milk_type_id: '',
    // Whether the beverage contains egg (e.g. egg coffee). Beverage-only.
    contains_egg: false,
    // Liquor / bar fields — only used when dish_type === 'liquor'. bottle_size_ml
    // (full bottle volume) drives peg/servings + Bar Inventory; the rest is catalogue
    // metadata. FK ids reference the liquor masters.
    bottle_size_ml: '',
    liquor_type_id: '',
    liquor_category_id: '',
    liquor_sub_category_id: '',
    liquor_brand_id: '',
    liquor_region_id: '',
    premium_level_id: '',
    abv: '',
    country: '',
    age_statement: '',
    liquor_serve_temp: '',  // '' | 'chilled' | 'room' | 'frozen'
    liquor_source: '',  // '' | 'brewed' | 'bought' — brewed in-house vs bought-in
    storage_location: '',
    supplier: '',
    hsn_code: '',
    pos_button_color: '',
    liquor_allergen_notes: '',
    display_name: '',
    addon_source: '',  // '' | 'in_house' | 'store_bought' — only used when kind === 'addon'
    // Add-ons are bundled with a parent dish by default. When ticked the add-on
    // can be ordered on its own, which is when the Take Away / Delivery
    // fulfilment toggles become relevant. Only used when kind === 'addon'.
    sold_separately: false,
    // Dish-level Free / Paid for add-ons. When Free we fan it out to every
    // variant.is_free on submit (price forced to 0) and hide the Prices tab.
    // Only used when kind === 'addon'.
    is_free: false,
    prep_time: '',
    cook_time: '',
    is_available: true,
    is_draft: false,  // saved via "Save as Draft"; reloaded so edits know the state
    image: null,
    takeaway_allowed: false,
    takeaway_cutlery_ids: [],
    takeaway_instructions: '',
    takeaway_description: '',
    // Whether this dish can be ordered for delivery. Mirrors take-away; when
    // off the dish is dine-in / take-away only.
    delivery_allowed: false,
    delivery_cutlery_ids: [],
    delivery_instructions: '',
    // Up to 3 free-form {key, value} pairs. Empty rows are stripped on submit.
    custom_fields: [
      { key: '', value: '' },
      { key: '', value: '' },
      { key: '', value: '' },
    ],
    // Single tax selection that applies to every variant. tax_type is one of
    // '' | 'label' | 'group' and tax_id refers to a TaxLabel or TaxGroup row.
    tax_type: '',
    tax_id: '',
    // Which end of the tax sum the typed prices are: true (the default) =
    // gross, the tax is already inside and 225 is the final amount; false =
    // net, the tax goes on top.
    price_includes_tax: true,
  });

  const [taxLabels, setTaxLabels] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  /* The taxes the OWNER picked for this dish: the label, or the group's
     labels in its order. Nothing picked means nothing here — the default
     tax is applied at checkout, not worked into the editor's figures. */
  const dishTaxRows = useMemo(() => {
    const live = (taxLabels || []).filter((l) => l.is_active !== false && l.current_rate != null);
    const row = (l, compound) => ({
      id: l.id, name: l.name, rate: Number(l.current_rate) || 0,
      rate_type: l.rate_type || 'PERCENT',
      is_compound: compound == null ? !!l.is_compound : !!compound,
    });
    if (dishForm.tax_type === 'label') {
      const l = live.find((x) => String(x.id) === String(dishForm.tax_id));
      return l ? [row(l)] : [];
    }
    if (dishForm.tax_type === 'group') {
      const g = (taxGroups || []).find((x) => String(x.id) === String(dishForm.tax_id));
      if (!g) return [];
      return (g.sub_labels || [])
        .map((sl) => { const l = live.find((x) => x.id === sl.id); return l ? row(l, sl.is_compound) : null; })
        .filter(Boolean);
    }
    return [];
  }, [dishForm.tax_type, dishForm.tax_id, taxLabels, taxGroups]);

  const [variants, setVariants] = useState([buildVariant({ display_order: 0 })]);
  // Variants are optional. When off, the dish keeps a single base form (still
  // used for pricing/recipe) and the Variants tab is hidden. Editing a dish that
  // already carries 2+ variants flips this on in the edit-load effect.
  // Liquor sells as serving sizes (pegs/shots/full bottle) — each is a variant —
  // so variants are on by default for a new liquor item.
  const [hasVariants, setHasVariants] = useState(dishType === 'liquor');
  const [selectedIngredientIds, setSelectedIngredientIds] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [tags, setTags] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [nonVegOptions, setNonVegOptions] = useState([]);
  // Beverage masters — types (Coffee, Tea…) carry an `allows_milk` flag that
  // drives the conditional Milk question; milk types (Whole, Almond…) feed the
  // milk-type picker.
  const [beverageTypeOptions, setBeverageTypeOptions] = useState([]);
  const [milkTypeOptions, setMilkTypeOptions] = useState([]);
  const [roastOptions, setRoastOptions] = useState([]);
  // Liquor / bar masters — only used when dish_type === 'liquor'. Type→Category→
  // Sub-category form a parent cascade; serving sizes (peg/shot/full bottle) feed
  // the per-variant picker and drive Bar Inventory's ml math.
  const [liquorTypeOptions, setLiquorTypeOptions] = useState([]);
  // Categories/sub-categories are still fetched (for future cascade pickers) but
  // not rendered yet — keep the setters, drop the unused value bindings.
  const [, setLiquorCategoryOptions] = useState([]);
  const [, setLiquorSubCategoryOptions] = useState([]);
  const [liquorRegionOptions, setLiquorRegionOptions] = useState([]);
  const [liquorPremiumOptions, setLiquorPremiumOptions] = useState([]);
  const [liquorServingSizeOptions, setLiquorServingSizeOptions] = useState([]);
  // Direct allergen tagging — the Big-8 master (master_allergens) tagged
  // straight onto the dish. `allergenOptions` is the catalog; selected ids
  // round-trip through edit-save and the FormData submission.
  const [selectedAllergenIds, setSelectedAllergenIds] = useState([]);
  const [allergenOptions, setAllergenOptions] = useState([]);
  // -- Kitchen operations ------------------------------------------------- //
  const [cookingStationOptions, setCookingStationOptions] = useState([]);
  const [platingNoteOptions,    setPlatingNoteOptions]    = useState([]);
  const [servingVesselOptions,  setServingVesselOptions]  = useState([]);
  const [parcelCutleryOptions, setParcelCutleryOptions]   = useState([]);
  const [selectedGarnishIds,    setSelectedGarnishIds]    = useState([]);
  const [garnishSearch,         setGarnishSearch]         = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [allDishes, setAllDishes] = useState([]);
  // Add-ons are now priced per-variant via the AddonMatrix (variant.addons[]);
  // there is no dish-level add-on selection state anymore.
  const [selectedSideIds, setSelectedSideIds] = useState([]);
  const [selectedBeverageIds, setSelectedBeverageIds] = useState([]);
  const [selectedRecommendedComboIds, setSelectedRecommendedComboIds] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);

  // Combo bundle state — only used when dishForm.kind === 'combo'.
  // Stores the dish ids that make up the combo, plus a single fixed price.
  const [selectedComboItemIds, setSelectedComboItemIds] = useState([]);
  const [comboPrice, setComboPrice] = useState('');

  const [imagePreview, setImagePreview] = useState(editingDish?.image || null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  // Snapshot of the freshly-loaded form, taken after the edit fetch lands.
  // Save compares against it to say "nothing to update" instead of saving a
  // no-op. `snapshotArm` ticks once the loaded values are committed to state.
  const initialSnapRef = useRef(null);
  const [snapshotArm, setSnapshotArm] = useState(0);
  const imageInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const errorBannerRef = useRef(null);

  // Add-on dishes hide most "advanced" cards (Kitchen Ops, Take Away, Custom
  // Fields, Variants, Ingredients, Recipe matrix, Complimentary) by default —
  // they're rarely needed for a chutney or extra cheese. Toggle reveals them.
  // Advanced fields (Kitchen Ops, Take-Away, Variants, Ingredients, Recipe,
  // Complimentary) are always shown — including for add-ons. Kept as a constant
  // so the many `(!isAddonKind || showAddonAdvanced)` guards stay readable.
  const showAddonAdvanced = true;
  const { toast } = useToast();
  const { t } = useT();
  const topRef = useRef(null);
  // Wraps the whole form so the navigation guard can tell clicks that originate
  // inside the form apart from clicks on the surrounding chrome (Sidebar, etc.).
  const formRootRef = useRef(null);

  const navigateToDishList = () => {
    navigate(`/restaurant/${restaurantId}/dishes`, { state: navigationState });
  };

  // True when the user has started building a *new* dish but hasn't saved it.
  // We only guard creation (the request is "while creating a dish") — editing
  // an existing dish keeps the old, friction-free leave behaviour. Auto-derived
  // defaults (size/unit/variant_name fill in on their own from master loads) are
  // deliberately NOT treated as work, so a pristine form never triggers a prompt.
  const formHasUnsavedWork = () => {
    if (editingDish) return false;
    if (dishForm.name?.trim()) return true;
    if (dishForm.description?.trim()) return true;
    if (imagePreview) return true;
    if (comboPrice && String(comboPrice).trim()) return true;
    const hasPrice = (p) => p != null && String(p).trim() !== '' && String(p).trim() !== '0';
    if (variants.some((v) =>
      hasPrice(v.price) || (v.location_prices || []).some((lp) => hasPrice(lp.price)))) return true;
    return [
      selectedTagIds, selectedIngredientIds, selectedAllergenIds, selectedGarnishIds,
      selectedSideIds, selectedBeverageIds, selectedRecommendedComboIds, selectedComboItemIds,
    ].some((ids) => ids && ids.length > 0);
  };

  // Leaving via the in-page Back / Cancel buttons. If there's unsaved work, offer
  // to park it as a draft instead of losing it. Closing the dialog (Esc/overlay)
  // is treated as "leave without saving" — the user already chose to leave.
  const handleCancelNavigation = async () => {
    if (formHasUnsavedWork()) {
      const saveDraft = await confirm({
        title: 'Save before leaving?',
        description:
          "You haven't saved this dish yet. Save it as a draft so you can finish it later, "
          + 'or leave without saving and discard it?',
        confirmText: 'Save as draft',
        cancelText: 'Leave without saving',
      });
      if (saveDraft) {
        // handleSubmitDish navigates to the list on success, or surfaces a
        // validation error (a draft still needs a name) and keeps us here.
        await handleSubmitDish(null, { asDraft: true });
        return;
      }
    }
    navigateToDishList();
  };

  // Guard the actual browser window: closing the tab, refreshing, or navigating
  // away (incl. switching to another window via a real unload) shows the native
  // "leave site?" prompt while there's unsaved creation work. Reads live values
  // through refs so the listener stays correct without re-binding every render.
  const unsavedWorkRef = useRef(false);
  unsavedWorkRef.current = formHasUnsavedWork();
  useEffect(() => {
    const beforeUnload = (e) => {
      if (!unsavedWorkRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);

  // Guard in-app navigation too. `beforeunload` only fires on a real browser
  // unload (tab close / refresh / hard nav); clicking a Sidebar link or any
  // other React Router <Link> changes the route in-place and would silently
  // abandon the dish. This app uses BrowserRouter (not a data router), so
  // useBlocker isn't available — instead we catch the click in the capture
  // phase, before <Link> handles it, whenever the user clicks a link OUTSIDE
  // the form while there's unsaved creation work, and offer Save as draft /
  // Stay before moving to another screen. The in-page Back / Cancel buttons
  // keep their own discard path via handleCancelNavigation.
  const pendingNavRef = useRef(false);
  useEffect(() => {
    const onCaptureClick = (e) => {
      if (pendingNavRef.current) return;        // already prompting for a click
      if (!unsavedWorkRef.current) return;       // nothing to lose
      // Respect intentional new-tab / non-primary clicks and already-handled events.
      if (e.defaultPrevented || e.button !== 0
        || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest?.('a[href]');
      if (!anchor) return;                        // only links navigate the SPA
      // Clicks inside the form are the user's own controls — never intercept.
      if (formRootRef.current?.contains(anchor)) return;
      if (anchor.target && anchor.target !== '_self') return; // opens elsewhere
      let url;
      try {
        url = new URL(anchor.href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;      // external link
      const dest = url.pathname + url.search + url.hash;
      if (url.pathname === window.location.pathname) return;   // same screen
      // Take over: ask first, then navigate ourselves if they choose to leave.
      e.preventDefault();
      e.stopPropagation();
      pendingNavRef.current = true;
      (async () => {
        try {
          const saveDraft = await confirm({
            title: 'Save before leaving?',
            description:
              "You haven't saved this dish yet. Save it as a draft so you can "
              + 'finish it later, or stay on this page to keep editing.',
            confirmText: 'Save as draft',
            cancelText: 'Stay on this page',
          });
          if (saveDraft) {
            // A draft still needs a name; if it fails validation we stay put and
            // the error surfaces, so only move on once the save actually lands.
            const ok = await handleSubmitDish(null, { asDraft: true, skipRedirect: true });
            if (ok) navigate(dest);
          }
        } finally {
          pendingNavRef.current = false;
        }
      })();
    };
    document.addEventListener('click', onCaptureClick, true);
    return () => document.removeEventListener('click', onCaptureClick, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Page should open scrolled to the top. The actual scroll container is the
  // app-shell `<div class="flex-1 overflow-y-auto">` from App.js — NOT window —
  // so `window.scrollTo` is a no-op. Walk up from the top anchor to find the
  // nearest ancestor that overflows, and reset its scrollTop. Also reset
  // window for good measure in case the layout changes later.
  useEffect(() => {
    const reset = () => {
      window.scrollTo(0, 0);
      let el = topRef.current?.parentElement;
      while (el) {
        const style = window.getComputedStyle(el);
        if (/(auto|scroll|overlay)/.test(style.overflowY) && el.scrollHeight > el.clientHeight) {
          el.scrollTop = 0;
          return;
        }
        el = el.parentElement;
      }
    };
    // Run after paint so the DOM is fully laid out and the scroll container
    // actually has its final scrollHeight.
    const raf = requestAnimationFrame(reset);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Sizes can now repeat across variants (same size, different option), so the
  // dropdown offers every size on every row. New rows still default to the
  // first not-yet-used size for convenience, falling back to the first size.
  const getAvailableSizes = () => sizeOptions;

  const deriveVariantNameSource = (variantName, sizeValue, dishName) => {
    if (!variantName) return 'custom';
    const normalizedVariant = variantName.trim().toLowerCase();
    if (dishName && normalizedVariant === dishName.trim().toLowerCase()) return 'dish';
    const sizeMatch = sizeOptions.find((o) => o.label.toLowerCase() === normalizedVariant);
    if (sizeMatch) return `size:${sizeValue || sizeMatch.value}`;
    return 'custom';
  };

  const mapExistingVariant = (variantData, index, dishName) => {
    let variantNameSource = deriveVariantNameSource(
      variantData.variant_name, variantData.size, dishName,
    );
    if (index === 0 && dishName && variantData.variant_name?.trim().toLowerCase() === dishName.trim().toLowerCase()) {
      variantNameSource = 'dish';
    }
    return buildVariant({
      // Custom-named rows (e.g. "Tortilla" / "Naan/Parantha") legitimately have
      // no size — don't fabricate one, or the auto-composed name would replace
      // the custom name (and a save would then destroy it in the DB).
      size: variantData.size || (variantNameSource === 'custom' ? '' : firstSize),
      quantity: variantData.quantity != null ? String(variantData.quantity) : defaultQuantity,
      unit: variantData.unit || defaultUnit,
      variant_name: variantData.variant_name || '',
      variant_name_source: variantNameSource,
      variant_option: variantData.variant_option || '',
      variant_option_2: variantData.variant_option_2 || '',
      serve_temperature: variantData.serve_temperature || '',
      // Coffee/milk-beverage prep, read back from the nutrition JSON blob.
      shots: variantData.nutrition?.bev_shots != null ? String(variantData.nutrition.bev_shots) : '',
      ice_level: variantData.nutrition?.bev_ice_level || '',
      sugar_level: variantData.nutrition?.bev_sugar_level || '',
      roast: variantData.nutrition?.bev_roast || '',
      price: variantData.price != null ? String(variantData.price) : '',
      is_seasonal: !!variantData.is_seasonal,
      is_free: !!variantData.is_free,
      brand_id: variantData.brand_id != null ? String(variantData.brand_id) : '',
      serving_size_id: variantData.serving_size_id != null ? String(variantData.serving_size_id) : '',
      is_full_bottle: !!variantData.is_full_bottle,
      barcode: variantData.barcode || '',
      // Beer serving format, read back from the nutrition JSON blob.
      beer_format: variantData.nutrition?.beer_format || '',
      nutrition: (variantData.nutrition && typeof variantData.nutrition === 'object')
        ? variantData.nutrition
        : {},
      addons: Array.isArray(variantData.addons)
        ? variantData.addons.map((a) => ({
            addon_id: a.addon_id ?? a.id,
            price: a.price != null ? String(a.price) : '',
          }))
        : [],
      calories: variantData.calories != null ? String(variantData.calories) : '',
      serves: variantData.serves || 1,
      preparation_time: variantData.preparation_time || 15,
      spicy_level: variantData.spicy_level ?? 0,
      is_available: variantData.is_available ?? true,
      display_order: index,
      parcel_instructions: variantData.parcel_instructions || '',
      parcel_cutlery_ids: (variantData.parcel_cutlery_items || []).map((c) => c.id),
      // Per-variant complimentary items. Server may send either:
      //  - rich rows { dish_id, variant_id, qty }
      //  - or just dish stubs { id, ... } (older shape) — fall back to qty=1.
      complimentary_items: (variantData.complimentary_items || []).map((d) => ({
        dish_id: d.dish_id ?? d.id,
        variant_id: d.variant_id ?? null,
        qty: d.qty != null ? Math.max(1, parseInt(d.qty, 10) || 1) : 1,
      })),
      recipe: Array.isArray(variantData.recipe)
        ? variantData.recipe.map((r) => ({
            ingredient_id: r.ingredient_id,
            quantity: r.quantity != null ? String(r.quantity) : '',
            unit: r.unit || '',
          }))
        : [],
      // Per-variant allergen ids (master_allergens) — shown in the inline
      // ALLERGENS column of the variant table.
      allergens: Array.isArray(variantData.allergens)
        ? variantData.allergens.map(Number).filter((n) => Number.isFinite(n))
        : [],
      // Per-location/channel prices from DishLocationPrice (matrix rows).
      location_prices: (variantData.location_prices || []).map((r) => ({
        location_id: r.location_id,
        price: r.price != null ? String(r.price) : '',
        takeaway_price: r.takeaway_price != null ? String(r.takeaway_price) : '',
        delivery_price: r.delivery_price != null ? String(r.delivery_price) : '',
      })),
    });
  };

  useEffect(() => {
    // The dish-detail fetch is dispatched FIRST — it's the request that
    // populates the form fields on edit, so it must not queue behind the
    // masters/dishes/locations calls in the browser's per-host connection
    // pool. The supporting lists are kicked off right after (see below).
    if (editingDish && restaurantId) {
      getDish(restaurantId, editingDish.id)
        .then((response) => {
          const dishData = response?.data || response;
          setDishForm({
            name: dishData.name,
            description: dishData.description,
            dish_type: dishData.dish_type,
            kind: dishData.kind || 'dish',
            is_vegetarian: dishData.is_vegetarian,
            is_vegan: dishData.is_vegan,
            diet_touched: true,  // an existing dish already carries a deliberate diet
            contains_alcohol: !!dishData.contains_alcohol,
            is_extra_spicy: !!dishData.is_extra_spicy,
            spicy_level: dishData.spicy_level || (dishData.is_extra_spicy ? 'extra' : ''),
            non_veg_type_ids: (
              (dishData.non_veg_type_ids && dishData.non_veg_type_ids.length)
                ? dishData.non_veg_type_ids
                : (dishData.non_veg_type_id ? [dishData.non_veg_type_id] : [])
            ).map(String),
            category_master_id: dishData.category_master_id ? String(dishData.category_master_id) : '',
            sub_category_master_id: dishData.sub_category_master_id ? String(dishData.sub_category_master_id) : '',
            // Brand lives on each DishVariant; surface the first variant's pick
            // as the dish-level default for the BASIC INFORMATION picker.
            brand_master_id: (() => {
              const first = (dishData.variants || []).find((v) => v?.brand_id != null);
              return first?.brand_id != null ? String(first.brand_id) : '';
            })(),
            cooking_station_ids: (
              (dishData.cooking_station_ids && dishData.cooking_station_ids.length)
                ? dishData.cooking_station_ids
                : (dishData.cooking_station_id ? [dishData.cooking_station_id] : [])
            ).map(String),
            plating_note_ids: (
              (dishData.plating_note_ids && dishData.plating_note_ids.length)
                ? dishData.plating_note_ids
                : (dishData.plating_note_id ? [dishData.plating_note_id] : [])
            ).map(String),
            serving_vessel_ids: (
              (dishData.serving_vessel_ids && dishData.serving_vessel_ids.length)
                ? dishData.serving_vessel_ids
                : (dishData.serving_vessel_id ? [dishData.serving_vessel_id] : [])
            ).map(String),
            kitchen_notes: dishData.kitchen_notes || '',
            custom_fields: (() => {
              const rows = Array.isArray(dishData.custom_fields) ? dishData.custom_fields : [];
              const padded = [...rows.slice(0, 3)];
              while (padded.length < 3) padded.push({ key: '', value: '' });
              return padded.map((r) => ({ key: r?.key || '', value: r?.value || '' }));
            })(),
            gravy_style: dishData.gravy_style || '',
            cooking_method: dishData.cooking_method || '',
            cooking_medium: dishData.cooking_medium || '',
            serve_temperature: dishData.serve_temperature || '',
            beverage_type_id: dishData.beverage_type_id ? String(dishData.beverage_type_id) : '',
            has_milk: !!dishData.has_milk,
            milk_type_id: dishData.milk_type_id ? String(dishData.milk_type_id) : '',
            contains_egg: !!dishData.contains_egg,
            // Liquor / bar metadata (blank for non-liquor dishes).
            bottle_size_ml: dishData.bottle_size_ml != null ? String(dishData.bottle_size_ml) : '',
            liquor_type_id: dishData.liquor_type_id ? String(dishData.liquor_type_id) : '',
            liquor_category_id: dishData.liquor_category_id ? String(dishData.liquor_category_id) : '',
            liquor_sub_category_id: dishData.liquor_sub_category_id ? String(dishData.liquor_sub_category_id) : '',
            liquor_brand_id: dishData.liquor_brand_id ? String(dishData.liquor_brand_id) : '',
            liquor_region_id: dishData.liquor_region_id ? String(dishData.liquor_region_id) : '',
            premium_level_id: dishData.premium_level_id ? String(dishData.premium_level_id) : '',
            abv: dishData.abv != null ? String(dishData.abv) : '',
            country: dishData.country || '',
            age_statement: dishData.age_statement || '',
            liquor_serve_temp: dishData.liquor_serve_temp || '',
            liquor_source: dishData.liquor_source || '',
            storage_location: dishData.storage_location || '',
            supplier: dishData.supplier || '',
            hsn_code: dishData.hsn_code || '',
            pos_button_color: dishData.pos_button_color || '',
            liquor_allergen_notes: dishData.liquor_allergen_notes || '',
            display_name: dishData.display_name || '',
            addon_source: dishData.addon_source || '',
            takeaway_allowed: dishData.takeaway_allowed != null ? !!dishData.takeaway_allowed : false,
            delivery_allowed: dishData.delivery_allowed != null ? !!dishData.delivery_allowed : false,
            sold_separately: !!dishData.sold_separately,
            // Free add-on if every saved variant is marked free.
            is_free: dishData.kind === 'addon'
              && (dishData.variants || []).length > 0
              && (dishData.variants || []).every((v) => !!v.is_free),
            sku: dishData.sku || '',
            prep_time: dishData.prep_time ?? Math.min(5, dishData.time_to_cook ?? 15),
            cook_time: dishData.cook_time ?? Math.max(0, (dishData.time_to_cook ?? 15) - Math.min(5, dishData.time_to_cook ?? 15)),
            is_available: dishData.is_available,
            is_draft: !!dishData.is_draft,
            image: null,
            tax_type: dishData.tax_group_id ? 'group' : (dishData.tax_label_id ? 'label' : ''),
            tax_id: dishData.tax_group_id
              ? String(dishData.tax_group_id)
              : (dishData.tax_label_id ? String(dishData.tax_label_id) : ''),
            price_includes_tax: dishData.price_includes_tax == null ? true : !!dishData.price_includes_tax,
          });
          setImagePreview(dishData.image || null);
          setRemoveExistingImage(false);
          setSelectedIngredientIds((dishData.ingredients || []).map((i) => i.id));
          setSelectedTagIds((dishData.tags || []).map((tg) => tg.id));
          // Add-ons load per-variant via mapExistingVariant (variant.addons[]).
          setSelectedSideIds((dishData.recommended_sides || []).map((d) => d.id));
          setSelectedBeverageIds((dishData.recommended_beverages || []).map((d) => d.id));
          setSelectedRecommendedComboIds((dishData.recommended_combos || []).map((d) => d.id));
          setSelectedAllergenIds((dishData.allergen_tags || []).map((a) => a.id));
          setSelectedGarnishIds((dishData.garnishes || []).map((g) => g.id));
          setSelectedLocationIds((dishData.locations || []).map((l) => l.id));
          // Complimentary items are per-variant now (Half vs Full can differ) —
          // mapExistingVariant seeds each variant's matrix from its OWN saved
          // set. For a variant-less dish (combo) fall back to the dish-level set.
          // A dish with 2+ saved variants opens with the Variants tab enabled.
          if ((dishData.variants || []).length > 1) setHasVariants(true);
          if (dishData.variants && dishData.variants.length > 0) {
            setVariants(dishData.variants.map((v, i) => mapExistingVariant(v, i, dishData.name)));
          } else {
            const compRows = (dishData.complimentary_items || []).map((d) => ({
              dish_id: d.id, variant_id: null, qty: 1,
            }));
            setVariants([buildVariant({
              variant_name: dishData.name, variant_name_source: 'dish',
              complimentary_items: compRows,
            })]);
          }
          const locIds = (dishData.locations || []).map((l) => Number(l.id));
          const pricesUniform = locIds.length <= 1 || (dishData.variants || []).every((v) => {
            const byLoc = new Map(
              (v.location_prices || []).map((r) => [
                Number(r.location_id),
                String(r.price) + '|' + String(r.takeaway_price) + '|' + String(r.delivery_price),
              ]),
            );
            const triples = locIds.map((id) => byLoc.get(id) || '');
            return triples.every((tp) => tp === triples[0]);
          });
          setSameForAll(pricesUniform);
          // All loaded values are queued in this batch — arm the baseline
          // snapshot; the effect takes it after they commit.
          setSnapshotArm((n) => n + 1);
        })
        .catch(() => {
          // A failed load must not masquerade as an empty dish — saving from here
          // would wipe the real variants/prices. Warn loudly.
          toast({
            title: 'Could not fully load this dish',
            description: 'Some details (including prices) may be missing. Reopen the dish before saving to avoid overwriting saved data.',
            variant: 'destructive',
          });
          setDishForm({
            name: editingDish.name,
            description: editingDish.description,
            dish_type: editingDish.dish_type,
            is_vegetarian: editingDish.is_vegetarian,
            is_vegan: editingDish.is_vegan,
            diet_touched: true,  // an existing dish already carries a deliberate diet
            sku: editingDish.sku || '',
            prep_time: editingDish.prep_time ?? Math.min(5, editingDish.time_to_cook ?? 15),
            cook_time: editingDish.cook_time ?? Math.max(0, (editingDish.time_to_cook ?? 15) - Math.min(5, editingDish.time_to_cook ?? 15)),
            is_available: editingDish.is_available,
            is_draft: !!editingDish.is_draft,
            image: null,
          });
          setImagePreview(editingDish.image || null);
          setRemoveExistingImage(false);
          if ((editingDish.variants || []).length > 1) setHasVariants(true);
          if (editingDish.variants?.length > 0) {
            setVariants(editingDish.variants.map((v, i) => mapExistingVariant(v, i, editingDish.name)));
          }
        });
    }
    loadDishMasters();  // one bundled request (+ allergens) — see loadDishMasters
    if (restaurantId) {
      loadAllDishes();
      loadLocations();
      // Tax options for the dish-level tax selector (applies to all variants).
      // Failures are non-fatal — the dropdown just stays empty.
      listTaxLabels(restaurantId).then(setTaxLabels).catch(() => setTaxLabels([]));
      listTaxGroups(restaurantId).then(setTaxGroups).catch(() => setTaxGroups([]));
    }
    // eslint-disable-next-line
  }, [editingDish?.id, restaurantId]);

  // Display names are always auto-composed (Size · Add-on 1 · Add-on 2). Keep
  // every row's stored name in sync with that composition whenever the dish
  // name or the size master list changes — covers the dish-name fallback for
  // sizeless rows and label resolution once size masters finish loading.
  useEffect(() => {
    setVariants((prev) => {
      let changed = false;
      const next = prev.map((variant) => {
        // Custom names ("Tortilla", "Naan/Parantha", …) came from the server
        // as-is and must never be overwritten by the auto-composition — the
        // customer ordering screen displays them.
        if (variant.variant_name_source === 'custom' && variant.variant_name?.trim()) return variant;
        const name = composeVariantName(variant);
        if (name === variant.variant_name) return variant;
        changed = true;
        return { ...variant, variant_name: name };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dishForm.name, sizeOptions]);

  const handleNameChange = (e) => {
    setDishForm({ ...dishForm, name: e.target.value });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setDishForm({ ...dishForm, image: e.target.files[0] });
      setImagePreview(URL.createObjectURL(e.target.files[0]));
      setRemoveExistingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setDishForm({ ...dishForm, image: null });
    setImagePreview(null);
    setRemoveExistingImage(true);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const loadIngredients = async () => {
    try {
      const data = await dishMastersService.refresh('ingredient', { restaurantId });
      setIngredients(Array.isArray(data) ? data : []);
    } catch {
      /* keep existing list on transient error */
    }
  };

  // Each loader hits the unified dish-master endpoint for just its type.
  // GET /api/users/dish/masters/<size|unit|tag>/  → JSON list

  const loadSizes = async () => {
    try {
      const rows = await dishMastersService.refresh('size');
      setSizeOptions(rows.map((row) => ({ value: row.code, label: row.name })));
    } catch { /* keep existing list on transient error */ }
  };

  const loadUnits = async () => {
    try {
      const rows = await dishMastersService.refresh('unit');
      setUnitOptions(rows.map((row) => ({ value: row.code, label: row.name })));
    } catch { /* keep existing list */ }
  };

  // Once size masters load, snap any variant whose size doesn't match a real
  // master code (e.g. the legacy lowercase 'full' fallback) onto the proper
  // master code, so the dropdown shows the Title-Case name and saves a valid code.
  useEffect(() => {
    if (!sizeOptions.length) return;
    setVariants((prev) => {
      let changed = false;
      const next = prev.map((vr) => {
        // A blank size is intentional on custom-named rows — leave it alone.
        if (!vr.size) return vr;
        if (sizeOptions.some((o) => o.value === vr.size)) return vr;
        const m = matchMaster(vr.size, sizeOptions) || sizeOptions[0];
        if (m && m.value !== vr.size) { changed = true; return { ...vr, size: m.value }; }
        return vr;
      });
      return changed ? next : prev;
    });
  }, [sizeOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same reconciliation for units — but leave an intentionally-empty unit alone.
  useEffect(() => {
    if (!unitOptions.length) return;
    setVariants((prev) => {
      let changed = false;
      const next = prev.map((vr) => {
        if (!vr.unit || unitOptions.some((o) => o.value === vr.unit)) return vr;
        const m = matchMaster(vr.unit, unitOptions);
        if (m && m.value !== vr.unit) { changed = true; return { ...vr, unit: m.value }; }
        return vr;
      });
      return changed ? next : prev;
    });
  }, [unitOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTags = async () => {
    try {
      const rows = await dishMastersService.refresh('tag');
      setTags(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };

  const loadCategoriesMaster = async () => {
    try {
      const rows = await dishMastersService.refresh('category');
      setCategoryOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };

  const loadSubCategoriesMaster = async () => {
    try {
      const rows = await dishMastersService.refresh('sub_category');
      setSubCategoryOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };

  const loadNonVegMaster = async () => {
    try {
      const rows = await dishMastersService.refresh('non_veg');
      setNonVegOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };

  const loadBeverageTypes = async () => {
    try {
      const rows = await dishMastersService.refresh('beverage_type');
      setBeverageTypeOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadMilkTypes = async () => {
    try {
      const rows = await dishMastersService.refresh('milk_type');
      setMilkTypeOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadCoffeeRoasts = async () => {
    try {
      const rows = await dishMastersService.refresh('coffee_roast');
      setRoastOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadCookingStations = async () => {
    try {
      const rows = await dishMastersService.refresh('cooking_station');
      setCookingStationOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadPlatingNotes = async () => {
    try {
      const rows = await dishMastersService.refresh('plating_note');
      setPlatingNoteOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadServingVessels = async () => {
    try {
      const rows = await dishMastersService.refresh('serving_vessel');
      setServingVesselOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadParcelCutlery = async () => {
    try {
      const rows = await dishMastersService.refresh('parcel_cutlery');
      setParcelCutleryOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadBrands = async () => {
    try {
      const rows = await dishMastersService.refresh('brand');
      setBrandOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadAllergens = async () => {
    if (!restaurantId) return;
    try {
      // Allergens are ingredients flagged is_allergen (per restaurant).
      const rows = await listAllergenIngredients(restaurantId);
      setAllergenOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadLiquorTypes = async () => {
    try {
      const rows = await dishMastersService.refresh('liquor_type');
      setLiquorTypeOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadLiquorCategories = async () => {
    try {
      const rows = await dishMastersService.refresh('liquor_category');
      setLiquorCategoryOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadLiquorSubCategories = async () => {
    try {
      const rows = await dishMastersService.refresh('liquor_sub_category');
      setLiquorSubCategoryOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadLiquorRegions = async () => {
    try {
      const rows = await dishMastersService.refresh('liquor_region');
      setLiquorRegionOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadLiquorPremiums = async () => {
    try {
      const rows = await dishMastersService.refresh('liquor_premium_level');
      setLiquorPremiumOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  const loadLiquorServingSizes = async () => {
    try {
      const rows = await dishMastersService.refresh('liquor_serving_size');
      setLiquorServingSizeOptions(Array.isArray(rows) ? rows : []);
    } catch { /* keep existing list */ }
  };
  // Initial mount + after-create refresh helper — fires every master in parallel.
  // ONE bundled request for every master list the form needs. This used to be
  // 21 separate GETs fired on mount — they saturated the browser's per-host
  // connection pool and queued the dish-detail fetch behind them, so edit
  // fields took seconds to populate. Allergens ride separately (different
  // endpoint), and the per-type loaders above remain for the cross-tab sync
  // channel. Falls back to the per-type loaders if the bundle call fails.
  const loadDishMasters = async () => {
    loadAllergens();
    try {
      const b = await dishMastersService.refreshBundle(null, { restaurantId });
      const rows = (k) => (Array.isArray(b[k]) ? b[k] : []);
      const asOptions = (k) => rows(k).map((r) => ({ value: r.code, label: r.name }));
      if (b.size) setSizeOptions(asOptions('size'));
      if (b.unit) setUnitOptions(asOptions('unit'));
      if (b.tag) setTags(rows('tag'));
      if (b.category) setCategoryOptions(rows('category'));
      if (b.sub_category) setSubCategoryOptions(rows('sub_category'));
      if (b.non_veg) setNonVegOptions(rows('non_veg'));
      if (b.beverage_type) setBeverageTypeOptions(rows('beverage_type'));
      if (b.milk_type) setMilkTypeOptions(rows('milk_type'));
      if (b.coffee_roast) setRoastOptions(rows('coffee_roast'));
      if (b.cooking_station) setCookingStationOptions(rows('cooking_station'));
      if (b.plating_note) setPlatingNoteOptions(rows('plating_note'));
      if (b.serving_vessel) setServingVesselOptions(rows('serving_vessel'));
      if (b.parcel_cutlery) setParcelCutleryOptions(rows('parcel_cutlery'));
      if (b.brand) setBrandOptions(rows('brand'));
      if (b.liquor_type) setLiquorTypeOptions(rows('liquor_type'));
      if (b.liquor_category) setLiquorCategoryOptions(rows('liquor_category'));
      if (b.liquor_sub_category) setLiquorSubCategoryOptions(rows('liquor_sub_category'));
      if (b.liquor_region) setLiquorRegionOptions(rows('liquor_region'));
      if (b.liquor_premium_level) setLiquorPremiumOptions(rows('liquor_premium_level'));
      if (b.liquor_serving_size) setLiquorServingSizeOptions(rows('liquor_serving_size'));
      if (b.ingredient) setIngredients(rows('ingredient'));
    } catch {
      return Promise.all([
        loadSizes(), loadUnits(), loadTags(),
        loadCategoriesMaster(), loadSubCategoriesMaster(),
        loadNonVegMaster(), loadBeverageTypes(), loadMilkTypes(), loadCoffeeRoasts(),
        loadCookingStations(), loadPlatingNotes(), loadServingVessels(),
        loadParcelCutlery(), loadBrands(),
        loadLiquorTypes(), loadLiquorCategories(), loadLiquorSubCategories(),
        loadLiquorRegions(), loadLiquorPremiums(), loadLiquorServingSizes(),
        loadIngredients(),
      ]);
    }
  };

  // Cross-tab master sync: when any tab creates a master (Settings page, a
  // sibling dish form, etc.) it broadcasts on this channel; every other tab
  // re-fetches the relevant list so the picker stays up-to-date in real time.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const ch = new BroadcastChannel('restoboxx-masters');
    const KIND_TO_LOADER = {
      dishSizes: loadSizes,
      dishUnits: loadUnits,
      dishTags: loadTags,
      dishCategories: loadCategoriesMaster,
      dishSubCategories: loadSubCategoriesMaster,
      dishNonVeg: loadNonVegMaster,
      dishBeverageType: loadBeverageTypes,
      dishMilkType: loadMilkTypes,
      coffeeRoasts: loadCoffeeRoasts,
      dishCookingStation: loadCookingStations,
      dishPlatingNote: loadPlatingNotes,
      dishServingVessel: loadServingVessels,
      dishParcelCutlery: loadParcelCutlery,
      dishBrand: loadBrands,
      dishIngredients: loadIngredients,
      dishAllergens: loadAllergens,
      dishLiquorType: loadLiquorTypes,
      dishLiquorCategory: loadLiquorCategories,
      dishLiquorSubCategory: loadLiquorSubCategories,
      dishLiquorRegion: loadLiquorRegions,
      dishLiquorPremiumLevel: loadLiquorPremiums,
      dishLiquorServingSize: loadLiquorServingSizes,
    };
    ch.onmessage = (e) => {
      const kind = e?.data?.kind;
      const fn = KIND_TO_LOADER[kind];
      if (fn) fn();
      // 'all' refreshes everything — useful for bulk imports.
      if (kind === 'all') loadDishMasters();
    };
    return () => ch.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const loadAllDishes = async () => {
    try {
      const result = await getDishes(restaurantId);
      const list = Array.isArray(result) ? result : result?.data || result?.results || [];
      setAllDishes(list);
    } catch {
      setAllDishes([]);
    }
  };

  // Create a brand-new add-on Dish (kind='addon') from the Variants tab's Add On
  // columns. It inherits this dish's selected locations; an optional price seeds
  // every location. Mirrors AddonMatrix.submitNewAddon so both entry points agree.
  // Once saved we reload the catalogue so the add-on appears in the Add On 1 / 2
  // dropdowns; the user then assigns it per variant row.
  const submitQuickAddon = async () => {
    const name = (addonQuickDlg?.name || '').trim();
    if (!name) {
      toast({ title: 'Add-on name is required', variant: 'destructive' });
      return;
    }
    // Diet is mandatory for add-ons (same rule as the full form and
    // AddonMatrix.submitNewAddon) — it drives the diet of every variant
    // the add-on is attached to.
    const diet = addonQuickDlg?.diet || '';
    if (!diet) {
      toast({ title: 'Pick a diet (Veg / Non-Veg / Vegan) for this add-on', variant: 'destructive' });
      return;
    }
    const priceNum = parseFloat(addonQuickDlg?.price);
    const price = Number.isFinite(priceNum) && priceNum > 0 ? priceNum : 0;
    setAddonQuickSaving(true);
    try {
      const payload = {
        type: 'addon',
        name,
        description: '',
        // Diet booleans: veg → vegetarian, vegan → both, nonveg → neither.
        is_vegetarian: diet === 'veg' || diet === 'vegan',
        is_vegan: diet === 'vegan',
        location_ids: selectedLocationIds || [],
        variants: [{
          variant_name: name,
          size: '', unit: '', quantity: 1,
          is_available: true,
          display_order: 0,
          location_prices: (price > 0 ? (selectedLocationIds || []) : []).map((lid) => ({
            location_id: lid, price, takeaway_price: 0, delivery_price: 0,
          })),
        }],
      };
      const res = await createDish(restaurantId, payload);
      const newId = res?.data?.id ?? res?.id;
      if (!res?.success || !newId) throw res?.error || 'Failed to create add-on';
      await loadAllDishes();
      toast({ title: 'Add-on created', description: `"${name}" is now available in the Add On pickers.` });
      setAddonQuickDlg(null);
    } catch (e) {
      toast({
        title: 'Could not create add-on',
        description: formatApiError(e, 'Failed to create add-on'),
        variant: 'destructive',
      });
    } finally {
      setAddonQuickSaving(false);
    }
  };

  const loadLocations = async () => {
    try {
      const list = await locationsService.list(restaurantId);
      setLocations(list || []);
    } catch {
      setLocations([]);
    }
  };

  // Prices are entered per-location in the Price Matrix (variant.location_prices[]),
  // not on the variant itself. The backend still expects a top-level `price`, so
  // fall back to the first location that has a positive base price.
  const firstLocationPrice = (variant) => {
    const rows = variant?.location_prices || [];
    for (const r of rows) {
      const p = parseFloat(r?.price);
      if (!isNaN(p) && p > 0) return p;
    }
    return NaN;
  };

  const mapVariantForSave = (variant, index) => {
    const isSeasonal = !!variant.is_seasonal;
    // Add-ons now carry their own optional standalone price (set on the Prices
    // tab → DishLocationPrice), so they're no longer force-marked free; a blank
    // price simply saves as 0 (effectively free). Any stale is_free from the old
    // "add-ons are always free" model is ignored for add-ons.
    const isFree = dishForm.kind === 'addon' ? false : !!variant.is_free;
    // Empty / unparseable price becomes 0 instead of NaN.
    const rawPrice = parseFloat(variant.price);
    const effectivePrice = !isNaN(rawPrice) && rawPrice > 0
      ? rawPrice
      : firstLocationPrice(variant);
    const parsedPrice = (isSeasonal || isFree)
      ? 0
      : (isNaN(effectivePrice) ? 0 : effectivePrice);
    const parsedQuantity = variant.quantity === '' ? 1 : parseFloat(variant.quantity);
    // Brand lives at the dish level for add-ons, beverages, store-bought and
    // liquor items, and is fanned out across every variant on submit. Other
    // dishes don't carry a brand at all.
    const brandCarryingType = ['beverage', 'store_bought', 'liquor'].includes(dishForm.dish_type);
    const brandId = (() => {
      if (dishForm.kind !== 'addon' && !brandCarryingType) return null;
      const raw = dishForm.brand_master_id;
      if (raw === '' || raw == null) return null;
      const n = parseInt(raw, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    })();
    return {
      size: variant.size,
      quantity: isNaN(parsedQuantity) ? 1 : parsedQuantity,
      unit: variant.unit,
      variant_name: variant.variant_name.trim(),
      variant_option: (variant.variant_option || '').trim(),
      variant_option_2: (variant.variant_option_2 || '').trim(),
      serve_temperature: dishForm.dish_type === 'beverage' ? (variant.serve_temperature || '') : '',
      price: parsedPrice,
      is_seasonal: isSeasonal,
      is_free: isFree,
      brand_id: brandId,
      // Liquor serving size + full-bottle flag (ignored server-side for non-liquor).
      serving_size_id: dishForm.dish_type === 'liquor' && variant.serving_size_id
        ? Number(variant.serving_size_id) : null,
      is_full_bottle: dishForm.dish_type === 'liquor' ? !!variant.is_full_bottle : false,
      barcode: dishForm.dish_type === 'liquor' ? (variant.barcode || '') : '',
      // Coffee/milk-beverage prep (shots, ice, sugar) is fixed per variant and
      // tucked into the nutrition JSON blob so it persists with no migration.
      // Only stored for milk beverages; ice is kept only when served cold.
      nutrition: {
        ...((variant.nutrition && typeof variant.nutrition === 'object') ? variant.nutrition : {}),
        ...(isMilkBeverage ? {
          bev_shots: (variant.shots === '' || variant.shots == null) ? null : (parseInt(variant.shots, 10) || 0),
          bev_ice_level: (variant.serve_temperature === 'cold' || variant.serve_temperature === 'both')
            ? (variant.ice_level || '') : '',
          bev_sugar_level: variant.sugar_level || '',
          bev_roast: variant.roast || '',
        } : {}),
        // Beer serving format rides in the nutrition blob (only for beer).
        ...(isBeer ? { beer_format: variant.beer_format || '' } : {}),
      },
      addons: (variant.addons || [])
        .filter((a) => a && a.addon_id)
        .map((a) => ({ addon_id: Number(a.addon_id), price: parseFloat(a.price) || 0 })),
      calories: variant.calories === '' ? 0 : parseInt(variant.calories, 10) || 0,
      serves: parseInt(variant.serves, 10) || 1,
      spicy_level: parseInt(variant.spicy_level, 10) || 0,
      preparation_time: parseInt(variant.preparation_time, 10) || 15,
      is_available: variant.is_available ?? true,
      display_order: variant.display_order ?? index,
      parcel_instructions: (variant.parcel_instructions || '').trim(),
      parcel_cutlery_ids: variant.parcel_cutlery_ids || [],
      complimentary_items: (variant.complimentary_items || []).map((c) => ({
        dish_id: Number(c.dish_id),
        variant_id: c.variant_id != null ? Number(c.variant_id) : null,
        qty: Math.max(1, parseInt(c.qty, 10) || 1),
      })),
      recipe: (variant.recipe || [])
        .filter((r) => r && r.ingredient_id)
        .map((r) => ({
          ingredient_id: Number(r.ingredient_id),
          quantity: r.quantity === '' || r.quantity == null
            ? 0
            : (parseFloat(r.quantity) || 0),
          unit: (r.unit || '').trim(),
        })),
      // Per-variant allergen ids (master_allergens). De-duped, numeric only.
      allergens: [...new Set((variant.allergens || [])
        .map(Number)
        .filter((n) => Number.isFinite(n)))],
      // Per-location/channel prices — saved into DishLocationPrice.
      location_prices: (variant.location_prices || [])
        .filter((r) => r && r.location_id)
        .map((r) => ({
          location_id: Number(r.location_id),
          price: parseFloat(r.price) || 0,
          takeaway_price: parseFloat(r.takeaway_price) || 0,
          delivery_price: parseFloat(r.delivery_price) || 0,
        })),
    };
  };


  const collectValidationErrors = () => {
    const errs = [];
    if (!dishForm.name?.trim()) {
      errs.push(t('addDish.validation.nameRequired'));
    } else {
      const nm = dishForm.name.trim().toLowerCase();
      const clash = (allDishes || []).some(
        (d) => d.id !== editingDish?.id && (d.name || '').trim().toLowerCase() === nm,
      );
      if (clash) errs.push(t('addDish.validation.nameDuplicate', { name: dishForm.name.trim() }));
    }
    // At least one location is mandatory — a dish must be offered somewhere.
    if (!(selectedLocationIds || []).length) errs.push(t('addDish.validation.locationsRequired'));
    // An add-on's diet drives the effective diet of every variant it's attached
    // to, so it can't be left at the default — the user must pick it explicitly.
    if (dishForm.kind === 'addon' && !dishForm.diet_touched) {
      errs.push(t('addDish.validation.addonDietRequired'));
    }
    // Liquor must carry a Type (Spirit / Beer / Wine…) — picked on Basic Information.
    if (dishForm.dish_type === 'liquor' && !dishForm.liquor_type_id) {
      errs.push(t('addDish.validation.liquorTypeRequired'));
    }
    // Liquor needs a bottle size — it drives Bar Inventory's peg/servings + value.
    if (dishForm.dish_type === 'liquor' && !(parseFloat(dishForm.bottle_size_ml) > 0)) {
      errs.push('Enter the bottle size (ml) on Liquor Details — it drives stock and pour math.');
    }
    const isCombo = dishForm.kind === 'combo';
    if (isCombo) {
      if (!selectedComboItemIds.length) {
        errs.push(t('addDish.validation.comboEmpty'));
      }
      const parsedPrice = parseFloat(comboPrice);
      if (comboPrice === '' || comboPrice == null || isNaN(parsedPrice) || parsedPrice <= 0) {
        errs.push(t('addDish.validation.comboPrice'));
      }
      return errs;
    }
    variants.forEach((v, i) => {
      const row = t('addDish.validation.variantPrefix', { n: i + 1 });
      if (!v.variant_name?.trim()) errs.push(t('addDish.validation.variantDisplayName', { row }));
      // size and unit are OPTIONAL — existing dishes legitimately have them
      // blank (per-size pricing isn't wired yet), and the form auto-seeds a
      // variant for every dish. Requiring them here blocked EVERY dish edit
      // before any API call. Same rationale as the optional price below.
      // Price is OPTIONAL — a variant persists with a base price of 0 when none
      // is entered. We only reject a price the user actually typed that is
      // negative or non-numeric.
      const directPrice = parseFloat(v.price);
      const badDirect = v.price !== '' && v.price != null
        && (isNaN(directPrice) || directPrice < 0);
      const badLocation = (v.location_prices || []).some((r) => {
        if (r?.price === '' || r?.price == null) return false;
        const p = parseFloat(r.price);
        return isNaN(p) || p < 0;
      });
      if (badDirect || badLocation) {
        errs.push(t('addDish.validation.addonPrice', { row }));
      }
      // Only validate piece count when a piece unit is chosen AND a quantity
      // was actually entered — never block on a blank quantity.
      if (v.unit === 'piece' && v.quantity !== '' && v.quantity != null) {
        const pieces = parseInt(v.quantity, 10);
        if (!pieces || pieces < 1) {
          errs.push(t('addDish.validation.pieces', { row }));
        }
      }
    });
    // Every non-seasonal variant must carry a Normal price for every selected
    // location — a dish can't be saved or updated with blank prices unless it's
    // marked Seasonal (priced day-of). Add-ons may be free, so they're exempt;
    // combos already returned above (they use the combo price).
    if (dishForm.kind !== 'addon') {
      // Only require prices for locations the Price matrix can actually show:
      // the dish may still be attached to an inactive branch that the (active
      // only) locations list omits — demanding a price for it would make the
      // error impossible to clear from this form.
      const visibleLocationIds = new Set((locations || []).map((l) => Number(l.id)));
      variants.forEach((v, i) => {
        if (v.is_seasonal) return;
        const priceByLoc = new Map(
          (v.location_prices || []).map((r) => [Number(r.location_id), r.price]),
        );
        const missing = (selectedLocationIds || []).some((lid) => {
          if (!visibleLocationIds.has(Number(lid))) return false;
          const p = parseFloat(priceByLoc.get(Number(lid)));
          return isNaN(p) || p <= 0;
        });
        if (missing) {
          errs.push(t('addDish.validation.variantPrice', {
            row: t('addDish.validation.variantPrefix', { n: i + 1 })
              + (v.variant_name?.trim() ? ` (${v.variant_name.trim()})` : ''),
          }));
        }
      });
    }
    // Every add-on placed in the matrix must be priced (> 0) for EVERY variant —
    // a zero / blank add-on cell is a configuration mistake, not a free add-on
    // (those are dish-level Complimentary items). Only the 'dish' kind shows the
    // matrix, so skip the check otherwise.
    if (dishForm.kind === 'dish') {
      const addonNameById = new Map(
        (allDishes || []).map((d) => [Number(d.id), d.name]),
      );
      const addonIds = [...new Set(
        variants.flatMap((v) => (v.addons || []).map((a) => Number(a.addon_id)))
          .filter((id) => Number.isFinite(id) && id > 0),
      )];
      addonIds.forEach((addonId) => {
        const unpriced = variants.some((v) => {
          const r = (v.addons || []).find((a) => Number(a.addon_id) === addonId);
          if (!r) return true; // add-on missing on this variant
          if (r.price === '' || r.price == null) return true;
          const p = parseFloat(r.price);
          return isNaN(p) || p <= 0;
        });
        if (unpriced) {
          errs.push(t('addDish.validation.addonMatrixPrice', {
            addon: addonNameById.get(addonId) || `Add-on #${addonId}`,
          }));
        }
      });
    }
    // No two variants may share the same display name (case-insensitive).
    const nameCounts = new Map();
    variants.forEach((v) => {
      const key = (v.variant_name || '').trim().toLowerCase();
      if (key) nameCounts.set(key, (nameCounts.get(key) || 0) + 1);
    });
    const dupes = [...nameCounts.entries()].filter(([, n]) => n > 1);
    if (dupes.length) {
      const original = variants.find(
        (v) => (v.variant_name || '').trim().toLowerCase() === dupes[0][0],
      );
      errs.push(
        t('addDish.validation.variantDuplicate', {
          name: original?.variant_name?.trim() || dupes[0][0],
        }),
      );
    }
    return errs;
  };

  // Returns true on a successful save, false otherwise (validation/API error).
  // Pass skipRedirect when the caller wants to handle navigation itself (e.g.
  // the navigation guard saves a draft, then sends the user to where they
  // clicked instead of the default dish list).
  // Canonical string of everything the save persists. Derived values that
  // settle after load without a user edit (auto-composed variant names) are
  // excluded so they never read as a change.
  const serializeFormState = () => JSON.stringify({
    dishForm: { ...dishForm, image: dishForm.image ? 'new-file' : null },
    imagePreview: imagePreview || null,
    removeExistingImage: !!removeExistingImage,
    hasVariants,
    comboPrice,
    comboItems: [...selectedComboItemIds].sort(),
    ingredients: [...selectedIngredientIds].sort(),
    allergens: [...selectedAllergenIds].sort(),
    tags: [...selectedTagIds].sort(),
    sides: [...selectedSideIds].sort(),
    beverages: [...selectedBeverageIds].sort(),
    recCombos: [...selectedRecommendedComboIds].sort(),
    garnishes: [...selectedGarnishIds].sort(),
    locations: [...selectedLocationIds].sort(),
    variants: variants.map((v) => ({
      ...v,
      variant_name: v.variant_name_source === 'custom' ? v.variant_name : '',
    })),
  });

  // Take the baseline once the loaded dish has been committed to state.
  useEffect(() => {
    if (snapshotArm) initialSnapRef.current = serializeFormState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotArm]);

  const handleSubmitDish = async (e, { asDraft = false, skipRedirect = false } = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormErrors([]);

    // Editing with no actual change → tell the user instead of a no-op save.
    if (editingDish && initialSnapRef.current
        && serializeFormState() === initialSnapRef.current) {
      toast({
        title: 'Nothing to update',
        description: 'No changes were made to this item.',
      });
      return false;
    }

    // Drafts may be incomplete on purpose — only a name is required so the work
    // can be parked and finished later. A normal save runs the full validation.
    const errs = asDraft
      ? (dishForm.name?.trim() ? [] : [t('addDish.validation.nameRequired')])
      : collectValidationErrors();
    if (errs.length) {
      setFormErrors(errs);
      // Don't fail silently: the error banner lives at the top of a long form,
      // so a click from the bottom looked like nothing happened. Surface it.
      toast({
        title: t('addDish.validation.header'),
        description: errs[0],
        variant: 'destructive',
      });
      // Defer so the banner is in the DOM before we scroll to it.
      setTimeout(() => {
        errorBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return false;
    }

    setIsLoading(true);
    try {
      const isCombo = dishForm.kind === 'combo';
      const isAddon = dishForm.kind === 'addon';
      // Variants toggled OFF → the dish has a single base variant; never persist
      // the extra rows (guards against a stale multi-row state).
      const baseVariants = (!hasVariants && !isComboKind && !isAddon)
        ? variants.slice(0, 1)
        : variants;
      // For add-ons the brand is picked once in BASIC INFORMATION and applied
      // to every variant — so a Coke add-on with 250ml/500ml/1L all share the
      // same brand without forcing the user to set it per row.
      const variantsForSave = isAddon
        ? baseVariants.map((v) => ({
            ...v,
            brand_id: dishForm.brand_master_id || '',
            // Add-ons carry no price — every variant is saved free (price 0).
            is_free: true,
          }))
        : baseVariants;
      // For combos we synthesize a single variant carrying the combo price so
      // the backend (which always expects ≥1 variant) can persist it without
      // schema changes. Real ingredient/tag/recommendation lists are skipped.
      const sanitizedVariants = isCombo
        ? [mapVariantForSave({
            ...buildVariant({ display_order: 0 }),
            variant_name: dishForm.name || 'Combo',
            size: firstSize,
            unit: 'piece',
            price: comboPrice,
            // Combos use a flat Complimentary picker, but the items still live
            // on the synthesized variant — pull them from variants[0] so they
            // survive the save round-trip.
            complimentary_items: (variants[0]?.complimentary_items) || [],
          }, 0)]
        : variantsForSave.map(mapVariantForSave);

      const formData = new FormData();
      formData.append('name', dishForm.name);
      formData.append('description', dishForm.description || '');
      formData.append('dish_type', dishForm.dish_type);
      formData.append('kind', dishForm.kind || 'dish');
      // The backend's create/update path keys off a single `type` discriminator
      // ('dish' | 'beverage' | 'addon' | 'combo' | 'store_bought' | 'bakery' |
      // 'liquor') and derives kind+dish_type from it. Without this every item
      // defaulted to a plain 'dish'. Mirror the form's kind/dish_type into it.
      const dishTypeDiscriminator = dishForm.kind === 'combo'
        ? 'combo'
        : dishForm.kind === 'addon'
          ? 'addon'
          : ['beverage', 'store_bought', 'bakery', 'liquor'].includes(dishForm.dish_type)
            ? dishForm.dish_type
            : 'dish';
      formData.append('type', dishTypeDiscriminator);
      if (dishForm.category_master_id) {
        formData.append('category_master_id', dishForm.category_master_id);
      }
      if (dishForm.sub_category_master_id) {
        formData.append('sub_category_master_id', dishForm.sub_category_master_id);
      }
      formData.append('is_vegetarian', dishForm.is_vegetarian);
      formData.append('is_vegan', dishForm.is_vegan);
      // Alcohol + serve temperature apply to drinks: soft beverages and liquor.
      const isDrinkItem = dishForm.dish_type === 'beverage' || dishForm.dish_type === 'liquor';
      formData.append(
        'contains_alcohol',
        isDrinkItem ? !!dishForm.contains_alcohol : false,
      );
      formData.append('is_extra_spicy', dishForm.spicy_level === 'extra' || dishForm.is_extra_spicy);
      formData.append('spicy_level', dishForm.spicy_level || '');
      formData.append('serve_temperature', isDrinkItem ? (dishForm.serve_temperature || '') : '');
      // Beverage type + milk cascade. Only sent for beverages; milk fields only
      // when the chosen type allows milk and the dish is marked has_milk. The
      // backend re-validates this, so the form is just the first gate.
      const isBeverageItem = dishForm.dish_type === 'beverage';
      const bevTypeAllowsMilk = isBeverageItem
        && beverageTypeOptions.some(
          (b) => String(b.id) === String(dishForm.beverage_type_id) && b.allows_milk,
        );
      const bevHasMilk = bevTypeAllowsMilk && !!dishForm.has_milk;
      formData.append('beverage_type_id', isBeverageItem ? (dishForm.beverage_type_id || '') : '');
      formData.append('has_milk', bevHasMilk ? 'true' : 'false');
      formData.append('milk_type_id', bevHasMilk ? (dishForm.milk_type_id || '') : '');
      // Egg flag is beverage-only; always send so unticking clears it.
      formData.append('contains_egg', (isBeverageItem && !!dishForm.contains_egg) ? 'true' : 'false');
      formData.append('addon_source', (dishForm.kind === 'addon' || dishForm.dish_type === 'beverage') ? (dishForm.addon_source || '') : '');
      // Liquor / bar metadata — only sent for liquor (the backend ignores it
      // otherwise). FK ids + bottle size + identity fields; sent only when filled
      // so update stays partial.
      if (dishForm.dish_type === 'liquor') {
        // Source (brewed vs bought-in) is a Beer-only field — never persist it for
        // other liquor types even if a value lingers from a Type switch.
        const pickedTypeIsBeer = (
          liquorTypeOptions.find((o) => String(o.id) === String(dishForm.liquor_type_id))?.name || ''
        ).toLowerCase().includes('beer');
        const liquorStr = {
          country: dishForm.country, age_statement: dishForm.age_statement,
          liquor_serve_temp: dishForm.liquor_serve_temp,
          liquor_source: pickedTypeIsBeer ? dishForm.liquor_source : '',
          storage_location: dishForm.storage_location,
          supplier: dishForm.supplier, hsn_code: dishForm.hsn_code,
          pos_button_color: dishForm.pos_button_color, liquor_allergen_notes: dishForm.liquor_allergen_notes,
          display_name: dishForm.display_name,
        };
        Object.entries(liquorStr).forEach(([k, v]) => formData.append(k, v || ''));
        const liquorFk = {
          liquor_type_id: dishForm.liquor_type_id, liquor_category_id: dishForm.liquor_category_id,
          liquor_sub_category_id: dishForm.liquor_sub_category_id, liquor_brand_id: dishForm.liquor_brand_id,
          liquor_region_id: dishForm.liquor_region_id, premium_level_id: dishForm.premium_level_id,
        };
        Object.entries(liquorFk).forEach(([k, v]) => formData.append(k, v || ''));
        formData.append('bottle_size_ml', dishForm.bottle_size_ml || '');
        formData.append('abv', dishForm.abv || '');
        // User-entered SKU (unique per restaurant). Blank → backend auto-assigns.
        formData.append('sku', (dishForm.sku || '').trim());
      }
      // Add-on "sold separately" flag — only meaningful for add-ons. When off the
      // add-on is bundle-only, so its take-away / delivery fulfilment is forced off.
      const addonSeparate = dishForm.kind === 'addon' && !!dishForm.sold_separately;
      formData.append('sold_separately', dishForm.kind === 'addon' ? String(addonSeparate) : '');
      // Style (gravy_style) and Cooked In apply to food dishes AND add-ons.
      const isFoodItem = dishForm.kind !== 'combo' && dishForm.dish_type === 'food';
      formData.append('gravy_style', isFoodItem ? (dishForm.gravy_style || '') : '');
      formData.append('cooking_method', isFoodItem ? (dishForm.cooking_method || '') : '');
      formData.append('cooking_medium', isFoodItem ? (dishForm.cooking_medium || '') : '');
      // Non-veg type only applies when the dish is non-veg (not vegetarian and not vegan).
      const isNonVeg = !dishForm.is_vegetarian && !dishForm.is_vegan;
      // Multi non-veg types. Also send the first as the legacy single FK so any
      // backward-compat reader stays correct.
      const nonVegTypeIds = isNonVeg ? (dishForm.non_veg_type_ids || []).map(Number).filter(Boolean) : [];
      formData.append('non_veg_type_ids', JSON.stringify(nonVegTypeIds));
      formData.append('non_veg_type_id', nonVegTypeIds.length ? String(nonVegTypeIds[0]) : '');
      const prepTime = parseInt(dishForm.prep_time, 10) || 0;
      const cookTime = parseInt(dishForm.cook_time, 10) || 0;
      formData.append('prep_time', String(prepTime));
      formData.append('cook_time', String(cookTime));
      formData.append('time_to_cook', String(prepTime + cookTime));
      // Drafts are saved inactive so they can never surface on a live menu;
      // saving normally clears the draft flag and respects the Active toggle.
      formData.append('is_available', asDraft ? false : dishForm.is_available);
      formData.append('is_draft', asDraft ? 'true' : 'false');
      if (dishForm.image) {
        formData.append('image', dishForm.image);
      } else if (editingDish && removeExistingImage) {
        formData.append('remove_image', 'true');
      }
      formData.append('variants', JSON.stringify(sanitizedVariants));
      formData.append('ingredient_ids', JSON.stringify(isCombo ? [] : selectedIngredientIds));
      formData.append('tag_ids', JSON.stringify(selectedTagIds));
      // Add-ons are now priced per-variant (variant.addons[]). The dish-level
      // recommended_addons M2M is kept as the UNION of every variant's add-ons
      // so consumers that only need "which add-ons" keep working. The backend
      // also re-derives this union from the variants, but we send it too.
      const addonUnionIds = [...new Set(
        (variants || [])
          .flatMap((v) => (v.addons || []).map((a) => Number(a.addon_id)))
          .filter((id) => Number.isFinite(id) && id > 0),
      )];
      formData.append('recommended_addon_ids', JSON.stringify(isCombo ? [] : addonUnionIds));
      formData.append('recommended_side_ids', JSON.stringify(isCombo ? [] : selectedSideIds));
      formData.append('recommended_beverage_ids', JSON.stringify(isCombo ? [] : selectedBeverageIds));
      formData.append('recommended_combo_ids', JSON.stringify(isCombo ? [] : selectedRecommendedComboIds));
      // Complimentary is stored dish-level. The variant matrix is per-variant in
      // the UI, so send the UNION of every variant's complimentary dish ids.
      const complimentaryIds = [...new Set(
        (variants || [])
          .flatMap((v) => (v.complimentary_items || []).map((c) => Number(c.dish_id)))
          .filter((id) => Number.isFinite(id) && id > 0),
      )];
      formData.append('complimentary_item_ids', JSON.stringify(isCombo ? [] : complimentaryIds));
      formData.append('allergen_tag_ids', JSON.stringify(isCombo ? [] : selectedAllergenIds));
      // -- Kitchen operations -- //
      // Kitchen ops are multi-select now — send id arrays (empty for combos).
      const kitchenIds = (arr) => JSON.stringify(isCombo ? [] : (arr || []).map(Number).filter(Boolean));
      formData.append('cooking_station_ids', kitchenIds(dishForm.cooking_station_ids));
      formData.append('plating_note_ids',    kitchenIds(dishForm.plating_note_ids));
      formData.append('serving_vessel_ids',  kitchenIds(dishForm.serving_vessel_ids));
      formData.append('kitchen_notes',      isCombo ? '' : (dishForm.kitchen_notes || ''));
      // Strip rows where both key and value are empty so we don't persist noise.
      const cleanedCustomFields = isCombo
        ? []
        : (dishForm.custom_fields || [])
            .map((r) => ({ key: (r.key || '').trim(), value: (r.value || '').trim() }))
            .filter((r) => r.key || r.value)
            .slice(0, 3);
      formData.append('custom_fields', JSON.stringify(cleanedCustomFields));
      formData.append('garnish_ids', JSON.stringify(isCombo ? [] : selectedGarnishIds));
      formData.append('location_ids', JSON.stringify(selectedLocationIds));
      // Dish-level tax — sends either tax_label_id or tax_group_id (exclusive).
      // Empty string clears whichever side wasn't picked so the server can
      // unset a previous selection on edit.
      formData.append(
        'tax_label_id',
        dishForm.tax_type === 'label' && dishForm.tax_id ? String(dishForm.tax_id) : '',
      );
      formData.append(
        'tax_group_id',
        dishForm.tax_type === 'group' && dishForm.tax_id ? String(dishForm.tax_id) : '',
      );
      // Whether those typed prices already have the tax inside.
      formData.append('price_includes_tax', dishForm.price_includes_tax ? 'true' : 'false');
      if (isCombo) {
        formData.append('combo_item_ids', JSON.stringify(selectedComboItemIds));
        formData.append('combo_price', String(parseFloat(comboPrice) || 0));
      }

      let response;
      if (editingDish) {
        response = await updateDish(restaurantId, editingDish.id, formData);
      } else {
        response = await createDish(restaurantId, formData);
      }

      if (response?.success) {
        toast({
          title: t('addDish.toast.success'),
          description: editingDish
            ? t('addDish.toast.dishUpdatedSuccess')
            : t('addDish.toast.dishCreatedSuccess'),
        });
        if (!skipRedirect) navigateToDishList();
        // Staying on the form (Save & continue) — the saved state is the new
        // baseline, so an immediate re-save reports "nothing to update".
        else initialSnapRef.current = serializeFormState();
        return true;
      } else {
        throw response?.error || t('addDish.toast.failedSave');
      }
    } catch (error) {
      const msg = formatApiError(error, t('addDish.toast.failedSave'));
      setFormErrors([msg]);
      toast({ title: t('addDish.toast.error'), description: msg, variant: 'destructive' });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // All-seasonal derived flag — true when every existing variant is seasonal.
  // Used by addVariantRow so new rows inherit the dish-wide seasonal state,
  // and by the Seasonal tick in the Price matrix header so toggling it locks
  // every variant's prices in one shot.
  const allSeasonal = variants.length > 0 && variants.every((v) => !!v.is_seasonal);

  const setAllSeasonal = (next) => {
    setVariants((prev) => prev.map((v) => ({ ...v, is_seasonal: next })));
  };

  // "Same price for all locations" — when on, the Price matrix collapses to a
  // single "All locations" row and every edit fans out to all active branches.
  // Default on: most operators price a dish once for the whole chain. Lifted to
  // this component so the tick can sit beside Seasonal in the Price card header.
  const [sameForAll, setSameForAll] = useState(true);

  const activeLocations = (locations || []).filter((l) =>
    (selectedLocationIds || []).includes(l.id),
  );

  // Turning it on seeds every branch from the first active location's prices so
  // the collapsed row reflects real values instead of blanking branches that
  // were already priced.
  const enableSameForAll = (on) => {
    if (on && activeLocations.length > 1) {
      const srcId = activeLocations[0].id;
      const activeIds = new Set(activeLocations.map((l) => Number(l.id)));
      setVariants((prev) =>
        prev.map((v) => {
          const src = (v.location_prices || []).find(
            (p) => Number(p.location_id) === Number(srcId),
          ) || {};
          const triple = {
            price: src.price ?? '',
            takeaway_price: src.takeaway_price ?? '',
            delivery_price: src.delivery_price ?? '',
          };
          const seeded = activeLocations.map((loc) => {
            const existing = (v.location_prices || []).find(
              (p) => Number(p.location_id) === Number(loc.id),
            ) || {};
            return { ...existing, location_id: Number(loc.id), ...triple };
          });
          const others = (v.location_prices || []).filter(
            (p) => !activeIds.has(Number(p.location_id)),
          );
          return { ...v, location_prices: [...seeded, ...others] };
        }),
      );
    }
    setSameForAll(on);
  };

  const addVariantRow = () => {
    setVariants((prev) => {
      // Once there is more than one variant, "Dish Name" stops making sense
      // as a label source (two rows can't share the dish name without
      // confusion). Migrate any existing 'dish' row to its size label, or
      // fall back to the dish name as a custom string if no size is set.
      const migrated = prev.map((v) => {
        if (v.variant_name_source !== 'dish') return v;
        if (v.size) {
          const base = getSizeLabel(v.size);
          return {
            ...v,
            variant_name: [base, (v.variant_option || '').trim(), (v.variant_option_2 || '').trim()]
              .filter(Boolean)
              .join(' · '),
            variant_name_source: `size:${v.size}`,
          };
        }
        return {
          ...v,
          variant_name: v.variant_name || dishForm.name || '',
          variant_name_source: 'custom',
        };
      });
      return [
        ...migrated,
        buildVariant({
          // New rows start with no size and an empty, editable display name —
          // the user picks a size/add-ons or types their own name.
          size: '',
          variant_name: '',
          variant_name_source: 'custom',
          display_order: migrated.length,
          // New rows inherit the current seasonal state so the dish-wide tick
          // keeps prices locked no matter how many rows are added later.
          is_seasonal: allSeasonal,
          // Unit is dish-wide and locked to row 1's choice — new rows must
          // mirror it, otherwise rows can drift apart since their unit select
          // is disabled and the user has no way to fix it.
          unit: migrated[0]?.unit || defaultUnit,
        }),
      ];
    });
  };

  const removeVariantRow = (index) => {
    if (variants.length > 1) setVariants(variants.filter((_, i) => i !== index));
  };

  // ---- Coffee → milk-type variants --------------------------------------- //
  // When the chosen beverage type offers milk (allows_milk, e.g. Coffee) and
  // the dish is "With Milk", each milk type becomes a sellable variant. We
  // cross every distinct size currently on the dish with every active milk
  // type — so Small/Large × Whole/Almond yields four rows. With a single size
  // it collapses to one variant per milk type. The milk name rides in the
  // existing free-text `variant_option` (display name → "Size · Whole Milk").
  const distinctVariantSizes = () => {
    const sizes = [...new Set(variants.map((v) => v.size).filter(Boolean))];
    return sizes.length ? sizes : [firstSize];
  };

  const buildMilkVariants = () => {
    const milks = milkTypeOptions;
    if (!milks.length) return [];
    const sizes = distinctVariantSizes();
    const unit = variants[0]?.unit || defaultUnit;
    const quantity = variants[0]?.quantity || defaultQuantity;
    const rows = [];
    sizes.forEach((size) => {
      milks.forEach((milk) => {
        const base = getSizeLabel(size);
        rows.push(buildVariant({
          size,
          unit,
          quantity,
          variant_option: milk.name,
          variant_name: `${base} · ${milk.name}`,
          variant_name_source: `size:${size}`,
          is_seasonal: allSeasonal,
          display_order: rows.length,
        }));
      });
    });
    return rows;
  };

  // Untouched starter variant — safe to overwrite without losing user edits.
  const variantsArePristine = () =>
    variants.length === 1
    && !((variants[0].variant_option || '').trim())
    && !(String(variants[0].price ?? '').trim())
    && !(variants[0].location_prices || []).length;

  // Replace the variant list with the size × milk-type cross-product and reveal
  // the Variants tab. `silent` skips the confirm + toast (the auto-fill path
  // only fires on a pristine dish, so there is nothing to lose or announce).
  const applyMilkVariants = async ({ silent = false } = {}) => {
    const rows = buildMilkVariants();
    if (!rows.length) {
      if (!silent) {
        toast({
          title: 'No milk types yet',
          description: 'Add a milk type first (the + beside Milk Types).',
          variant: 'destructive',
        });
      }
      return;
    }
    if (!silent && !variantsArePristine()) {
      const ok = await confirm({
        title: 'Replace variants?',
        description: `Replace the current variants with ${rows.length} size × milk-type row(s)? `
          + 'Existing variant edits will be lost.',
        confirmText: 'Replace',
      });
      if (!ok) return;
    }
    setHasVariants(true);
    setVariants(rows);
    if (!silent) {
      toast({ title: `Created ${rows.length} milk-type variant${rows.length === 1 ? '' : 's'}` });
    }
  };

  // Serve temperature is a single choice of three: Hot, Cold, or Hot & Cold
  // ('both'). Mutually exclusive — clicking the active pill clears it.
  const SERVE_OPTIONS = [
    { value: 'hot',  label: '☕ Hot',         activeCls: 'border-rose-500 bg-rose-50 text-rose-700' },
    { value: 'cold', label: '🧊 Cold',        activeCls: 'border-sky-500 bg-sky-50 text-sky-700' },
    { value: 'both', label: '☕🧊 Hot & Cold', activeCls: 'border-violet-500 bg-violet-50 text-violet-700' },
  ];
  // Serve label as it reads inside a composed display name.
  const SERVE_NAME = { hot: 'Hot', cold: 'Cold', both: 'Hot & Cold' };
  // Coffee/milk-beverage prep option lists.
  const ICE_OPTIONS = [
    { value: 'none', label: 'No Ice' },
    { value: 'less', label: 'Less' },
    { value: 'normal', label: 'Normal' },
    { value: 'extra', label: 'Extra' },
  ];
  const SUGAR_OPTIONS = [
    { value: '0', label: 'No Sugar' },
    { value: '25', label: '25%' },
    { value: '50', label: '50%' },
    { value: '75', label: '75%' },
    { value: 'normal', label: 'Normal' },
  ];
  // A variant is served cold (so the Ice control applies) when it's Cold or Hot&Cold.
  const servesCold = (v) => v.serve_temperature === 'cold' || v.serve_temperature === 'both';
  // Display name is ALWAYS auto-composed: Size · Add-on 1 · Add-on 2 · Serve,
  // skipping the parts that aren't set — e.g. "Full · Cheese · Extra Sauce".
  // (For milk beverages, the milk name rides in variant_option.) There is no
  // custom name; with no size set (a single base variant) the dish name stands
  // in for the size part so the row is never nameless.
  const composeVariantName = (v) => {
    const base = v.size ? getSizeLabel(v.size) : (dishForm.name || '');
    return [
      base,
      // Beer serving format (Pint / Bottle / …) distinguishes otherwise-identical
      // rows, so it's part of the name — without it every beer format collapses
      // to the size label and reads as a duplicate.
      (v.beer_format || '').trim(),
      (v.variant_option || '').trim(),
      (v.variant_option_2 || '').trim(),
      SERVE_NAME[v.serve_temperature] || '',
    ]
      .filter(Boolean)
      .join(' · ');
  };

  const updateVariant = (index, field, value) => {
    setVariants((prev) => {
      const updated = [...prev];
      const variant = { ...updated[index] };
      let nextValue = value;
      // Size is optional — allow clearing it back to "not selected".
      if (field === 'unit' && !nextValue) nextValue = defaultUnit;
      variant[field] = nextValue;

      // A manual edit of the display name locks the row as custom so it stops
      // auto-recomposing from size/add-ons and keeps whatever the user typed.
      if (field === 'variant_name') {
        variant.variant_name_source = 'custom';
      }
      if (field === 'size' && variant.variant_name_source?.startsWith('size:')) {
        variant.variant_name_source = `size:${nextValue}`;
      }
      // Auto display-name = Size · Add-on 1 · Add-on 2 · Serve (see
      // composeVariantName), recomputed whenever any of those change. The name
      // is never custom, so this always runs.
      if (field === 'size' || field === 'variant_option' || field === 'variant_option_2' || field === 'serve_temperature' || field === 'beer_format') {
        // Custom-named rows keep their name (it's the option's identity, e.g.
        // "Tortilla") — recomposing would silently rename them to "Full".
        if (variant.variant_name_source !== 'custom') {
          variant.variant_name = composeVariantName(variant);
        }
      }
      // Allergens are inherited from the chosen add-ons — the user no longer
      // picks them per variant. Recompute the union whenever an add-on changes.
      if (field === 'variant_option' || field === 'variant_option_2') {
        const ids = new Set();
        [variant.variant_option, variant.variant_option_2]
          .map((s) => (s || '').trim())
          .filter(Boolean)
          .forEach((nm) => {
            const addon = (allDishes || []).find((d) => d.kind === 'addon' && d.name === nm);
            (addon?.allergen_tags || []).forEach((a) =>
              ids.add(Number(typeof a === 'object' ? a.id : a)));
          });
        variant.allergens = [...ids];
      }
      if (field === 'unit' && !variant.quantity) variant.quantity = defaultQuantity;

      updated[index] = variant;

      // Unit is chosen once on row 1 and applies to every variant — the dish
      // has a single canonical unit, so row 1's selection propagates to all
      // sibling rows (the rows 2+ selects are disabled in the UI).
      if (field === 'unit' && index === 0) {
        for (let i = 1; i < updated.length; i += 1) {
          if (updated[i].unit !== nextValue) {
            updated[i] = { ...updated[i], unit: nextValue };
          }
        }
      }

      return updated;
    });
  };

  // ── Non-veg type → locked ingredient ────────────────────────────────────
  // Picking a Non-Veg type (Chicken, Fish…) makes that protein an ingredient of
  // the dish: it is auto-added to the ingredient list and LOCKED there (can't be
  // unticked) for as long as the type stays selected. The ingredient is matched
  // to the type by name (case-insensitive); if none exists yet it is created.
  // A dish can carry several Non-Veg types — each maps to a locked protein.
  const lockedNonVegNames = useMemo(() => {
    const isNonVeg = !dishForm.is_vegetarian && !dishForm.is_vegan
      && dishForm.dish_type !== 'beverage' && dishForm.dish_type !== 'liquor';
    if (!isNonVeg) return [];
    return (dishForm.non_veg_type_ids || [])
      .map((id) => nonVegOptions.find((o) => String(o.id) === String(id)))
      .map((nv) => (nv?.name || '').trim())
      .filter(Boolean);
  }, [dishForm.is_vegetarian, dishForm.is_vegan, dishForm.dish_type, dishForm.non_veg_type_ids, nonVegOptions]);

  const lockedIngredientIds = useMemo(() => {
    if (!lockedNonVegNames.length) return [];
    const wanted = new Set(lockedNonVegNames.map((n) => n.toLowerCase()));
    return ingredients
      .filter((i) => wanted.has((i.name || '').trim().toLowerCase()))
      .map((i) => i.id);
  }, [lockedNonVegNames, ingredients]);

  // Keep the locked proteins selected (and present in every variant's recipe).
  // Fires when a type is picked, after an auto-created ingredient reloads, and
  // when editing an existing non-veg dish. Proteins whose type was REMOVED (or
  // when the diet leaves Non-Veg) are swapped out so they don't linger as
  // phantom selections — e.g. unticking Chicken drops it from the recipe.
  const prevLockedIdsRef = useRef([]);
  useEffect(() => {
    const prevLockedIds = prevLockedIdsRef.current;
    prevLockedIdsRef.current = lockedIngredientIds;
    const nowSet = new Set(lockedIngredientIds.map(Number));
    // Drop proteins a now-removed Non-Veg type had auto-added.
    const dropped = prevLockedIds.filter((id) => !nowSet.has(Number(id)));
    if (dropped.length) {
      const dropSet = new Set(dropped.map(Number));
      setSelectedIngredientIds((prev) => prev.filter((x) => !dropSet.has(Number(x))));
      setVariants((prev) => prev.map((v) => ({
        ...v,
        recipe: (v.recipe || []).filter((r) => !dropSet.has(Number(r.ingredient_id))),
      })));
    }
    if (!lockedIngredientIds.length) return;
    setSelectedIngredientIds((prev) => {
      const add = lockedIngredientIds.filter((id) => !prev.includes(id));
      return add.length ? [...prev, ...add] : prev;
    });
    setVariants((prev) => prev.map((v) => {
      const recipe = v.recipe || [];
      const have = new Set(recipe.map((r) => Number(r.ingredient_id)));
      const add = lockedIngredientIds
        .filter((id) => !have.has(Number(id)))
        .map((id) => ({ ingredient_id: Number(id), quantity: '', unit: '' }));
      return add.length ? { ...v, recipe: [...recipe, ...add] } : v;
    }));
  }, [lockedIngredientIds]);

  // Ensure an ingredient exists for the chosen non-veg type, creating one if the
  // master doesn't have it yet. Selection/locking is handled by the effect above.
  const ensureNonVegIngredient = async (nonVegId) => {
    if (!nonVegId) return;
    const nv = nonVegOptions.find((o) => String(o.id) === String(nonVegId));
    const name = (nv?.name || '').trim();
    if (!name) return;
    const exists = ingredients.some(
      (i) => (i.name || '').trim().toLowerCase() === name.toLowerCase(),
    );
    if (exists) return;
    try {
      await createIngredient(restaurantId, { name, track_inventory: true });
      await loadIngredients();
    } catch (e) {
      toast({
        title: 'Could not add non-veg ingredient',
        description: formatApiError(e, `"${name}" couldn't be added to the ingredient list.`),
        variant: 'destructive',
      });
    }
  };

  const toggleIngredient = (id) => {
    // The protein behind the current Non-Veg type is locked on — block removal.
    if (lockedIngredientIds.map(Number).includes(Number(id)) && selectedIngredientIds.includes(id)) return;
    const wasSelected = selectedIngredientIds.includes(id);
    setSelectedIngredientIds((prev) =>
      wasSelected ? prev.filter((x) => x !== id) : [...prev, id],
    );
    // Mirror the change in the variant recipe matrix so the picker and the
    // matrix stay in sync: turning on seeds an empty row in each variant,
    // turning off drops the row everywhere.
    setVariants((variantsPrev) =>
      variantsPrev.map((v) => {
        const recipe = v.recipe || [];
        if (wasSelected) {
          return {
            ...v,
            recipe: recipe.filter((r) => Number(r.ingredient_id) !== Number(id)),
          };
        }
        if (recipe.some((r) => Number(r.ingredient_id) === Number(id))) return v;
        return {
          ...v,
          recipe: [...recipe, { ingredient_id: Number(id), quantity: '', unit: '' }],
        };
      }),
    );
  };

  const selectedIngredients = ingredients.filter((i) => selectedIngredientIds.includes(i.id));
  // Selected ingredients float to the top (stable sort keeps each group's order).
  const selectedIngredientSet = new Set(selectedIngredientIds);
  const _searchAndFloatSelected = (list) => (ingredientSearch.trim()
    ? list.filter((i) => i.name.toLowerCase().includes(ingredientSearch.toLowerCase()))
    : list
  )
    .slice()
    .sort((a, b) => (selectedIngredientSet.has(b.id) ? 1 : 0) - (selectedIngredientSet.has(a.id) ? 1 : 0));

  // The ingredient picker is split: items flagged for inventory tracking get
  // their own "Select Ingredients" list on top (they're the ones you measure /
  // cost), the rest fall to "Other Ingredients" below. Both feed the same
  // selectedIngredientIds.
  const trackedIngredients = _searchAndFloatSelected(ingredients.filter((i) => i.track_inventory));
  const otherIngredients = _searchAndFloatSelected(ingredients.filter((i) => !i.track_inventory));

  // Garnishes reuse the ingredient master — filter the same `ingredients`
  // list by the local search box, independent of the ingredient picker.
  const filteredGarnishes = garnishSearch.trim()
    ? ingredients.filter((i) => i.name.toLowerCase().includes(garnishSearch.toLowerCase()))
    : ingredients;

  const toggleGarnish = (id) => {
    setSelectedGarnishIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Multi-select kitchen ops (cooking stations / plating notes / serving
  // vessels). `field` is the dishForm key; ids are stored as strings.
  const toggleKitchenOp = (field, id) => {
    setDishForm((p) => {
      const cur = p[field] || [];
      return { ...p, [field]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
    });
  };


  // -- Form-wide accent palette ------------------------------------------
  // The active dish-kind drives the accent for primary buttons, active pills
  // and the top strip of the form, so the whole page picks up the color of
  // the tile the user has chosen. Mirrors the SCHEME used by the kind tiles.
  //   Dish     → rose       Beverage → sky/info
  //   Add-on   → emerald    Combo    → violet
  const accentKind = dishForm.kind === 'combo'
    ? 'combo'
    : dishForm.kind === 'addon'
      ? 'addon'
      : dishForm.dish_type === 'beverage'
        ? 'beverage'
        : 'dish';
  // Beverage type + milk cascade: the chosen type decides whether the Milk
  // question shows at all (only types flagged allows_milk, e.g. Coffee).
  const selectedBeverageType = beverageTypeOptions.find(
    (b) => String(b.id) === String(dishForm.beverage_type_id),
  );
  const beverageAllowsMilk = !!selectedBeverageType?.allows_milk;
  // For a milk beverage the second-dimension column is the milk type (chosen
  // from the master list) rather than a free-text Option — the milk name still
  // rides in variant_option so the display name keeps composing as "Size · Whole".
  const isMilkBeverage = dishForm.dish_type === 'beverage' && beverageAllowsMilk && !!dishForm.has_milk;
  // Add-on catalog for the variant's two option dimensions (Add on 1 / Add on
  // 2). Sourced from other dishes of kind 'addon'; the chosen add-on *name*
  // rides in variant_option / variant_option_2 so the display name composes as
  // "Size · Add-on 1 · Add-on 2". Excludes this dish (an add-on can't include
  // itself when editing one).
  const addonChoices = (allDishes || []).filter(
    (d) => d.kind === 'addon' && d.id !== editingDish?.id,
  );
  const formAccent = {
    dish: {
      strip:       'bg-orange-300',
      btnPrimary:  'bg-orange-500 text-white hover:bg-orange-600',
      pillActive:  'border-orange-400 bg-orange-50 text-orange-800',
      ringSoft:    'ring-orange-200',
      textAccent:  'text-orange-700',
      bgSoft:      'bg-orange-50',
    },
    beverage: {
      strip:       'bg-sky-300',
      btnPrimary:  'bg-sky-600 text-white hover:bg-sky-700',
      pillActive:  'border-sky-400 bg-sky-50 text-sky-800',
      ringSoft:    'ring-sky-200',
      textAccent:  'text-sky-700',
      bgSoft:      'bg-sky-50',
    },
    addon: {
      strip:       'bg-emerald-300',
      btnPrimary:  'bg-emerald-600 text-white hover:bg-emerald-700',
      pillActive:  'border-emerald-400 bg-emerald-50 text-emerald-800',
      ringSoft:    'ring-emerald-200',
      textAccent:  'text-emerald-700',
      bgSoft:      'bg-emerald-50',
    },
    combo: {
      strip:       'bg-violet-300',
      btnPrimary:  'bg-violet-600 text-white hover:bg-violet-700',
      pillActive:  'border-violet-400 bg-violet-50 text-violet-800',
      ringSoft:    'ring-violet-200',
      textAccent:  'text-violet-700',
      bgSoft:      'bg-violet-50',
    },
  }[accentKind];

  // -- Combo aggregation --------------------------------------------------
  // When the dish is a combo, surface live totals (calories, allergens) and
  // the picked items so the user can sanity-check the bundle before saving.
  const isComboKind = dishForm.kind === 'combo';
  const isAddonKind = dishForm.kind === 'addon';
  // Liquor is a bottle, not a cooked dish — it gets its own Liquor Details tab and
  // hides the food-oriented tabs (Attributes, Ingredients, Kitchen, Add-ons).
  const isLiquor = dishForm.dish_type === 'liquor';
  const isBeverage = dishForm.dish_type === 'beverage';
  // Beer sells by serving format (Pint / Bottle / Glass / Pitcher / Tower)
  // rather than ml pegs, so the variants table swaps in a Serving Format column
  // when the picked liquor Type is Beer. Matched on the type master's name.
  const selectedLiquorTypeName = (
    liquorTypeOptions.find((o) => String(o.id) === String(dishForm.liquor_type_id))?.name || ''
  ).toLowerCase();
  const isBeer = isLiquor && selectedLiquorTypeName.includes('beer');
  const BEER_FORMATS = ['Pint', 'Bottle', 'Glass', 'Pitcher', 'Tower'];

  // Beer is usually offered in every serving format at once, so "Load serving
  // sizes" drops in one variant row per format (Pint / Bottle / Glass / Pitcher
  // / Tower) instead of adding them one by one. The format rides in beer_format
  // and doubles as the row's display name.
  const buildBeerVariants = () => {
    const size = variants[0]?.size || firstSize;
    const unit = variants[0]?.unit || defaultUnit;
    return BEER_FORMATS.map((format, i) => buildVariant({
      size,
      unit,
      beer_format: format,
      variant_name: format,
      variant_name_source: 'custom',
      is_seasonal: allSeasonal,
      display_order: i,
    }));
  };

  const applyBeerVariants = async () => {
    const rows = buildBeerVariants();
    if (!variantsArePristine()) {
      const ok = await confirm({
        title: 'Replace variants?',
        description: `Replace the current variants with ${rows.length} beer serving-format rows `
          + '(Pint, Bottle, Glass, Pitcher, Tower)? Existing variant edits will be lost.',
        confirmText: 'Replace',
      });
      if (!ok) return;
    }
    setHasVariants(true);
    setVariants(rows);
    toast({ title: `Loaded ${rows.length} beer serving-format variants` });
  };
  // Dish base diet + an add-on→diet lookup. A variant's effective diet is
  // DERIVED from the dish's base diet plus the diets of the add-ons attached to
  // that variant (a non-veg add-on makes a veg/vegan variant Non-Veg). The map
  // is keyed by add-on dish id so the variants table and AddonMatrix agree.
  const dishBaseDiet = dietOf(dishForm);
  const addonDietById = useMemo(() => {
    const m = new Map();
    (allDishes || []).forEach((d) => {
      if (d.kind === 'addon') m.set(Number(d.id), dietOf(d));
    });
    return m;
  }, [allDishes]);
  // Add-on *name* → diet, for the two option-dimension picks (Add on 1 / Add on
  // 2) which ride in variant_option / variant_option_2 as add-on names.
  const addonDietByName = useMemo(() => {
    const m = new Map();
    (allDishes || []).forEach((d) => {
      if (d.kind === 'addon') m.set((d.name || '').trim().toLowerCase(), dietOf(d));
    });
    return m;
  }, [allDishes]);
  // The effective diet for a single variant, derived from the dish base diet plus
  // every add-on on the variant: the priced add-ons (variant.addons) AND the
  // Add on 1 / Add on 2 dimension picks. If any one of them is non-veg the
  // variant becomes Non-Veg (see effectiveVariantDiet).
  const variantDietFor = (variant) => effectiveVariantDiet(
    dishBaseDiet,
    [
      ...(variant?.addons || []).map((a) => addonDietById.get(Number(a.addon_id)) || 'veg'),
      ...['variant_option', 'variant_option_2']
        .map((f) => (variant?.[f] || '').trim().toLowerCase())
        .filter(Boolean)
        .map((name) => addonDietByName.get(name) || 'veg'),
    ],
  );
  // Diet only applies to food/add-ons; beverages & liquor use the Alcoholic
  // toggle instead (matches the dish-level Diet selector's visibility).
  const showDietColumn = dishForm.dish_type !== 'beverage' && dishForm.dish_type !== 'liquor';
  // Add-ons are bundled with a parent dish by default. Ticking "to be sold
  // separately" makes the add-on orderable on its own — which is the only time
  // the Take Away / Delivery fulfilment toggles are relevant for an add-on.
  const addonSellsSeparately = isAddonKind && !!dishForm.sold_separately;
  // The fulfilment section (Take Away / Delivery) shows for every non-combo
  // item, but for add-ons only once they're marked sold-separately. Liquor is
  // poured and served at the bar — it has no take-away/delivery channel — so
  // the section is hidden for it entirely.
  const showFulfilment = !isComboKind && !isLiquor && (!isAddonKind || addonSellsSeparately);
  // Noun for the page title — the form builds Dishes, Beverages, Add-ons and
  // Combos, so the header reflects what's actually being created/edited.
  const kindNounKey = isComboKind
    ? 'combo'
    : isAddonKind
      ? 'addon'
      : dishForm.dish_type === 'liquor'
        ? 'liquor'
        : dishForm.dish_type === 'beverage'
          ? 'beverage'
          : 'dish';
  const kindNoun = t(`addDish.pageHeader.kind.${kindNounKey}`);
  // Section tabs. Each tab maps to one or more cards in the form body (each card
  // is guarded by `activeTab === <key>`). `show` hides tabs with no content for
  // the current kind — combos have no variants/ingredients, add-ons hide the
  // advanced sections until the "Show advanced fields" toggle is on.
  const advancedShown = !isAddonKind || showAddonAdvanced;
  const TAB_DEFS = [
    { key: 'basic',           label: 'Basic Information',      show: true },
    // Liquor's own metadata (bottle size, brand, type/category, ABV…).
    { key: 'liquor',          label: 'Liquor Details',         show: isLiquor },
    // Hidden for combos (single combo price). Add-ons now carry their own
    // optional standalone price, so they get the Prices tab too.
    { key: 'prices',          label: 'Prices',                 show: !isComboKind },
    // Attributes groups Style/Cooked-In plus Tags, Custom Fields and Timing
    // (Spicy lives on Basic Information).
    // Hidden for add-ons — a dip/extra carries no style/timing of its own —
    // and for liquor (a bottle has no spice/plating style).
    { key: 'additional',      label: 'Attributes',             show: !isComboKind && !isAddonKind && !isLiquor },
    // Recipe groups Garnishes, Ingredients and Allergens.
    // Hidden for combos (they bundle dishes), add-ons (a dip/extra isn't composed
    // from tracked ingredients here) and liquor (a bought-in bottle).
    { key: 'composition',     label: 'Recipe',                 show: !isComboKind && !isAddonKind && !isLiquor },
    // Per-variant nutrition + the Health Meter. Combos aggregate from members.
    { key: 'nutrition',       label: 'Nutritional Info',       show: !isComboKind && !isLiquor },
    { key: 'kitchen',         label: 'Kitchen Instructions',   show: !isComboKind && !isAddonKind && !isLiquor && advancedShown },
    // Variants sits directly before Add On Items — the variant rows pick Add on
    // 1 / Add on 2, so the two tabs read as one group.
    { key: 'variants',        label: 'Variants',               show: !isComboKind && !isAddonKind && hasVariants },
    // A bottle isn't paired with add-on items.
    { key: 'recommendations', label: 'Add On Items',           show: dishForm.kind === 'dish' && !isLiquor },
    // Liquor (a bottle) doesn't bundle complimentary items.
    { key: 'complimentary',   label: isComboKind ? 'Combo & Complimentary' : 'Complimentary', show: isComboKind || (!isAddonKind && advancedShown && !isLiquor) },
  ];
  let visibleTabs = TAB_DEFS.filter((tabDef) => tabDef.show);
  // Surface Variants right after Basic Information for every kind that has it —
  // sizes/options (and, for drinks, hot/cold + milk prep) are the heart of the
  // item and everything downstream (Prices, Add Ons) keys off the variant rows,
  // so they belong second rather than buried mid-flow.
  {
    const variantsTab = visibleTabs.find((tabDef) => tabDef.key === 'variants');
    if (variantsTab) {
      const rest = visibleTabs.filter((tabDef) => tabDef.key !== 'variants');
      const basicIdx = rest.findIndex((tabDef) => tabDef.key === 'basic');
      const insertAt = basicIdx === -1 ? 0 : basicIdx + 1;
      visibleTabs = [...rest.slice(0, insertAt), variantsTab, ...rest.slice(insertAt)];
    }
  }
  // Surface Recipe (the base dish's own make-up) right after Basic Information —
  // the base composition is captured up front before variants/prices key off it.
  {
    const compositionTab = visibleTabs.find((tabDef) => tabDef.key === 'composition');
    if (compositionTab) {
      const rest = visibleTabs.filter((tabDef) => tabDef.key !== 'composition');
      const basicIdx = rest.findIndex((tabDef) => tabDef.key === 'basic');
      const insertAt = basicIdx === -1 ? 0 : basicIdx + 1;
      visibleTabs = [...rest.slice(0, insertAt), compositionTab, ...rest.slice(insertAt)];
    }
  }
  // Attributes (style/timing/tags) sits directly before Variants — the
  // dish's character is set before its sizes/options are enumerated.
  {
    const attributesTab = visibleTabs.find((tabDef) => tabDef.key === 'additional');
    const variantsIdx = visibleTabs.findIndex((tabDef) => tabDef.key === 'variants');
    if (attributesTab && variantsIdx !== -1) {
      const rest = visibleTabs.filter((tabDef) => tabDef.key !== 'additional');
      const insertAt = rest.findIndex((tabDef) => tabDef.key === 'variants');
      visibleTabs = [...rest.slice(0, insertAt), attributesTab, ...rest.slice(insertAt)];
    }
  }
  // If the open tab is hidden for this kind (e.g. add-on advanced fields turned
  // off while on the Kitchen tab), fall back to Basic so content never vanishes.
  const activeTabKey = visibleTabs.some((tabDef) => tabDef.key === activeTab) ? activeTab : 'basic';

  // Name and location are mandatory before leaving Basic Information — the
  // Prices matrix and per-location fields on later tabs depend on them. Block
  // the switch and nudge the user instead of letting them wander off with the
  // essentials still blank.
  // Liquor must carry a Type (Spirit / Beer / Wine…) — it drives the variant
  // serving format and the rest of the liquor cascade, so it's mandatory here.
  const liquorTypeMissing = isLiquor && !dishForm.liquor_type_id;
  const basicIncomplete = !dishForm.name?.trim() || !(selectedLocationIds || []).length || liquorTypeMissing;
  // An add-on's diet drives the diet of every variant it's attached to, so it's
  // mandatory on Basic Information — block the wizard (e.g. Go to Nutritional
  // Info) until it's picked, matching the save-time addonDietRequired check.
  const addonDietMissing = isAddonKind && !dishForm.diet_touched;
  // A dish name must be unique within the restaurant — block leaving Basic
  // Information (and saving) while it clashes with another dish.
  const trimmedDishName = (dishForm.name || '').trim().toLowerCase();
  const dishNameTaken = !!trimmedDishName && (allDishes || []).some(
    (d) => d.id !== editingDish?.id && (d.name || '').trim().toLowerCase() === trimmedDishName,
  );
  // Variant display names must be unique within the dish. Count keys so the
  // table can flag every clashing row; grab the first clash for messaging.
  const variantNameKeyCounts = variants.reduce((acc, v) => {
    const k = (v.variant_name || '').trim().toLowerCase();
    if (k) acc.set(k, (acc.get(k) || 0) + 1);
    return acc;
  }, new Map());
  const duplicateVariantName = (() => {
    for (const v of variants) {
      const k = (v.variant_name || '').trim().toLowerCase();
      if (k && variantNameKeyCounts.get(k) > 1) return (v.variant_name || '').trim();
    }
    return null;
  })();
  const requestTabChange = (key) => {
    if (key !== 'basic' && basicIncomplete) {
      const missingName = !dishForm.name?.trim();
      const missingLocations = !(selectedLocationIds || []).length;
      toast({
        title: missingName
          ? t('addDish.validation.nameRequired')
          : missingLocations
            ? t('addDish.validation.locationsRequired')
            : t('addDish.validation.liquorTypeRequired'),
        description: (!missingName && !missingLocations && liquorTypeMissing)
          ? 'Pick a liquor Type (Spirit, Beer, Wine…) on Basic Information first.'
          : 'Enter the dish name and pick at least one location on Basic Information first.',
        variant: 'destructive',
      });
      setActiveTab('basic');
      return;
    }
    // Block leaving Basic Information with a duplicate dish name.
    if (key !== 'basic' && dishNameTaken) {
      toast({
        title: t('addDish.validation.nameDuplicate', { name: dishForm.name.trim() }),
        variant: 'destructive',
      });
      setActiveTab('basic');
      return;
    }
    // Block leaving Basic Information for an add-on until a diet is picked.
    if (key !== 'basic' && addonDietMissing) {
      toast({
        title: t('addDish.validation.addonDietRequired'),
        description: 'Pick a diet (Veg / Non-Veg / Vegan) on Basic Information first.',
        variant: 'destructive',
      });
      setActiveTab('basic');
      return;
    }
    // Block leaving the Variants step while two variants share a name.
    if (activeTabKey === 'variants' && key !== 'variants' && duplicateVariantName) {
      toast({
        title: t('addDish.validation.variantDuplicate', { name: duplicateVariantName }),
        variant: 'destructive',
      });
      return;
    }
    setActiveTab(key);
  };

  // Step navigation for the footer: the visible tabs form an ordered wizard, so
  // the bottom bar advances "Go to <next tab>" until the last step, where it
  // becomes Save. requestTabChange still enforces the Basic-info gate.
  const currentTabIndex = visibleTabs.findIndex((tabDef) => tabDef.key === activeTabKey);
  const prevTab = currentTabIndex > 0 ? visibleTabs[currentTabIndex - 1] : null;
  const nextTab = currentTabIndex >= 0 && currentTabIndex < visibleTabs.length - 1
    ? visibleTabs[currentTabIndex + 1]
    : null;

  const comboSelectedDishes = isComboKind
    ? allDishes.filter((d) => selectedComboItemIds.includes(d.id))
    : [];
  const comboCalorieTotal = comboSelectedDishes.reduce((sum, d) => {
    const cals = Array.isArray(d.variants) && d.variants.length
      ? (parseFloat(d.variants[0].calories) || 0)
      : (parseFloat(d.calories) || 0);
    return sum + cals;
  }, 0);
  const comboAllergenSet = new Set();
  comboSelectedDishes.forEach((d) => {
    // Allergens are tagged directly on each dish (Big-8 master), not derived
    // from ingredients anymore.
    (d.allergen_tags || []).forEach((a) => comboAllergenSet.add(typeof a === 'string' ? a : a.name));
  });
  const comboTotalItems = comboSelectedDishes.length;

  const toggleComboItem = (id) => {
    setSelectedComboItemIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  /* Save as Draft parks half-built work. It is offered while an item is being
     created, and on a draft reopened to carry on with - never on an item that
     has already been saved properly, which has nothing to park: that one
     shows Cancel and Update only. */
  const canSaveDraft = !editingDish || !!dishForm.is_draft;

  /* The item's type, for the chip in the header. Only when EDITING, as a
     read-only reminder of the locked type (changing kind/dish_type would
     orphan variants, prices and menu links). New items pick the type on the
     previous screen (All Menu Items → Add Menu Item dropdown, or the per-tab
     Add buttons), so nothing is shown for them. It used to be a full-width
     banner under the title - a whole band of the page to say one word. */
  const kindChip = (() => {
    if (!editingDish) return null;
    const current =
      dishForm.kind === 'combo'
        ? 'combo'
        : dishForm.kind === 'addon'
          ? 'addon'
          : ['beverage', 'store_bought', 'bakery', 'liquor'].includes(dishForm.dish_type)
            ? dishForm.dish_type
            : 'dish';
    return {
      dish:         { label: t('addDish.typeSelector.dish.label'),         Icon: UtensilsCrossed, cls: 'border-orange-300 bg-orange-50 text-orange-900' },
      beverage:     { label: t('addDish.typeSelector.beverage.label'),     Icon: Coffee,          cls: 'border-sky-300 bg-sky-50 text-sky-900' },
      addon:        { label: t('addDish.typeSelector.addon.label'),        Icon: PlusCircle,      cls: 'border-emerald-300 bg-emerald-50 text-emerald-900' },
      combo:        { label: t('addDish.typeSelector.combo.label'),        Icon: Package,         cls: 'border-violet-300 bg-violet-50 text-violet-900' },
      store_bought: { label: t('addDish.typeSelector.store_bought.label'), Icon: ShoppingBag,     cls: 'border-amber-300 bg-amber-50 text-amber-900' },
      bakery:       { label: t('addDish.typeSelector.bakery.label'),       Icon: Croissant,       cls: 'border-orange-300 bg-orange-50 text-orange-900' },
      liquor:       { label: t('addDish.typeSelector.liquor.label'),       Icon: Wine,            cls: 'border-purple-300 bg-purple-50 text-purple-900' },
    }[current];
  })();
  const KindIcon = kindChip ? kindChip.Icon : null;

  return (
    <div ref={formRootRef} className="min-h-full bg-background p-6">
      {/* Full-bleed: the form takes the whole viewport width — no cap, no centering margins. */}
      <div ref={topRef} className="w-full min-w-[1400px]">
        {/* ONE band. Left: the back link, then the item's name as the title
            with an "Editing" marker (and "Draft" when it is one). Right: the
            type chip, the SKU, the Active switch and the buttons, every one of
            them button height. It used to stack the back link over a title
            over a button row over the Active switch, with the type in a
            full-width banner beneath - four bands of page before the first
            field, and the title said "Edit Dish" where the name should be. */}
        <div className="mb-5 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              type="button"
              onClick={handleCancelNavigation}
              aria-label={t('addDish.pageHeader.backButton')}
              title={t('addDish.pageHeader.backButton')}
              className="group inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
              <span>{t('addDish.pageHeader.backButton')}</span>
            </button>
            {/* Title + status hide on the type-chooser step — the chooser has its
                own heading, and "Active" is meaningless before a type is picked. */}
            {(editingDish || typeChosen) && (
              <>
                <span className="h-6 w-px shrink-0 bg-border" aria-hidden="true" />
                {/* The live name from the form is the title; "Edit Dish" only
                    until there is a name to show. */}
                <h1 className="font-display text-2xl font-bold tracking-tight text-foreground leading-tight truncate min-w-0">
                  {editingDish && dishForm.name?.trim()
                    ? dishForm.name.trim()
                    : editingDish
                      ? t('addDish.pageHeader.title.edit', { kind: kindNoun })
                      : t('addDish.pageHeader.title.add', { kind: kindNoun })}
                </h1>
                {editingDish && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                    <Pencil className="h-3 w-3" />
                    {t('addDish.pageHeader.mode.editing')}
                  </span>
                )}
                {editingDish && dishForm.is_draft && (
                  <span className="inline-flex shrink-0 items-center rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                    {t('addDish.pageHeader.draft')}
                  </span>
                )}
              </>
            )}
          </div>
          {(editingDish || typeChosen) && (
            <div className="flex shrink-0 items-center gap-2.5">
              {kindChip && (
                <span
                  className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-md border px-3 text-sm font-semibold ${kindChip.cls}`}
                  title={t('addDish.pageHeader.kindLocked')}
                >
                  <KindIcon className="h-4 w-4" />
                  {kindChip.label}
                </span>
              )}
              {dishForm.sku && !isLiquor && (
                <span className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md border border-border bg-card px-3 font-mono text-sm">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('addDish.pageHeader.sku')}</span>
                  <span className="font-semibold tabular-nums text-foreground">{dishForm.sku}</span>
                </span>
              )}
              <div className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md border border-border bg-card px-3 text-sm">
                <button
                  type="button"
                  role="switch"
                  aria-checked={dishForm.is_available}
                  onClick={() => setDishForm((p) => ({ ...p, is_available: !p.is_available }))}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    dishForm.is_available
                      ? 'border-success/30 bg-success'
                      : 'border-destructive/30 bg-destructive'
                  }`}
                  title={dishForm.is_available ? t('addDish.pageHeader.statusTitle.active') : t('addDish.pageHeader.statusTitle.inactive')}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      dishForm.is_available ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span className={dishForm.is_available ? 'text-success' : 'text-destructive'}>
                  {dishForm.is_available ? t('addDish.pageHeader.status.active') : t('addDish.pageHeader.status.inactive')}
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelNavigation}
                className="px-5"
              >
                {t('addDish.formActions.cancel')}
              </Button>
              {/* Save as Draft — parks incomplete work (only a name is required);
                  saved inactive and flagged is_draft. Not for a saved item. */}
              {canSaveDraft && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={(ev) => handleSubmitDish(ev, { asDraft: true })}
                  disabled={isLoading}
                  className="px-5 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900 hover:border-amber-400"
                >
                  {t('addDish.formActions.saveDraft')}
                </Button>
              )}
              <Button
                type="submit"
                form="dish-form"
                disabled={isLoading}
                className={`px-5 ${formAccent.btnPrimary}`}
              >
                {isLoading
                  ? t('addDish.formActions.saving')
                  : editingDish
                    ? t('addDish.formActions.update', { type: kindNoun })
                    : t('addDish.formActions.save', { type: kindNoun })}
              </Button>
            </div>
          )}
        </div>

        {formErrors.length > 0 && (
          <div ref={errorBannerRef} className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <div className="flex items-center gap-2 font-medium mb-1">
              <AlertTriangle className="h-4 w-4" />
              {t('addDish.validation.header')}
            </div>
            <ul className="list-disc list-inside space-y-0.5 ml-1">
              {formErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        <form id="dish-form" onSubmit={handleSubmitDish} className="space-y-6">
          {/* The item's type used to sit here as a full-width banner; it is
              now the chip in the header beside the SKU. */}
          {(editingDish || typeChosen) && (
          <>
          {/* Section tabs — keep the form on one short screen. The cards below
              each render only when their tab is active (activeTabKey === key). */}
          <div className="sticky top-0 z-20 -mx-1 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border">
            <div className="flex gap-1 overflow-x-auto px-1 py-2">
              {visibleTabs.map((tabDef) => {
                const on = activeTabKey === tabDef.key;
                // Tabs other than Basic stay locked until the dish name is
                // entered and at least one location is picked.
                const locked = tabDef.key !== 'basic' && basicIncomplete;
                return (
                  <button
                    key={tabDef.key}
                    type="button"
                    onClick={() => requestTabChange(tabDef.key)}
                    aria-current={on ? 'page' : undefined}
                    title={locked ? 'Enter the dish name and select at least one location first' : undefined}
                    className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      on
                        ? `${formAccent.btnPrimary} shadow-sm`
                        : locked
                          ? 'text-muted-foreground/50 hover:bg-accent/50'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    {tabDef.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Section cards. The former two-column grid is now a single stack;
              each card shows only on its tab, so columns are unnecessary. */}
          <div className="space-y-4">
            {activeTabKey === 'basic' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('addDish.basicInfo.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Top region — the form is very wide (min 1400px), so the image
                    sits in a fixed-width left column with the identity fields
                    (name, category, etc.) filling the space to its right instead
                    of leaving the area beside a small thumbnail empty. */}
                <div className="grid grid-cols-1 lg:grid-cols-[16rem_minmax(0,1fr)] gap-x-6 gap-y-4">
                  {/* LEFT — visual identity: image, then Diet beneath it. */}
                  <div className="space-y-4">
                    {/* Dish image — a tall card that fills the left column. */}
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Image</Label>
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        className="group relative h-44 w-full rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50 flex items-center justify-center overflow-hidden transition-colors cursor-pointer"
                        aria-label={imagePreview ? 'Change dish image' : 'Upload dish image'}
                      >
                        {imagePreview ? (
                          <>
                            <img src={imagePreview} alt="Dish" className="h-full w-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Upload className="h-6 w-6 text-white" />
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-col items-center gap-1.5 text-slate-400 group-hover:text-blue-500 transition-colors">
                            <ImagePlus className="h-9 w-9" />
                            <span className="text-[11px] font-medium">Click to upload</span>
                          </div>
                        )}
                      </button>
                      <input
                        id="image"
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => imageInputRef.current?.click()}
                        className="w-full h-8 text-xs border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 hover:border-blue-300"
                      >
                        <Upload className="h-3.5 w-3.5 mr-1.5" />
                        {imagePreview ? 'Change Image' : 'Choose Image'}
                      </Button>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[10px] text-slate-500 truncate" title={dishForm.image?.name || ''}>
                          {dishForm.image?.name || 'PNG, JPG up to 5MB'}
                        </p>
                        {imagePreview && (
                          <button
                            type="button"
                            onClick={handleRemoveImage}
                            className="inline-flex items-center gap-1 text-[10px] font-medium text-red-600 hover:text-red-700 shrink-0"
                          >
                            <X className="h-3 w-3" /> Remove
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Diet — Veg / Non-Veg / Vegan (food only; drinks use the
                        Alcoholic toggle in Additional Information). */}
                    {dishForm.dish_type !== 'beverage' && dishForm.dish_type !== 'liquor' && (
                      <div className="space-y-1.5">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                          Diet{isAddonKind && <span className="text-rose-500"> *</span>}
                        </Label>
                        <div className="flex flex-wrap gap-1.5">
                          {[
                            { value: 'veg', label: 'Veg', dot: 'bg-green-500', activeCls: 'border-green-400 bg-green-50 text-green-700' },
                            { value: 'nonveg', label: 'Non-Veg', dot: 'bg-rose-600', activeCls: 'border-rose-500 bg-rose-50 text-rose-800' },
                            { value: 'vegan', label: 'Vegan', dot: 'bg-emerald-800', activeCls: 'border-emerald-700 bg-emerald-50 text-emerald-900' },
                          ].map((opt) => {
                            // Add-ons must pick a diet deliberately: until they do,
                            // no pill is highlighted so the default Veg can't slip
                            // through unnoticed. Every other kind keeps the default.
                            const dietUnset = isAddonKind && !dishForm.diet_touched;
                            const current = dietUnset
                              ? null
                              : dishForm.is_vegan ? 'vegan' : dishForm.is_vegetarian ? 'veg' : 'nonveg';
                            const active = current === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                  if (opt.value === 'veg') setDishForm({ ...dishForm, is_vegetarian: true, is_vegan: false, diet_touched: true });
                                  else if (opt.value === 'nonveg') setDishForm({ ...dishForm, is_vegetarian: false, is_vegan: false, diet_touched: true });
                                  else setDishForm({ ...dishForm, is_vegetarian: true, is_vegan: true, diet_touched: true });
                                }}
                                aria-pressed={active}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                                  active
                                    ? opt.activeCls
                                    : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                                }`}
                              >
                                <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                        {isAddonKind && !dishForm.diet_touched && (
                          <p className="text-[11px] text-rose-600">Pick a diet for this add-on.</p>
                        )}
                      </div>
                    )}

                    {/* Non-Veg Type — appears right under Diet when Non-Veg is picked. */}
                    {!dishForm.is_vegetarian && !dishForm.is_vegan && dishForm.dish_type !== 'beverage' && dishForm.dish_type !== 'liquor' && (
                      <div className="space-y-1.5 rounded-md bg-rose-50/40 border border-rose-100 p-2">
                        <div className="flex items-center gap-2">
                          <Label className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">Non-Veg Type</Label>
                          <IconBtn
                            icon={Plus}
                            onClick={() => setMasterDialog({ kind: 'dishNonVeg', onCreated: loadNonVegMaster })}
                            title="Add new non-veg type" />
                          <IconBtn
                            icon={RefreshCw}
                            tone="subdued"
                            onClick={loadNonVegMaster}
                            title="Refresh non-veg types" />
                        </div>
                        <Select
                          // Bound to non_veg_type_ids[] — the array the lock logic
                          // (lockedNonVegNames) and the save handler both read. The
                          // legacy singular non_veg_type_id is mirrored for any older
                          // readers. Without this the picked protein never reaches the
                          // array, so it was neither locked into the ingredients nor
                          // saved, and editing showed an empty picker.
                          value={String(dishForm.non_veg_type_id || (dishForm.non_veg_type_ids || [])[0] || '')}
                          onValueChange={(value) => {
                            setDishForm((p) => ({
                              ...p,
                              non_veg_type_id: value,
                              non_veg_type_ids: value ? [value] : [],
                            }));
                            ensureNonVegIngredient(value);
                          }}
                        >
                          <SelectTrigger className="w-full h-8 text-xs bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {nonVegOptions.length === 0 ? (
                              <div className="px-3 py-2 text-xs text-slate-500">
                                No types yet — use the + button.
                              </div>
                            ) : (
                              nonVegOptions.map((nv) => (
                                <SelectItem key={nv.id} value={String(nv.id)}>
                                  {nv.name}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Spicy level — Less / Normal / Extra (food only; drinks
                        and combos carry no spice of their own). */}
                    {dishForm.dish_type !== 'beverage' && dishForm.dish_type !== 'liquor' && dishForm.kind !== 'combo' && (
                      <div className="space-y-1.5">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Spicy</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {[
                            { value: 'less',   label: 'Less',   flames: 1, flameColor: 'text-amber-500',  activeCls: 'border-amber-400 bg-amber-50 text-amber-800' },
                            { value: 'normal', label: 'Normal', flames: 2, flameColor: 'text-orange-500', activeCls: 'border-orange-400 bg-orange-50 text-orange-800' },
                            { value: 'extra',  label: 'Extra',  flames: 3, flameColor: 'text-red-600',    activeCls: 'border-red-500 bg-red-50 text-red-800' },
                          ].map((opt) => {
                            const active = dishForm.spicy_level === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() =>
                                  setDishForm((prev) => ({
                                    ...prev,
                                    spicy_level: prev.spicy_level === opt.value ? '' : opt.value,
                                    is_extra_spicy: opt.value === 'extra' && prev.spicy_level !== 'extra',
                                  }))
                                }
                                aria-pressed={active}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                                  active ? opt.activeCls : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                                }`}
                              >
                                <span className={`inline-flex items-center -space-x-1 ${opt.flameColor}`}>
                                  {Array.from({ length: opt.flames }).map((_, i) => (
                                    <Flame key={i} className="h-3 w-3" fill="currentColor" strokeWidth={1.5} />
                                  ))}
                                </span>
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RIGHT — identity fields fill the remaining width. */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 min-h-7">
                          <Label htmlFor="name" className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Name <span className="text-rose-500">*</span></Label>
                        </div>
                        <Input
                          id="name"
                          value={dishForm.name}
                          onChange={handleNameChange}
                          placeholder={t(`addDish.basicInfo.namePlaceholder.${kindNounKey}`)}
                          required
                          className={dishNameTaken ? 'border-rose-400 focus-visible:ring-rose-300' : undefined}
                        />
                        {dishNameTaken && (
                          <p className="text-[11px] text-rose-600">
                            {t('addDish.validation.nameDuplicate', { name: dishForm.name.trim() })}
                          </p>
                        )}
                      </div>
                      {/* SKU — liquor only. User-entered stock code, unique per
                          restaurant; left blank it is auto-assigned on save. The
                          read-only badge at the page header is hidden for liquor
                          so this editable field is the single source of truth. */}
                      {isLiquor && (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 min-h-7">
                            <Label htmlFor="sku" className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">SKU</Label>
                          </div>
                          <Input
                            id="sku"
                            value={dishForm.sku || ''}
                            onChange={(e) => setDishForm((p) => ({ ...p, sku: e.target.value.toUpperCase() }))}
                            placeholder="Auto-assigned if left blank"
                            className="font-mono"
                          />
                        </div>
                      )}
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 min-h-7">
                          <Label htmlFor="category_master_id" className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Category</Label>
                          <IconBtn
                            icon={Plus}
                            onClick={() => setMasterDialog({ kind: 'dishCategories', onCreated: loadCategoriesMaster })}
                            title="Add new category" />
                          <IconBtn
                            icon={RefreshCw}
                            tone="subdued"
                            onClick={loadCategoriesMaster}
                            title="Refresh categories" />
                        </div>
                        <Select
                          value={dishForm.category_master_id || '_none'}
                          onValueChange={(value) =>
                            setDishForm({
                              ...dishForm,
                              category_master_id: value === '_none' ? '' : value,
                              // Picking a different category invalidates the sub-category.
                              sub_category_master_id: '',
                            })
                          }
                        >
                          <SelectTrigger id="category_master_id">
                            <SelectValue placeholder="Select category (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">None</SelectItem>
                            {categoryOptions.map((c) => (
                              <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Sub-Category — filtered by the chosen category. Rows with
                          no category attached are always shown (cross-category). */}
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 min-h-7">
                          <Label htmlFor="sub_category_master_id" className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sub Category</Label>
                          <IconBtn
                            icon={Plus}
                            disabled={!dishForm.category_master_id}
                            onClick={() => setMasterDialog({
                              kind: 'dishSubCategories',
                              onCreated: loadSubCategoriesMaster,
                              categoryId: dishForm.category_master_id,
                              categoryOptions,
                            })}
                            title={dishForm.category_master_id
                              ? 'Add new sub-category'
                              : 'Pick a category first — sub-categories must belong to one'} />
                          <IconBtn
                            icon={RefreshCw}
                            tone="subdued"
                            onClick={loadSubCategoriesMaster}
                            title="Refresh sub-categories" />
                        </div>
                        <Select
                          value={dishForm.sub_category_master_id || '_none'}
                          onValueChange={(value) =>
                            setDishForm({ ...dishForm, sub_category_master_id: value === '_none' ? '' : value })
                          }
                          disabled={!dishForm.category_master_id}
                        >
                          <SelectTrigger
                            id="sub_category_master_id"
                            title={dishForm.category_master_id ? undefined : 'Pick a category first'}
                          >
                            <SelectValue placeholder={
                              dishForm.category_master_id
                                ? 'Select sub-category (optional)'
                                : 'Pick a category first'
                            } />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">None</SelectItem>
                            {(() => {
                              const catId = dishForm.category_master_id
                                ? parseInt(dishForm.category_master_id, 10)
                                : null;
                              if (!catId) {
                                return (
                                  <div className="px-3 py-2 text-xs text-slate-500">
                                    Select a category first.
                                  </div>
                                );
                              }
                              // Parentless sub-categories are global — they
                              // show under every category.
                              const filtered = subCategoryOptions.filter(
                                (s) => !s.category_id || s.category_id === catId,
                              );
                              if (filtered.length === 0) {
                                return (
                                  <div className="px-3 py-2 text-xs text-slate-500">
                                    No sub-categories under this category yet.
                                  </div>
                                );
                              }
                              return filtered.map((s) => (
                                <SelectItem key={s.id} value={String(s.id)}>
                                  {s.name}
                                </SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Liquor Type — lifted onto Basic Information so it can be
                          set before going to Variants (picking Beer reveals a
                          Serving Format column there). The rest of the liquor
                          cascade (Category / Sub-category) stays on Liquor Details,
                          filtered by this Type. */}
                      {isLiquor && (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 min-h-7">
                            <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Liquor Type <span className="text-rose-500">*</span></Label>
                            <IconBtn
                              icon={Plus}
                              onClick={() => setMasterDialog({ kind: 'dishLiquorType', onCreated: loadLiquorTypes })}
                              title="Add liquor type" />
                            <IconBtn
                              icon={RefreshCw}
                              tone="subdued"
                              onClick={loadLiquorTypes}
                              title="Refresh liquor types" />
                          </div>
                          <Select
                            value={dishForm.liquor_type_id || '_none'}
                            onValueChange={(v) => setDishForm((p) => ({ ...p, liquor_type_id: v === '_none' ? '' : v, liquor_category_id: '', liquor_sub_category_id: '' }))}
                          >
                            <SelectTrigger><SelectValue placeholder="Spirit, Beer, Wine…" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">None</SelectItem>
                              {liquorTypeOptions.map((o) => <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {/* Beer flow: Liquor Type → Source → Brand, stacked in order.
                              Source (brewed/bought) shows for Beer; Brand shows only when
                              bought-in. */}
                          {isBeer && (
                            <div className="space-y-1.5 pt-1">
                              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Source</Label>
                              <Select
                                value={dishForm.liquor_source || '_none'}
                                onValueChange={(v) => setDishForm((p) => ({ ...p, liquor_source: v === '_none' ? '' : v }))}
                              >
                                <SelectTrigger><SelectValue placeholder="Brewed or bought" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">None</SelectItem>
                                  <SelectItem value="brewed">Brewed in-house</SelectItem>
                                  <SelectItem value="bought">Bought-in</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          {isBeer && dishForm.liquor_source === 'bought' && (
                            <div className="space-y-1.5 pt-1">
                              <div className="flex items-center gap-2 min-h-7">
                                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Brand</Label>
                                <IconBtn icon={Plus} onClick={() => setMasterDialog({ kind: 'dishBrand', onCreated: loadBrands })} title="Add brand" />
                                <IconBtn icon={RefreshCw} tone="subdued" onClick={loadBrands} title="Refresh brands" />
                              </div>
                              <Select
                                value={dishForm.liquor_brand_id || '_none'}
                                onValueChange={(v) => setDishForm((p) => ({ ...p, liquor_brand_id: v === '_none' ? '' : v, brand_master_id: v === '_none' ? '' : v }))}
                              >
                                <SelectTrigger><SelectValue placeholder="Select brand" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">None</SelectItem>
                                  {brandOptions.map((b) => <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Variants toggle — sits directly under Category so the
                          dish's classification (category / sub-category) and its
                          variant choice read as one group. For add-ons it also
                          carries the "Sold As Separate Dish" tick. Hidden for combos. */}
                      {!isComboKind && (
                        <div className="space-y-1.5">
                          {/* Variants are not offered for add-ons — an add-on is a
                              single item. The toggle + label are hidden for add-ons;
                              only the "Sold As Separate Dish" tick remains below. */}
                          {!isAddonKind && (
                            <div className="flex items-center gap-2 min-h-7">
                              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                                Variants
                              </Label>
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                            {!isAddonKind && (
                              <button
                                type="button"
                                role="switch"
                                aria-checked={hasVariants}
                                aria-label="Enable variants"
                                onClick={() => {
                                  const next = !hasVariants;
                                  setHasVariants(next);
                                  // Turning variants OFF collapses the dish to a
                                  // single base variant, so Prices (and everything
                                  // else) stops showing the extra rows.
                                  if (!next) setVariants((prev) => (prev.length > 1 ? [prev[0]] : prev));
                                }}
                                className="inline-flex items-center gap-2 cursor-pointer select-none"
                              >
                                <span className={`text-[11px] font-medium ${hasVariants ? 'text-emerald-600' : 'text-red-600'}`}>{hasVariants ? 'On' : 'Off'}</span>
                                <span
                                  aria-hidden="true"
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                    hasVariants ? 'bg-emerald-500' : 'bg-red-500'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                      hasVariants ? 'translate-x-4' : 'translate-x-0.5'
                                    }`}
                                  />
                                </span>
                              </button>
                            )}

                            {/* Sold As Separate Dish — add-ons only. Bundled with a
                                parent dish by default; ticking lets it be ordered on
                                its own, revealing the Take Away / Delivery toggles
                                below (see showFulfilment). */}
                            {isAddonKind && (
                              <>
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={!!dishForm.sold_separately}
                                    onChange={(e) =>
                                      setDishForm((p) => ({ ...p, sold_separately: e.target.checked }))
                                    }
                                    className="h-3.5 w-3.5 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                                  />
                                  <span className="text-[11px] font-medium text-slate-700">Sold As Seperate Dish</span>
                                </label>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Beverage Type + milk cascade (beverages only). Sits right
                          below Category so the drink's classification reads together.
                          Picking a type flagged allows_milk (e.g. Coffee) reveals a
                          Milk Yes/No choice; choosing Yes reveals the milk-type picker. */}
                      {dishForm.dish_type === 'beverage' && (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 min-h-7">
                            <Label htmlFor="beverage_type_id" className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Beverage Type</Label>
                            <IconBtn
                              icon={Plus}
                              onClick={() => setMasterDialog({ kind: 'dishBeverageType', onCreated: loadBeverageTypes })}
                              title="Add new beverage type" />
                            <IconBtn
                              icon={RefreshCw}
                              tone="subdued"
                              onClick={loadBeverageTypes}
                              title="Refresh beverage types" />
                          </div>
                          <Select
                            value={dishForm.beverage_type_id || '_none'}
                            onValueChange={(value) => {
                              const id = value === '_none' ? '' : value;
                              const opt = beverageTypeOptions.find((b) => String(b.id) === String(id));
                              const allows = !!opt?.allows_milk;
                              setDishForm((prev) => ({
                                ...prev,
                                beverage_type_id: id,
                                // Drop the milk cascade if the new type can't take milk.
                                has_milk: allows ? prev.has_milk : false,
                                milk_type_id: allows && prev.has_milk ? prev.milk_type_id : '',
                              }));
                            }}
                          >
                            <SelectTrigger id="beverage_type_id">
                              <SelectValue placeholder="Select beverage type (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">None</SelectItem>
                              {beverageTypeOptions.map((b) => (
                                <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Milk Yes/No — only for beverage types that allow milk (e.g. Coffee). */}
                      {dishForm.dish_type === 'beverage' && beverageAllowsMilk && (
                        <div className="space-y-1.5">
                          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Milk</Label>
                          <div className="flex flex-wrap gap-1.5">
                            {[
                              { value: true,  label: '🥛 With Milk',    activeCls: 'border-amber-400 bg-amber-50 text-amber-800' },
                              { value: false, label: 'No Milk',          activeCls: 'border-slate-600 bg-slate-700 text-white' },
                            ].map((opt) => {
                              const active = !!dishForm.has_milk === opt.value;
                              return (
                                <button
                                  key={String(opt.value)}
                                  type="button"
                                  onClick={() => {
                                    setDishForm((prev) => ({
                                      ...prev,
                                      has_milk: opt.value,
                                      // Clearing milk also clears any chosen milk type.
                                      milk_type_id: opt.value ? prev.milk_type_id : '',
                                    }));
                                    // Turning milk ON for a fresh dish auto-builds one
                                    // variant per size × milk type. Only when pristine,
                                    // so a customized variant set is never clobbered.
                                    if (opt.value && milkTypeOptions.length && variantsArePristine()) {
                                      applyMilkVariants({ silent: true });
                                    }
                                  }}
                                  aria-pressed={active}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                                    active ? opt.activeCls : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Contains Egg — beverage-only flag (e.g. egg coffee,
                          egg-white cocktails). A simple Yes/No pill pair. */}
                      {dishForm.dish_type === 'beverage' && (
                        <div className="space-y-1.5">
                          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Contains Egg</Label>
                          <div className="flex flex-wrap gap-1.5">
                            {[
                              { value: true,  label: '🥚 Contains Egg', activeCls: 'border-amber-400 bg-amber-50 text-amber-800' },
                              { value: false, label: 'No Egg',          activeCls: 'border-slate-600 bg-slate-700 text-white' },
                            ].map((opt) => {
                              const active = !!dishForm.contains_egg === opt.value;
                              return (
                                <button
                                  key={String(opt.value)}
                                  type="button"
                                  onClick={() => setDishForm((prev) => ({ ...prev, contains_egg: opt.value }))}
                                  aria-pressed={active}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                                    active ? opt.activeCls : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Brand picker removed — a resale item's brand defaults to
                          none; the value still flows through on submit. */}
                    </div>

                    {/* Description + Locations side-by-side so the textarea doesn't
                        eat all the vertical space on its own. At least one
                        location is required — a dish must be offered somewhere. */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="description" className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Description</Label>
                        <Textarea
                          id="description"
                          value={dishForm.description}
                          onChange={(e) => setDishForm({ ...dishForm, description: e.target.value })}
                          rows={5}
                          placeholder="Describe the dish..."
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                          Locations <span className="text-rose-500">*</span>
                        </Label>
                        <LocationsPicker
                          locations={locations}
                          selectedIds={selectedLocationIds}
                          onChange={setSelectedLocationIds}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Take Away + Delivery toggles — a compact switch row in Basic
                    Information. Shown for every non-combo item; for add-ons they
                    appear only once "To be sold separately" is ticked, since a
                    bundle-only add-on isn't fulfilled on its own.
                    (The Variants toggle lives above, by the Locations row.) */}
                {showFulfilment && (
                  <div className="pt-2 border-t border-slate-100">
                    {/* Two side-by-side fulfilment sections: Take Away (left) and
                        Delivery (right). Each owns its toggle plus the fields that
                        only matter once that channel is allowed. */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* ── Take Away (left) ─────────────────────────────── */}
                      <div className="space-y-3 md:pr-6 md:border-r md:border-slate-100">
                        <div className="flex items-center gap-2">
                          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                            Take Away
                          </Label>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={dishForm.takeaway_allowed}
                            aria-label="Allow take-away"
                            onClick={() =>
                              setDishForm((prev) => ({ ...prev, takeaway_allowed: !prev.takeaway_allowed }))
                            }
                            className="inline-flex items-center gap-2 cursor-pointer select-none"
                          >
                            <span className={`text-[11px] font-medium ${dishForm.takeaway_allowed ? 'text-emerald-600' : 'text-red-600'}`}>
                              {dishForm.takeaway_allowed ? 'Allowed' : 'Off'}
                            </span>
                            <span
                              aria-hidden="true"
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                dishForm.takeaway_allowed ? 'bg-emerald-500' : 'bg-red-500'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                  dishForm.takeaway_allowed ? 'translate-x-4' : 'translate-x-0.5'
                                }`}
                              />
                            </span>
                          </button>
                        </div>

                        {dishForm.takeaway_allowed && (
                          <>
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-2">
                                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                                  Cutlery
                                </Label>
                                <IconBtn
                                  icon={Plus}
                                  onClick={() => setMasterDialog({ kind: 'dishParcelCutlery', onCreated: loadParcelCutlery })}
                                  title="Add new take-away cutlery"
                                />
                                <IconBtn
                                  icon={RefreshCw}
                                  tone="subdued"
                                  onClick={loadParcelCutlery}
                                  title="Refresh take-away cutlery"
                                />
                              </div>
                              <Select
                                value={
                                  (dishForm.takeaway_cutlery_ids || [])[0] != null
                                    ? String((dishForm.takeaway_cutlery_ids || [])[0])
                                    : '_none'
                                }
                                onValueChange={(value) =>
                                  setDishForm({
                                    ...dishForm,
                                    takeaway_cutlery_ids: value === '_none' ? [] : [Number(value)],
                                  })
                                }
                              >
                                <SelectTrigger id="takeaway_cutlery">
                                  <SelectValue placeholder="Select cutlery (optional)" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">None</SelectItem>
                                  {parcelCutleryOptions.map((c) => (
                                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
                                Instructions
                              </Label>
                              <Textarea
                                value={dishForm.takeaway_instructions}
                                onChange={(e) => setDishForm({ ...dishForm, takeaway_instructions: e.target.value })}
                                placeholder="e.g. Pack sauce separately…"
                                rows={2}
                                className="text-xs"
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* ── Delivery (right) ─────────────────────────────── */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                            Delivery
                          </Label>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={dishForm.delivery_allowed}
                            aria-label="Allow delivery"
                            onClick={() =>
                              setDishForm((prev) => ({ ...prev, delivery_allowed: !prev.delivery_allowed }))
                            }
                            className="inline-flex items-center gap-2 cursor-pointer select-none"
                          >
                            <span className={`text-[11px] font-medium ${dishForm.delivery_allowed ? 'text-emerald-600' : 'text-red-600'}`}>
                              {dishForm.delivery_allowed ? 'Allowed' : 'Off'}
                            </span>
                            <span
                              aria-hidden="true"
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                dishForm.delivery_allowed ? 'bg-emerald-500' : 'bg-red-500'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                  dishForm.delivery_allowed ? 'translate-x-4' : 'translate-x-0.5'
                                }`}
                              />
                            </span>
                          </button>
                        </div>

                        {dishForm.delivery_allowed && (
                          <>
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-2">
                                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                                  Cutlery
                                </Label>
                                <IconBtn
                                  icon={Plus}
                                  onClick={() => setMasterDialog({ kind: 'dishParcelCutlery', onCreated: loadParcelCutlery })}
                                  title="Add new cutlery"
                                />
                                <IconBtn
                                  icon={RefreshCw}
                                  tone="subdued"
                                  onClick={loadParcelCutlery}
                                  title="Refresh cutlery"
                                />
                              </div>
                              <Select
                                value={
                                  (dishForm.delivery_cutlery_ids || [])[0] != null
                                    ? String((dishForm.delivery_cutlery_ids || [])[0])
                                    : '_none'
                                }
                                onValueChange={(value) =>
                                  setDishForm({
                                    ...dishForm,
                                    delivery_cutlery_ids: value === '_none' ? [] : [Number(value)],
                                  })
                                }
                              >
                                <SelectTrigger id="delivery_cutlery">
                                  <SelectValue placeholder="Select cutlery (optional)" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">None</SelectItem>
                                  {parcelCutleryOptions.map((c) => (
                                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
                                Delivery Instructions
                              </Label>
                              <Textarea
                                value={dishForm.delivery_instructions}
                                onChange={(e) => setDishForm({ ...dishForm, delivery_instructions: e.target.value })}
                                placeholder="e.g. Seal lid, add cutlery, insulate hot items…"
                                rows={2}
                                className="text-xs"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Liquor Details — bottle size + brand/type/category identity + the
                catalogue metadata. Shown only for liquor; bottle size drives the
                peg/servings math in Bar Inventory. */}
            {isLiquor && activeTabKey === 'liquor' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Liquor Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Bottle size (ml) *</Label>
                      <Input type="number" min="0" step="any" value={dishForm.bottle_size_ml}
                        onChange={(e) => setDishForm((p) => ({ ...p, bottle_size_ml: e.target.value }))}
                        placeholder="e.g., 750" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">ABV (%)</Label>
                      <Input type="number" min="0" max="100" step="any" value={dishForm.abv}
                        onChange={(e) => setDishForm((p) => ({ ...p, abv: e.target.value }))} placeholder="e.g., 40" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 min-h-7">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Region</Label>
                        <IconBtn icon={Plus} onClick={() => setMasterDialog({ kind: 'dishLiquorRegion', onCreated: loadLiquorRegions })} title="Add region" />
                        <IconBtn icon={RefreshCw} tone="subdued" onClick={loadLiquorRegions} title="Refresh regions" />
                      </div>
                      <Select value={dishForm.liquor_region_id || '_none'}
                        onValueChange={(v) => setDishForm((p) => ({ ...p, liquor_region_id: v === '_none' ? '' : v }))}>
                        <SelectTrigger><SelectValue placeholder="Speyside, Napa…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">None</SelectItem>
                          {liquorRegionOptions.map((o) => <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 min-h-7">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Premium level</Label>
                        <IconBtn icon={Plus} onClick={() => setMasterDialog({ kind: 'dishLiquorPremiumLevel', onCreated: loadLiquorPremiums })} title="Add premium level" />
                        <IconBtn icon={RefreshCw} tone="subdued" onClick={loadLiquorPremiums} title="Refresh premium levels" />
                      </div>
                      <Select value={dishForm.premium_level_id || '_none'}
                        onValueChange={(v) => setDishForm((p) => ({ ...p, premium_level_id: v === '_none' ? '' : v }))}>
                        <SelectTrigger><SelectValue placeholder="House, Premium…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">None</SelectItem>
                          {liquorPremiumOptions.map((o) => <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Serve temperature</Label>
                      <Select value={dishForm.liquor_serve_temp || '_none'}
                        onValueChange={(v) => setDishForm((p) => ({ ...p, liquor_serve_temp: v === '_none' ? '' : v }))}>
                        <SelectTrigger><SelectValue placeholder="Chilled / Room / Frozen" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">None</SelectItem>
                          <SelectItem value="chilled">Chilled</SelectItem>
                          <SelectItem value="room">Room temperature</SelectItem>
                          <SelectItem value="frozen">Frozen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { f: 'country', label: 'Country', ph: 'Scotland, France…' },
                      { f: 'age_statement', label: 'Age', ph: 'e.g., 12Y' },
                      { f: 'display_name', label: 'Short name (POS)', ph: 'e.g., JW Black' },
                      { f: 'storage_location', label: 'Storage location', ph: 'Bar 1, Cellar…' },
                      { f: 'supplier', label: 'Supplier', ph: 'Distributor name' },
                      { f: 'hsn_code', label: 'HSN code', ph: 'Tax code' },
                      { f: 'pos_button_color', label: 'POS button colour', ph: 'e.g., #B91C1C' },
                      { f: 'liquor_allergen_notes', label: 'Allergen notes', ph: 'e.g., Contains sulphites' },
                    ].map(({ f, label, ph }) => (
                      <div key={f} className="space-y-1.5">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
                        <Input value={dishForm[f]} onChange={(e) => setDishForm((p) => ({ ...p, [f]: e.target.value }))} placeholder={ph} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Kitchen Operations — three FK dropdowns + a garnish multi-select.
                Hidden for combos (they bundle existing dishes and don't carry
                their own kitchen metadata). Hidden by default for add-ons
                unless the user opts in via the "Show advanced fields" toggle. */}
            {!isComboKind && (!isAddonKind || showAddonAdvanced) && activeTabKey === 'kitchen' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('addDish.kitchenOps.title')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Cooking Station */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                          Cooking Station
                        </Label>
                        <IconBtn
                          icon={Plus}
                          onClick={() => setMasterDialog({ kind: 'dishCookingStation', onCreated: loadCookingStations })}
                          title="Add new cooking station" />
                        <IconBtn
                          icon={RefreshCw}
                          tone="subdued"
                          onClick={loadCookingStations}
                          title="Refresh cooking stations" />
                      </div>
                      <div className="rounded-md border border-slate-200 bg-white p-2 space-y-1 max-h-[190px] overflow-y-auto">
                        {cookingStationOptions.length === 0 ? (
                          <div className="px-2 py-2 text-xs text-slate-500 text-center">None yet — add with <strong>+</strong>.</div>
                        ) : (
                          cookingStationOptions.map((cs) => {
                            const on = (dishForm.cooking_station_ids || []).includes(String(cs.id));
                            return (
                              <label key={cs.id} className={`flex items-center gap-2 px-2 py-1 rounded-md border cursor-pointer text-xs transition-colors ${on ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                                <input type="checkbox" checked={on} onChange={() => toggleKitchenOp('cooking_station_ids', String(cs.id))} className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate font-medium" title={cs.name}>{cs.name}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* Plating Note */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                          Plating Note
                        </Label>
                        <IconBtn
                          icon={Plus}
                          onClick={() => setMasterDialog({ kind: 'dishPlatingNote', onCreated: loadPlatingNotes })}
                          title="Add new plating note" />
                        <IconBtn
                          icon={RefreshCw}
                          tone="subdued"
                          onClick={loadPlatingNotes}
                          title="Refresh plating notes" />
                      </div>
                      <div className="rounded-md border border-slate-200 bg-white p-2 space-y-1 max-h-[190px] overflow-y-auto">
                        {platingNoteOptions.length === 0 ? (
                          <div className="px-2 py-2 text-xs text-slate-500 text-center">None yet — add with <strong>+</strong>.</div>
                        ) : (
                          platingNoteOptions.map((pn) => {
                            const on = (dishForm.plating_note_ids || []).includes(String(pn.id));
                            return (
                              <label key={pn.id} className={`flex items-center gap-2 px-2 py-1 rounded-md border cursor-pointer text-xs transition-colors ${on ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                                <input type="checkbox" checked={on} onChange={() => toggleKitchenOp('plating_note_ids', String(pn.id))} className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate font-medium" title={pn.name}>{pn.name}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* Serving Vessel */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                          Serving Vessel
                        </Label>
                        <IconBtn
                          icon={Plus}
                          onClick={() => setMasterDialog({ kind: 'dishServingVessel', onCreated: loadServingVessels })}
                          title="Add new serving vessel" />
                        <IconBtn
                          icon={RefreshCw}
                          tone="subdued"
                          onClick={loadServingVessels}
                          title="Refresh serving vessels" />
                      </div>
                      <div className="rounded-md border border-slate-200 bg-white p-2 space-y-1 max-h-[190px] overflow-y-auto">
                        {servingVesselOptions.length === 0 ? (
                          <div className="px-2 py-2 text-xs text-slate-500 text-center">None yet — add with <strong>+</strong>.</div>
                        ) : (
                          servingVesselOptions.map((sv) => {
                            const on = (dishForm.serving_vessel_ids || []).includes(String(sv.id));
                            return (
                              <label key={sv.id} className={`flex items-center gap-2 px-2 py-1 rounded-md border cursor-pointer text-xs transition-colors ${on ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                                <input type="checkbox" checked={on} onChange={() => toggleKitchenOp('serving_vessel_ids', String(sv.id))} className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate font-medium" title={sv.name}>{sv.name}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Garnishes — a garnish is just an ingredient used decoratively,
                      so it's picked from the ingredient master and lives with the
                      kitchen/plating metadata rather than in its own section. */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                        Garnishes
                      </Label>
                      <IconBtn
                        icon={Plus}
                        onClick={() => setMasterDialog({ kind: 'dishIngredients', onCreated: loadIngredients })}
                        title="Add new ingredient (used as garnish)" />
                      <IconBtn
                        icon={RefreshCw}
                        tone="subdued"
                        onClick={loadIngredients}
                        title="Refresh ingredients" />
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                      <Input
                        placeholder="Search garnishes…"
                        value={garnishSearch}
                        onChange={(e) => setGarnishSearch(e.target.value)}
                        className="h-8 text-xs w-full pl-8"
                      />
                    </div>
                    {selectedGarnishIds.length > 0 && (
                      <div className="rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1.5 flex flex-wrap items-center gap-1">
                        <span className="text-[10px] font-medium text-emerald-700">Tagged:</span>
                        {ingredients
                          .filter((i) => selectedGarnishIds.includes(i.id))
                          .map((i) => (
                            <span key={i.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-700">
                              {i.name}
                            </span>
                          ))}
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-[190px] overflow-y-auto pr-1">
                      {filteredGarnishes.length === 0 ? (
                        <div className="col-span-full px-3 py-3 text-xs text-slate-500 text-center border border-dashed rounded-md">
                          No ingredients to pick from. Add one with the <strong>+</strong> above.
                        </div>
                      ) : (
                        filteredGarnishes.map((ing) => {
                          const on = selectedGarnishIds.includes(ing.id);
                          return (
                            <label
                              key={ing.id}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded-md border cursor-pointer text-xs transition-colors ${
                                on
                                  ? 'border-emerald-300 bg-emerald-50'
                                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() => toggleGarnish(ing.id)}
                                className="h-3.5 w-3.5 shrink-0"
                              />
                              <span className="truncate font-medium" title={ing.name}>{ing.name}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Notes — free-form kitchen description (handling tips,
                      prep reminders, allergen call-outs) that doesn't fit
                      a master dropdown. */}
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="kitchen_notes"
                      className="text-[11px] font-semibold uppercase tracking-wider text-slate-600"
                    >
                      Notes
                    </Label>
                    <Textarea
                      id="kitchen_notes"
                      rows={3}
                      placeholder="Handling tips, prep reminders, allergen call-outs…"
                      value={dishForm.kitchen_notes}
                      onChange={(e) => setDishForm({ ...dishForm, kitchen_notes: e.target.value })}
                    />
                  </div>

                </CardContent>
              </Card>
            )}


            {/* Attributes — Style and Cooked In, on the Attributes tab
                (Spicy lives on Basic Information).
                Hidden for combos (a combo derives these from its members) and
                for drinks (beverages/liquor have no spice or plating style, so
                the card would be empty — serve temperature lives per variant). */}
            {!isComboKind && dishForm.dish_type !== 'beverage' && dishForm.dish_type !== 'liquor' && activeTabKey === 'additional' && (
            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle>{t('addDish.attributes.title')}</CardTitle>
              </CardHeader>
              {/* Groups flow into responsive columns so they fill the row
                  width instead of stacking down the left edge. */}
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                {/* Row: Style (food dishes and add-ons) */}
                {dishForm.dish_type === 'food' && dishForm.kind !== 'combo' && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Style</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { value: 'dry',        label: 'Dry',        Icon: Wheat,   iconColor: 'text-amber-600',  activeCls: 'border-amber-400 bg-amber-50 text-amber-800' },
                        { value: 'semi_gravy', label: 'Semi-Gravy', Icon: Droplet, iconColor: 'text-sky-600',    activeCls: 'border-sky-400 bg-sky-50 text-sky-800' },
                        { value: 'gravy',      label: 'Gravy',      Icon: Soup,    iconColor: 'text-indigo-600', activeCls: 'border-indigo-500 bg-indigo-50 text-indigo-800' },
                      ].map((opt) => {
                        const active = dishForm.gravy_style === opt.value;
                        const Icon = opt.Icon;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              setDishForm((prev) => ({
                                ...prev,
                                gravy_style: prev.gravy_style === opt.value ? '' : opt.value,
                              }))
                            }
                            aria-pressed={active}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                              active ? opt.activeCls : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                            }`}
                          >
                            <Icon
                              className={`h-3.5 w-3.5 ${opt.iconColor}`}
                              strokeWidth={2}
                              fill={opt.value === 'semi_gravy' ? 'currentColor' : 'none'}
                            />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Row: Cooked In (food dishes and add-ons) — Butter / Ghee / Oil */}
                {dishForm.dish_type === 'food' && dishForm.kind !== 'combo' && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Cooked In</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { value: 'butter', label: 'Butter', dot: 'bg-amber-400',  activeCls: 'border-amber-400 bg-amber-50 text-amber-800' },
                        { value: 'ghee',   label: 'Ghee',   dot: 'bg-yellow-500', activeCls: 'border-yellow-500 bg-yellow-50 text-yellow-800' },
                        { value: 'oil',    label: 'Oil',    dot: 'bg-lime-500',   activeCls: 'border-lime-500 bg-lime-50 text-lime-800' },
                      ].map((opt) => {
                        const active = dishForm.cooking_medium === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              setDishForm((prev) => ({
                                ...prev,
                                cooking_medium: prev.cooking_medium === opt.value ? '' : opt.value,
                              }))
                            }
                            aria-pressed={active}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                              active ? opt.activeCls : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                            }`}
                          >
                            <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Row: Cooking Method (food dishes and add-ons) — Fried / Baked / Steamed / Grilled / Sauteed */}
                {dishForm.dish_type === 'food' && dishForm.kind !== 'combo' && (
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Cooking Method</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { value: 'fried',   label: 'Fried',   dot: 'bg-orange-500', activeCls: 'border-orange-400 bg-orange-50 text-orange-800' },
                        { value: 'baked',   label: 'Baked',   dot: 'bg-amber-500',  activeCls: 'border-amber-400 bg-amber-50 text-amber-800' },
                        { value: 'steamed', label: 'Steamed', dot: 'bg-sky-500',    activeCls: 'border-sky-400 bg-sky-50 text-sky-800' },
                        { value: 'grilled', label: 'Grilled', dot: 'bg-rose-500',   activeCls: 'border-rose-400 bg-rose-50 text-rose-800' },
                        { value: 'sauteed', label: 'Sauteed', dot: 'bg-lime-500',   activeCls: 'border-lime-400 bg-lime-50 text-lime-800' },
                      ].map((opt) => {
                        const active = dishForm.cooking_method === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() =>
                              setDishForm((prev) => ({
                                ...prev,
                                cooking_method: prev.cooking_method === opt.value ? '' : opt.value,
                              }))
                            }
                            aria-pressed={active}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                              active ? opt.activeCls : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                            }`}
                          >
                            <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Diet (Veg / Non-Veg / Vegan) lives on the Basic Information tab. */}

                {/* Alcohol is set implicitly by item type (Liquor → alcoholic,
                    everything else → not); no manual Alcoholic / Non-Alcoholic
                    picker. Non-Veg Type lives with Diet on Basic Information. */}

                {/* Serve temperature is set per variant on the Variants tab —
                    no dish-level Serve control here. */}

              </CardContent>
            </Card>
            )}

            {/* Prep / Cook / Total — total auto-derived from prep + cook.
                Hidden for add-ons (a chutney/dip carries no meaningful cook time)
                and for combos. */}
            {!isComboKind && !isAddonKind && activeTabKey === 'additional' && (
            <Card>
              <CardHeader>
                <CardTitle>Timing</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-3">
                  {/* Prep time is not relevant for add-ons. */}
                  {!isAddonKind && (
                  <div className="space-y-1">
                    <Label htmlFor="prep_time" className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                      Prep Time (mins)
                    </Label>
                    <Input
                      id="prep_time"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={dishForm.prep_time}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
                        setDishForm({ ...dishForm, prep_time: digits });
                      }}
                      placeholder=""
                      className="w-24 h-8 text-xs"
                    />
                  </div>
                  )}
                  <div className="space-y-1">
                    <Label htmlFor="cook_time" className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                      Cook Time (mins)
                    </Label>
                    <Input
                      id="cook_time"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={dishForm.cook_time}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
                        setDishForm({ ...dishForm, cook_time: digits });
                      }}
                      placeholder=""
                      className="w-24 h-8 text-xs"
                    />
                  </div>
                  <div className="h-8 inline-flex items-center gap-1.5 px-3 rounded-md border border-blue-200 bg-blue-50">
                    <Timer className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-700">
                      Total
                    </span>
                    <span className="text-xs font-bold text-blue-900">
                      {((parseInt(dishForm.prep_time, 10) || 0) + (parseInt(dishForm.cook_time, 10) || 0))} mins
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {!isComboKind && activeTabKey === 'additional' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle>Tags</CardTitle>
                  <div className="flex items-center gap-1.5">
                    <IconBtn
                      icon={Plus}
                      onClick={() => setMasterDialog({ kind: 'dishTags', onCreated: loadTags })}
                      title="Add new tag" />
                    <IconBtn
                      icon={RefreshCw}
                      tone="subdued"
                      onClick={loadTags}
                      title="Refresh tags" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {tags.length === 0 ? (
                    <span className="text-xs text-slate-500">No tags yet — create them under Settings → Masters → Tags.</span>
                  ) : (
                    tags.map((tg) => {
                      const on = selectedTagIds.includes(tg.id);
                      return (
                        <button
                          key={tg.id}
                          type="button"
                          onClick={() => setSelectedTagIds((p) => p.includes(tg.id) ? p.filter((x) => x !== tg.id) : [...p, tg.id])}
                          className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                            on
                              ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                              : 'border-slate-300 bg-white text-slate-700 hover:border-primary/50 hover:bg-accent hover:text-accent-foreground'
                          }`}
                        >
                          {tg.name}
                        </button>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Custom Fields — hidden for combos; also hides for add-ons unless
                advanced fields are shown. Garnishes moved to the
                Recipe tab. */}
            {!isComboKind && activeTabKey === 'additional' && (!isAddonKind || showAddonAdvanced) && (
              <Card>
                <CardHeader>
                  <CardTitle>Custom Fields</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(dishForm.custom_fields || []).map((row, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-2"
                    >
                      <Input
                        placeholder={`Custom property ${idx + 1}`}
                        value={row.key}
                        maxLength={40}
                        onChange={(e) => {
                          const next = [...(dishForm.custom_fields || [])];
                          next[idx] = { ...next[idx], key: e.target.value };
                          setDishForm({ ...dishForm, custom_fields: next });
                        }}
                        className="text-xs"
                      />
                      <Input
                        placeholder={`Custom property ${idx + 1} value`}
                        value={row.value}
                        maxLength={120}
                        onChange={(e) => {
                          const next = [...(dishForm.custom_fields || [])];
                          next[idx] = { ...next[idx], value: e.target.value };
                          setDishForm({ ...dishForm, custom_fields: next });
                        }}
                        className="text-xs"
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

              {/* Garnishes moved to the Kitchen Instructions tab — a garnish is
                  just an ingredient, so it doesn't warrant its own section here. */}

              {/* Select Ingredients — the inventory-tracked ingredients (the ones
                  you measure / cost). Part of the Recipe tab.
                  Hidden for combos (they bundle dishes, not raw ingredients);
                  also hidden by default for add-ons. */}
              {/* Select Ingredients (left) + Allergens (right) sit side by side
                  on wide screens, stacking on narrow ones. */}
              {!isComboKind && activeTabKey === 'composition' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
              {(!isAddonKind || showAddonAdvanced) && (
                <Card className="overflow-hidden">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <CardTitle>Select Ingredients</CardTitle>
                      <div className="flex items-center gap-1.5">
                        <IconBtn
                          icon={Plus}
                          onClick={() => setMasterDialog({ kind: 'dishIngredients', trackInventory: true, onCreated: loadIngredients })}
                          title="Add new inventory-tracked ingredient" />
                        <IconBtn
                          icon={RefreshCw}
                          tone="subdued"
                          onClick={loadIngredients}
                          title="Refresh ingredients" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                      <Input
                        placeholder="Search ingredients…"
                        value={ingredientSearch}
                        onChange={(e) => setIngredientSearch(e.target.value)}
                        className="h-8 text-xs w-full pl-8"
                      />
                    </div>
                    {selectedIngredients.length > 0 && (
                      <div className="rounded-md bg-blue-50 border border-blue-200 px-2 py-1.5 flex flex-wrap items-center gap-1">
                        <span className="text-[10px] font-medium text-blue-700">Selected:</span>
                        {selectedIngredients.map((i) => (
                          <span key={i.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-blue-200 text-blue-700">
                            {i.name}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-1.5 max-h-[190px] overflow-y-auto pr-1">
                      {trackedIngredients.length === 0 ? (
                        <div className="col-span-full px-3 py-3 text-xs text-slate-500 text-center border border-dashed rounded-md">
                          No inventory-tracked ingredients. Turn on tracking for items in Settings → Ingredients.
                        </div>
                      ) : (
                        trackedIngredients.map((ing) => {
                          const on = selectedIngredientIds.includes(ing.id);
                          const locked = lockedIngredientIds.map(Number).includes(Number(ing.id));
                          const cardCls = locked
                            ? 'border-rose-300 bg-rose-50'
                            : on
                              ? 'border-primary bg-accent'
                              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50';
                          return (
                            <label
                              key={ing.id}
                              title={locked ? `${ing.name} — added by the Non-Veg type and locked here` : ing.name}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs transition-colors ${locked ? 'cursor-not-allowed' : 'cursor-pointer'} ${cardCls}`}
                            >
                              <input
                                type="checkbox"
                                checked={on}
                                disabled={locked}
                                onChange={() => toggleIngredient(ing.id)}
                                className="h-3.5 w-3.5 shrink-0"
                              />
                              <span className="truncate font-medium" title={ing.name}>
                                {ing.name}
                              </span>
                              {locked && <span className="ml-auto text-[9px] shrink-0 text-rose-500" title="Locked by Non-Veg type">🔒</span>}
                            </label>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>
                )}

                {/* Allergens — the Big-8 food allergens tagged directly on the
                    dish (independent of ingredients). Sits to the right of Select
                    Ingredients; spans the full width when the ingredients card is
                    hidden (add-ons without advanced fields). */}
                <Card className={`overflow-hidden ${(isAddonKind && !showAddonAdvanced) ? 'lg:col-span-2' : ''}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <CardTitle>Allergens</CardTitle>
                      <div className="flex items-center gap-1 flex-nowrap">
                        <IconBtn
                          icon={Plus}
                          onClick={() => setMasterDialog({ kind: 'dishAllergens', onCreated: loadAllergens })}
                          title="Add new allergen" />
                        <IconBtn
                          icon={RefreshCw}
                          tone="subdued"
                          onClick={loadAllergens}
                          title="Refresh allergens" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {allergenOptions.length === 0 ? (
                      <div className="px-3 py-3 text-xs text-slate-500 text-center border border-dashed rounded-md">
                        No allergens in the master.
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {allergenOptions.map((a) => {
                          const on = selectedAllergenIds.includes(a.id);
                          return (
                            <button
                              key={a.id}
                              type="button"
                              aria-pressed={on}
                              onClick={() =>
                                setSelectedAllergenIds((prev) =>
                                  prev.includes(a.id)
                                    ? prev.filter((x) => x !== a.id)
                                    : [...prev, a.id],
                                )
                              }
                              className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${
                                on
                                  ? 'border-rose-500 bg-rose-100 text-rose-800 font-semibold'
                                  : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700'
                              }`}
                            >
                              {on && <span aria-hidden className="mr-1">⚠</span>}
                              {a.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              )}

                {/* Other Ingredients — every ingredient not flagged for inventory
                    tracking. Same picker, feeds the same selectedIngredientIds;
                    kept below allergens so the tracked/measured items lead. */}
                {!isComboKind && (!isAddonKind || showAddonAdvanced) && activeTabKey === 'composition' && (
                <Card className="overflow-hidden">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <CardTitle>Other Ingredients</CardTitle>
                      <div className="flex items-center gap-1.5">
                        <IconBtn
                          icon={RefreshCw}
                          tone="subdued"
                          onClick={loadIngredients}
                          title="Refresh ingredients" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                      <Input
                        placeholder="Search ingredients…"
                        value={ingredientSearch}
                        onChange={(e) => setIngredientSearch(e.target.value)}
                        className="h-8 text-xs w-full pl-8"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-1.5 max-h-[190px] overflow-y-auto pr-1">
                      {otherIngredients.length === 0 ? (
                        <div className="col-span-full px-3 py-3 text-xs text-slate-500 text-center border border-dashed rounded-md">
                          No ingredients yet — use the <strong>+</strong> above to add.
                        </div>
                      ) : (
                        otherIngredients.map((ing) => {
                          const on = selectedIngredientIds.includes(ing.id);
                          const locked = lockedIngredientIds.map(Number).includes(Number(ing.id));
                          const cardCls = locked
                            ? 'border-rose-300 bg-rose-50'
                            : on
                              ? 'border-primary bg-accent'
                              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50';
                          return (
                            <label
                              key={ing.id}
                              title={locked ? `${ing.name} — added by the Non-Veg type and locked here` : ing.name}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs transition-colors ${locked ? 'cursor-not-allowed' : 'cursor-pointer'} ${cardCls}`}
                            >
                              <input
                                type="checkbox"
                                checked={on}
                                disabled={locked}
                                onChange={() => toggleIngredient(ing.id)}
                                className="h-3.5 w-3.5 shrink-0"
                              />
                              <span className="truncate font-medium" title={ing.name}>
                                {ing.name}
                              </span>
                              {locked && <span className="ml-auto text-[9px] shrink-0 text-rose-500" title="Locked by Non-Veg type">🔒</span>}
                            </label>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>
                )}

          </div>

          {/* Per-variant Complimentary Items live in a modal opened from each
              variant row — see the Variants table action column below. */}

          {/* Variants — full-width card sitting just above the Price matrix so
              both share the same column width (variants drive every matrix).
              Shown for add-ons too so their sizes can be set here; add-ons carry
              no price, so there is no FREE / PAID column for them. */}
          {!isComboKind && activeTabKey === 'variants' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle>{t('addDish.variants.title')}</CardTitle>
                  <div className="flex items-center gap-2">
                    {dishForm.dish_type === 'beverage' && beverageAllowsMilk && dishForm.has_milk && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => applyMilkVariants()}
                        title="Replace rows with one per size × milk type"
                      >
                        <RefreshCw className="h-4 w-4 mr-1.5" />
                        Milk variants
                      </Button>
                    )}
                    {isBeer && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => applyBeerVariants()}
                        title="Load a row for every beer serving format (Pint, Bottle, Glass, Pitcher, Tower)"
                      >
                        <RefreshCw className="h-4 w-4 mr-1.5" />
                        Load serving sizes
                      </Button>
                    )}
                    <Button type="button" size="sm" onClick={addVariantRow} className={formAccent.btnPrimary}>
                      <Plus className="h-4 w-4 mr-1.5" />
                      Add Row
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-x-auto overflow-y-auto max-h-[560px]">
                  <Table className="w-full">
                    <TableHeader className="bg-gray-50 sticky top-0 z-10">
                      <TableRow>
                        <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-32 min-w-[112px]">
                          <div className="flex items-center gap-1 flex-nowrap">
                            <span>SIZE</span>
                            <IconBtn
                              icon={Plus}
                              onClick={() => setMasterDialog({ kind: 'dishSizes', onCreated: loadSizes })}
                              title="Add new size" />
                            <IconBtn
                              icon={RefreshCw}
                              tone="subdued"
                              onClick={loadSizes}
                              title="Refresh sizes" />
                          </div>
                        </TableHead>
                        {isMilkBeverage ? (
                          <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-36">MILK TYPE</TableHead>
                        ) : isLiquor ? null : (
                          <>
                            <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-36">
                              <div className="flex items-center gap-1 flex-nowrap">
                                <span>ADD ON 1</span>
                                <IconBtn
                                  icon={Plus}
                                  onClick={() => setAddonQuickDlg({ name: '', price: '', diet: '' })}
                                  title="Create a new add-on" />
                                <IconBtn
                                  icon={RefreshCw}
                                  tone="subdued"
                                  onClick={loadAllDishes}
                                  title="Refresh add-ons" />
                              </div>
                            </TableHead>
                            <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-36">
                              <div className="flex items-center gap-1 flex-nowrap">
                                <span>ADD ON 2</span>
                                <IconBtn
                                  icon={Plus}
                                  onClick={() => setAddonQuickDlg({ name: '', price: '', diet: '' })}
                                  title="Create a new add-on" />
                                <IconBtn
                                  icon={RefreshCw}
                                  tone="subdued"
                                  onClick={loadAllDishes}
                                  title="Refresh add-ons" />
                              </div>
                            </TableHead>
                          </>
                        )}
                        {showDietColumn && (
                          <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-24">DIET</TableHead>
                        )}
                        {dishForm.dish_type === 'beverage' && (
                          <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-32 min-w-[120px]">SERVE</TableHead>
                        )}
                        {isMilkBeverage && (
                          <>
                            <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-20">SHOTS</TableHead>
                            <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-28">ICE</TableHead>
                            <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-28">SUGAR</TableHead>
                            <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-32">
                              <div className="flex items-center gap-1 flex-nowrap">
                                <span>ROAST</span>
                                <IconBtn
                                  icon={Plus}
                                  onClick={() => setMasterDialog({ kind: 'coffeeRoasts', onCreated: loadCoffeeRoasts })}
                                  title="Add new roast" />
                                <IconBtn
                                  icon={RefreshCw}
                                  tone="subdued"
                                  onClick={loadCoffeeRoasts}
                                  title="Refresh roasts" />
                              </div>
                            </TableHead>
                          </>
                        )}
                        {isBeer && (
                          <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-36">SERVING FORMAT</TableHead>
                        )}
                        {isLiquor && (
                          <>
                            <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-36">
                              <div className="flex items-center gap-1 flex-nowrap">
                                <span>SERVING SIZE</span>
                                <IconBtn
                                  icon={Plus}
                                  onClick={() => setMasterDialog({ kind: 'dishLiquorServingSize', onCreated: loadLiquorServingSizes })}
                                  title="Add new serving size" />
                                <IconBtn
                                  icon={RefreshCw}
                                  tone="subdued"
                                  onClick={loadLiquorServingSizes}
                                  title="Refresh serving sizes" />
                              </div>
                            </TableHead>
                            {/* Beer sells by serving format (Pint/Bottle/…), not the
                                spirit "full bottle" flag — hide the column for beer. */}
                            {!isBeer && (
                              <TableHead className="text-center px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-24">FULL BOTTLE</TableHead>
                            )}
                          </>
                        )}
                        <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap">DISPLAY NAME <span className="text-rose-500">*</span></TableHead>
                        {/* Allergens · per variant — inline column for dishes and
                            beverages (additive to the dish-level Allergens card on
                            the Recipe tab). Hidden for liquor (a
                            bought-in bottle) and combos. */}
                        {!isLiquor && !isComboKind && (
                          <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-44 min-w-[180px]">
                            <span title="Inherited automatically from the chosen add-ons">
                              ALLERGENS <span className="font-normal text-gray-400 normal-case">(from add-ons)</span>
                            </span>
                          </TableHead>
                        )}
                        {!isBeverage && !isLiquor && (
                          <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-20">SERVES</TableHead>
                        )}
                        {!isBeverage && !isLiquor && (
                          <TableHead className="text-left px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-24">CALORIES</TableHead>
                        )}
                        {/* Per-variant Active/Inactive — a single variant can be 86'd
                            (e.g. only the Large is out) while the dish stays available. */}
                        <TableHead className="text-center px-3 py-2.5 text-xs font-semibold text-gray-700 align-middle whitespace-nowrap w-24">ACTIVE</TableHead>
                        <TableHead className="px-2 py-2.5 w-10" aria-label="Remove row" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {variants && variants.length > 0 && variants.map((variant, index) => {
                        const variantNameKey = (variant.variant_name || '').trim().toLowerCase();
                        const isDupVariantName = !!variantNameKey && variantNameKeyCounts.get(variantNameKey) > 1;
                        const availableSizes = getAvailableSizes(index);
                        const currentSize = sizeOptions.find((o) => o.value === variant.size)
                          || (variant.size ? { value: variant.size, label: getSizeLabel(variant.size) } : null);
                        const sizeOptionsWithSelf = currentSize && !availableSizes.find((o) => o.value === currentSize.value)
                          ? [currentSize, ...availableSizes]
                          : availableSizes;
                        return (
                          <TableRow key={index} className="border-t align-middle">
                            <TableCell className="px-3 py-2 align-middle">
                              <Select
                                value={variant.size || 'none'}
                                onValueChange={(value) => updateVariant(index, 'size', value === 'none' ? '' : value)}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select…">
                                    {variant.size ? getSizeLabel(variant.size) : 'Select…'}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">— None —</SelectItem>
                                  {sizeOptionsWithSelf.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            {isMilkBeverage ? (
                              <TableCell className="px-3 py-2 align-middle">
                                {(() => {
                                  // Milk name rides in variant_option. Keep any
                                  // value not in the master list selectable so an
                                  // older/custom milk doesn't vanish from the row.
                                  const current = (variant.variant_option || '').trim();
                                  const hasCurrent = current
                                    && !milkTypeOptions.some((m) => m.name === current);
                                  return (
                                    <Select
                                      value={current}
                                      onValueChange={(value) => updateVariant(index, 'variant_option', value)}
                                    >
                                      <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select milk" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {hasCurrent && (
                                          <SelectItem value={current}>{current}</SelectItem>
                                        )}
                                        {milkTypeOptions.map((m) => (
                                          <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  );
                                })()}
                              </TableCell>
                            ) : isLiquor ? null : (
                              // Two add-on dimensions: pick an add-on item for each.
                              // The chosen names ride in variant_option / _2 and
                              // compose into the display name. Either may be None.
                              <>
                                {['variant_option', 'variant_option_2'].map((field) => {
                                  const current = (variant[field] || '').trim();
                                  const missing = current && !addonChoices.some((d) => d.name === current);
                                  // The other add-on dimension's pick — exclude it
                                  // here so the same add-on can't be chosen twice on
                                  // one variant (its own current value stays listed).
                                  const otherField = field === 'variant_option' ? 'variant_option_2' : 'variant_option';
                                  const otherPick = (variant[otherField] || '').trim();
                                  const options = addonChoices.filter(
                                    (d) => d.name === current || d.name !== otherPick,
                                  );
                                  return (
                                    <TableCell key={field} className="px-3 py-2 align-middle">
                                      <Select
                                        value={current || 'none'}
                                        onValueChange={(value) => updateVariant(index, field, value === 'none' ? '' : value)}
                                      >
                                        <SelectTrigger className="w-full">
                                          <SelectValue placeholder="Select add-on" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="none">— None —</SelectItem>
                                          {/* Keep a deleted/renamed add-on visible so it isn't silently dropped. */}
                                          {missing && <SelectItem value={current}>{current}</SelectItem>}
                                          {options.map((d) => {
                                            const dm = dietMeta(dietOf(d));
                                            return (
                                              <SelectItem key={d.id} value={d.name}>
                                                <span className="inline-flex items-center gap-1.5">
                                                  <span className={`h-2 w-2 rounded-full shrink-0 ${dm.dot}`} title={`${dm.label} add-on`} />
                                                  {d.name}
                                                </span>
                                              </SelectItem>
                                            );
                                          })}
                                        </SelectContent>
                                      </Select>
                                    </TableCell>
                                  );
                                })}
                              </>
                            )}
                            {showDietColumn && (() => {
                              const vd = dietMeta(variantDietFor(variant));
                              const hasAddons = (variant.addons || []).length > 0
                                || !!(variant.variant_option || '').trim()
                                || !!(variant.variant_option_2 || '').trim();
                              return (
                                <TableCell className="px-3 py-2 align-middle">
                                  <span
                                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs ${vd.badge}`}
                                    title={
                                      dishBaseDiet === 'nonveg'
                                        ? 'Non-Veg dish — every variant is Non-Veg.'
                                        : hasAddons
                                          ? 'Derived from the dish Diet and the add-ons on this variant.'
                                          : 'Set by the dish Diet. Add a non-veg add-on to make this variant Non-Veg.'
                                    }
                                  >
                                    <span className={`h-2 w-2 rounded-full ${vd.dot}`} />
                                    {vd.label}
                                  </span>
                                </TableCell>
                              );
                            })()}
                            {dishForm.dish_type === 'beverage' && (
                              <TableCell className="px-3 py-2 align-middle">
                                <div className="flex flex-col items-stretch gap-1">
                                  {SERVE_OPTIONS.map((opt) => {
                                    const active = (variant.serve_temperature || '') === opt.value;
                                    return (
                                      <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => updateVariant(index, 'serve_temperature', active ? '' : opt.value)}
                                        aria-pressed={active}
                                        className={`w-full text-center whitespace-nowrap px-1.5 py-0.5 rounded-full border text-[10px] leading-tight transition-colors ${
                                          active ? opt.activeCls : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                                        }`}
                                      >
                                        {opt.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </TableCell>
                            )}
                            {isMilkBeverage && (
                              <>
                                {/* SHOTS — number of espresso shots (fixed per variant). */}
                                <TableCell className="px-3 py-2 align-middle">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={variant.shots ?? ''}
                                    onChange={(e) => updateVariant(index, 'shots', e.target.value)}
                                    placeholder="0"
                                    className="w-full"
                                  />
                                </TableCell>
                                {/* ICE — only meaningful when the variant is served cold. */}
                                <TableCell className="px-3 py-2 align-middle">
                                  {servesCold(variant) ? (
                                    <Select
                                      value={variant.ice_level || ''}
                                      onValueChange={(value) => updateVariant(index, 'ice_level', value)}
                                    >
                                      <SelectTrigger className="w-full"><SelectValue placeholder="Ice" /></SelectTrigger>
                                      <SelectContent>
                                        {ICE_OPTIONS.map((o) => (
                                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <span className="text-xs text-muted-foreground" title="Ice applies to cold drinks only">—</span>
                                  )}
                                </TableCell>
                                {/* SUGAR — sweetness level (fixed per variant). */}
                                <TableCell className="px-3 py-2 align-middle">
                                  <Select
                                    value={variant.sugar_level || ''}
                                    onValueChange={(value) => updateVariant(index, 'sugar_level', value)}
                                  >
                                    <SelectTrigger className="w-full"><SelectValue placeholder="Sugar" /></SelectTrigger>
                                    <SelectContent>
                                      {SUGAR_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                                {/* ROAST — master-backed (Light/Medium/Dark…). Keep any
                                    saved value not in the list selectable so a renamed/
                                    removed roast doesn't vanish from the row. */}
                                <TableCell className="px-3 py-2 align-middle">
                                  {(() => {
                                    const current = (variant.roast || '').trim();
                                    const missing = current && !roastOptions.some((r) => r.name === current);
                                    return (
                                      <Select
                                        value={current || 'none'}
                                        onValueChange={(value) => updateVariant(index, 'roast', value === 'none' ? '' : value)}
                                      >
                                        <SelectTrigger className="w-full"><SelectValue placeholder="Roast" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="none">— None —</SelectItem>
                                          {missing && <SelectItem value={current}>{current}</SelectItem>}
                                          {roastOptions.map((r) => (
                                            <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    );
                                  })()}
                                </TableCell>
                              </>
                            )}
                            {isBeer && (
                              <TableCell className="px-3 py-2 align-middle">
                                {/* Beer serving format (Pint / Bottle / Glass / Pitcher /
                                    Tower). Stored in the nutrition blob (no migration). */}
                                <Select
                                  value={variant.beer_format || '_none'}
                                  onValueChange={(v) => updateVariant(index, 'beer_format', v === '_none' ? '' : v)}
                                >
                                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Pint / Bottle…" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="_none">None</SelectItem>
                                    {BEER_FORMATS.map((f) => (
                                      <SelectItem key={f} value={f}>{f}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                            )}
                            {isLiquor && (
                              <>
                                <TableCell className="px-3 py-2 align-middle">
                                  {/* Serving-size preset — picking one sets the variant's
                                      poured ml (variant.quantity), which drives Bar Inventory
                                      deduction. A Full Bottle uses the bottle size instead. */}
                                  <Select
                                    value={variant.serving_size_id || '_none'}
                                    onValueChange={(v) => {
                                      const id = v === '_none' ? '' : v;
                                      const opt = liquorServingSizeOptions.find((o) => String(o.id) === String(id));
                                      updateVariant(index, 'serving_size_id', id);
                                      // Seed the poured ml from the preset's default_ml.
                                      if (opt && opt.default_ml != null && opt.default_ml !== '') {
                                        updateVariant(index, 'quantity', String(opt.default_ml));
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Peg / shot…" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="_none">None</SelectItem>
                                      {liquorServingSizeOptions.map((o) => (
                                        <SelectItem key={o.id} value={String(o.id)}>
                                          {o.name}{o.default_ml ? ` · ${o.default_ml}ml` : ''}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                                {/* No "full bottle" flag for beer — it sells by serving format. */}
                                {!isBeer && (
                                  <TableCell className="px-3 py-2 align-middle text-center">
                                    {/* Full bottle: sells/deducts a whole bottle (bottle size ml). */}
                                    <input
                                      type="checkbox"
                                      checked={!!variant.is_full_bottle}
                                      onChange={(e) => {
                                        const on = e.target.checked;
                                        updateVariant(index, 'is_full_bottle', on);
                                        if (on && dishForm.bottle_size_ml) {
                                          updateVariant(index, 'quantity', String(dishForm.bottle_size_ml));
                                          updateVariant(index, 'serving_size_id', '');
                                        }
                                      }}
                                      className="h-4 w-4 accent-purple-600"
                                    />
                                  </TableCell>
                                )}
                              </>
                            )}
                            <TableCell className="px-3 py-2 align-middle min-w-[160px]">
                              {/* Display name is auto-composed (Size · Add-on 1 ·
                                  Add-on 2) and read-only. Rows whose stored name is
                                  custom (e.g. "Tortilla" seeded server-side) show
                                  that name instead — it's what customers see when
                                  ordering. It must be unique within the dish; a
                                  clash is resolved by changing the size or add-ons. */}
                              {(() => {
                                const isCustomName = variant.variant_name_source === 'custom';
                                // Editable in every row. Non-custom rows show the
                                // auto-composed name (Size · Add-on 1 · Add-on 2);
                                // typing anything switches the row to custom and
                                // keeps what you entered.
                                const shownName = isCustomName
                                  ? (variant.variant_name || '')
                                  : (variant.variant_name || composeVariantName(variant));
                                return (
                              <div className="space-y-1">
                                <Input
                                  value={shownName}
                                  onChange={(e) => updateVariant(index, 'variant_name', e.target.value)}
                                  placeholder="Display name (auto — edit to customise)"
                                  className={`w-full text-sm ${isDupVariantName ? 'border-rose-400 bg-rose-50 text-rose-700' : ''}`}
                                />
                                {isDupVariantName && (
                                  <p className="text-[11px] text-rose-600">Duplicate — change the name, size or add-ons to make it unique.</p>
                                )}
                              </div>
                                );
                              })()}
                            </TableCell>
                            {/* Allergens · per variant — READ-ONLY. Inherited from
                                the add-ons chosen on this row (an add-on's allergens
                                flow through automatically); the user doesn't pick
                                them here. variant.allergens[] = [ingredientId, …]. */}
                            {!isLiquor && !isComboKind && (
                            <TableCell className="px-3 py-2 align-middle">
                              {(() => {
                                const ids = (Array.isArray(variant.allergens) ? variant.allergens : []).map(Number);
                                const names = ids
                                  .map((id) => allergenOptions.find((a) => Number(a.id) === id)?.name)
                                  .filter(Boolean);
                                if (names.length === 0) {
                                  return <span className="text-xs text-muted-foreground">—</span>;
                                }
                                return (
                                  <div className="flex flex-wrap gap-1">
                                    {names.map((nm) => (
                                      <span key={nm} className="inline-block text-[11px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                                        {nm}
                                      </span>
                                    ))}
                                  </div>
                                );
                              })()}
                            </TableCell>
                            )}
                            {!isBeverage && !isLiquor && (
                            <TableCell className="px-3 py-2 align-middle">
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                value={variant.serves}
                                onChange={(e) => updateVariant(index, 'serves', e.target.value)}
                                placeholder="1"
                                className="w-20"
                              />
                            </TableCell>
                            )}
                            {!isBeverage && !isLiquor && (
                            <TableCell className="px-3 py-2 align-middle">
                              <Input
                                type="number"
                                value={variant.calories}
                                onChange={(e) => updateVariant(index, 'calories', e.target.value)}
                                placeholder="Kcal"
                                className="w-full"
                              />
                            </TableCell>
                            )}
                            {/* Active/Inactive for this variant. Independent of the
                                dish-level toggle — round-trips via variant.is_available. */}
                            <TableCell className="px-3 py-2 align-middle text-center w-24">
                              {(() => {
                                const on = variant.is_available ?? true;
                                return (
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={on}
                                    onClick={() => updateVariant(index, 'is_available', !on)}
                                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                                      on
                                        ? 'border-success/30 bg-success'
                                        : 'border-destructive/30 bg-destructive'
                                    }`}
                                    title={on ? 'Active — available to order' : 'Inactive — hidden from ordering'}
                                    aria-label="Variant active"
                                  >
                                    <span
                                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                        on ? 'translate-x-5' : 'translate-x-0.5'
                                      }`}
                                    />
                                  </button>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="px-2 py-2 align-middle w-10">
                              <button
                                type="button"
                                onClick={() => removeVariantRow(index)}
                                disabled={variants.length === 1}
                                title={variants.length === 1 ? 'At least one variant is required' : 'Remove this row'}
                                aria-label="Remove variant row"
                                className={`p-1 rounded ${
                                  variants.length === 1
                                    ? 'text-slate-300 cursor-not-allowed'
                                    : 'text-slate-400 hover:text-rose-600'
                                }`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per-variant allergens now live in an inline ALLERGENS column in the
              variant table above (for both dishes and beverages); the standalone
              "Allergens per Variant" matrix was removed in favour of it. */}

          {/* Price · variant × location matrix — variants are columns,
              selected locations are rows, each cell holds three prices
              (Normal / Take-Away / Delivery). Falls back to a hint when
              no locations are picked. */}
          {!isComboKind && activeTabKey === 'prices' && (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <CardTitle className="min-w-0">{t('addDish.priceMatrix.title')}</CardTitle>
                  <div className="flex items-center gap-2 flex-wrap shrink-0">
                    {activeLocations.length > 1 && (
                      <label
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer select-none transition-colors ${
                          sameForAll
                            ? 'border-blue-400 bg-blue-50 text-blue-800'
                            : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50/40'
                        }`}
                        title="Enter prices once — every branch gets the same values. Uncheck to price each location separately."
                      >
                        <input
                          type="checkbox"
                          checked={sameForAll}
                          onChange={(e) => enableSameForAll(e.target.checked)}
                          className="h-4 w-4 accent-blue-600 cursor-pointer"
                          aria-label="Same price for all locations"
                        />
                        <span className={`text-xs font-semibold ${sameForAll ? 'text-blue-800' : 'text-slate-700'}`}>
                          Same price for all locations
                        </span>
                      </label>
                    )}
                    {/* Seasonal (day-of pricing) doesn't apply to add-ons — and
                        would zero their standalone price — so hide it for them. */}
                    {!isAddonKind && (
                    <label
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer select-none transition-colors ${
                        allSeasonal
                          ? 'border-amber-400 bg-amber-50 text-amber-800'
                          : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-amber-300 hover:bg-amber-50/40'
                      }`}
                      title={allSeasonal
                        ? 'Seasonal — every price is locked. Uncheck to enter prices.'
                        : 'Mark this dish as Seasonal — locks every price cell across variants & locations.'}
                    >
                      <input
                        type="checkbox"
                        checked={allSeasonal}
                        onChange={(e) => setAllSeasonal(e.target.checked)}
                        className="h-4 w-4 accent-amber-600 cursor-pointer"
                        aria-label="Seasonal"
                      />
                      <span className={`text-xs font-semibold ${allSeasonal ? 'text-amber-800' : 'text-slate-700'}`}>
                        Seasonal
                      </span>
                      {allSeasonal && (
                        <span className="text-[10px] font-mono text-amber-700">prices locked</span>
                      )}
                    </label>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {/* Dish-level tax — compact inline control above the matrix.
                    Pick a single Tax Label (e.g. VAT) or a composite Tax Group;
                    it applies to every variant. */}
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 font-mono">
                    Tax
                  </span>
                  <Select
                    value={dishForm.tax_type || 'none'}
                    onValueChange={(v) =>
                      setDishForm({
                        ...dishForm,
                        tax_type: v === 'none' ? '' : v,
                        tax_id: '',
                      })
                    }
                  >
                    <SelectTrigger className="h-8 w-36 text-sm">
                      <SelectValue placeholder="No tax" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No tax</SelectItem>
                      <SelectItem value="label">Tax Label</SelectItem>
                      <SelectItem value="group">Tax Group</SelectItem>
                    </SelectContent>
                  </Select>
                  {dishForm.tax_type && (
                    <Select
                      value={dishForm.tax_id ? String(dishForm.tax_id) : ''}
                      onValueChange={(v) => setDishForm({ ...dishForm, tax_id: v })}
                    >
                      <SelectTrigger className="h-8 w-52 text-sm">
                        <SelectValue placeholder={
                          dishForm.tax_type === 'group' ? 'Pick a tax group' : 'Pick a tax label'
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {(dishForm.tax_type === 'group' ? taxGroups : taxLabels)
                          .filter((t) => t.is_active !== false)
                          .map((t) => {
                            const rate = dishForm.tax_type === 'group'
                              ? t.effective_rate
                              : t.current_rate;
                            return (
                              <SelectItem key={t.id} value={String(t.id)}>
                                {t.name}
                                {rate != null ? ` — ${Number(rate).toFixed(2)}%` : ''}
                              </SelectItem>
                            );
                          })}
                        {((dishForm.tax_type === 'group' ? taxGroups : taxLabels).length === 0) && (
                          <div className="px-2 py-1.5 text-xs text-slate-500 italic">
                            No {dishForm.tax_type === 'group' ? 'tax groups' : 'tax labels'} defined yet.
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  )}
                  {!dishForm.tax_type && (
                    <span className="text-[11px] text-slate-500">{t('addDish.tax.defaultNote')}</span>
                  )}
                </div>
                {/* Which end of the tax sum the typed prices are. Asked only once
                    a tax is picked above; with none, the default tax is applied
                    at checkout and the editor keeps its hands off the figures. */}
                {dishTaxRows.length > 0 && (
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                      {[[true, t('addDish.tax.incl')], [false, t('addDish.tax.excl')]].map(([v, label]) => (
                        <button
                          key={label}
                          type="button"
                          aria-pressed={!!dishForm.price_includes_tax === v}
                          onClick={() => setDishForm({ ...dishForm, price_includes_tax: v })}
                          className={`rounded-md px-3 py-1 text-xs font-medium transition ${
                            !!dishForm.price_includes_tax === v
                              ? 'bg-slate-800 text-white'
                              : 'text-slate-500 hover:bg-slate-100'}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <span className="text-[11px] text-slate-500">
                      {dishForm.price_includes_tax
                        ? t('addDish.tax.inclNote', { taxes: taxNames(dishTaxRows) })
                        : t('addDish.tax.exclNote', { taxes: taxNames(dishTaxRows) })}
                    </span>
                  </div>
                )}
                <PriceMatrix
                  variants={hasVariants || isComboKind || isAddonKind ? variants : variants.slice(0, 1)}
                  setVariants={setVariants}
                  kind={dishForm.kind}
                  locations={locations}
                  selectedLocationIds={selectedLocationIds}
                  sizeOptions={sizeOptions}
                  unitOptions={unitOptions}
                  sameForAll={sameForAll}
                  takeawayAllowed={dishForm.takeaway_allowed && showFulfilment}
                  deliveryAllowed={dishForm.delivery_allowed && showFulfilment}
                  taxRows={dishTaxRows}
                  taxInclusive={!!dishForm.price_includes_tax}
                />
              </CardContent>
            </Card>
          )}

          {/* Recipe · variant matrix — one screen where variants are columns,
              ingredients are rows. Replaces the modal-per-variant flow with
              an inline spreadsheet you can scan across to compare quantities.
              Hidden by default for add-ons. */}
          {!isComboKind && (!isAddonKind || showAddonAdvanced) && activeTabKey === 'kitchen' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('addDish.recipeMatrix.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <RecipeMatrix
                  variants={variants}
                  setVariants={setVariants}
                  ingredientsMaster={ingredients}
                  setSelectedIngredientIds={setSelectedIngredientIds}
                  unitOptions={unitOptions}
                  sizeOptions={sizeOptions}
                />
              </CardContent>
            </Card>
          )}

          {/* Nutritional Info — per-variant nutrition + the Health Meter.
              Beverages get the drink parameter set; dishes/add-ons the food set.
              Hidden for combos (they aggregate from their member items). */}
          {!isComboKind && activeTabKey === 'nutrition' && (
            <NutritionPanel
              variants={variants}
              setVariants={setVariants}
              dishType={dishForm.dish_type}
              sizeOptions={sizeOptions}
            />
          )}

          {/* Combo Builder — shown only when kind === 'combo'. Sits above the
              Complimentary card so users define the combo composition first,
              then layer free add-ons on top. */}
          {isComboKind && activeTabKey === 'complimentary' && (
            <Card className="border-primary/30">
              <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-4 w-4" /> {t('addDish.comboBuilder.title')}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Price input */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">
                      Combo Price <span className="text-rose-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={comboPrice}
                      onChange={(e) => setComboPrice(e.target.value)}
                      placeholder="e.g. 12.50"
                      className="mt-1"
                    />
                  </div>
                  <div className="rounded-md border border-primary/20 bg-accent/40 p-3">
                    <div className="text-[11px] uppercase tracking-wider text-accent-foreground font-semibold font-mono mb-2">
                      Live Summary
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-muted-foreground text-xs">Items in combo</div>
                        <div className="font-semibold text-foreground tabular">{comboTotalItems}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs flex items-center gap-1">
                          <Flame className="h-3 w-3 text-primary" /> Total calories
                        </div>
                        <div className="font-semibold text-foreground tabular">
                          {comboCalorieTotal > 0 ? `${comboCalorieTotal} kcal` : '—'}
                        </div>
                      </div>
                    </div>
                    {comboAllergenSet.size > 0 && (
                      <div className="mt-3 pt-3 border-t border-primary/20">
                        <div className="text-[11px] font-medium text-destructive mb-1">Allergens in combo:</div>
                        <div className="flex flex-wrap gap-1">
                          {[...comboAllergenSet].map((a) => (
                            <span key={a} className="text-[10px] px-1.5 py-0.5 rounded-full bg-card border border-destructive/30 text-destructive">
                              {a}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 3 pickers for combo composition */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <RecommendationPicker
                    title="Dishes"
                    emptyHint="No dishes available."
                    items={allDishes.filter((d) => d.kind !== 'addon' && d.dish_type !== 'beverage' && d.id !== editingDish?.id)}
                    selectedIds={selectedComboItemIds}
                    onChange={(ids) => setSelectedComboItemIds(ids)}
                  />
                  <RecommendationPicker
                    title="Beverages"
                    emptyHint="No beverages available."
                    items={allDishes.filter((d) => d.kind !== 'addon' && d.dish_type === 'beverage' && d.id !== editingDish?.id)}
                    selectedIds={selectedComboItemIds}
                    onChange={(ids) => setSelectedComboItemIds(ids)}
                  />
                  <RecommendationPicker
                    title="Add-ons"
                    emptyHint="No add-ons available."
                    items={allDishes.filter((d) => d.kind === 'addon' && d.id !== editingDish?.id)}
                    selectedIds={selectedComboItemIds}
                    onChange={(ids) => setSelectedComboItemIds(ids)}
                  />
                </div>

                {/* Selected items chip list */}
                {comboSelectedDishes.length > 0 && (
                  <div className="rounded-md border border-border bg-card p-3">
                    <div className="text-xs font-semibold text-muted-foreground mb-2">In this combo:</div>
                    <div className="flex flex-wrap gap-1.5">
                      {comboSelectedDishes.map((d) => (
                        <span
                          key={d.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-accent border border-primary/20 text-xs text-accent-foreground"
                        >
                          {d.name}
                          <button
                            type="button"
                            onClick={() => toggleComboItem(d.id)}
                            className="hover:text-destructive"
                            title="Remove from combo"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Complimentary picker — four category pickers (Dishes / Beverages /
              Add-ons / Combos). For dishes/beverages/add-ons we render the
              per-variant matrix so each variant can bundle different items;
              for combos we render the same matrix in `flat` mode (no variant
              column) since a combo has only one synthesized base form. Hidden
              by default for add-ons (an add-on rarely bundles its own freebies). */}
          {(!isAddonKind || showAddonAdvanced) && activeTabKey === 'complimentary' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('addDish.complimentary.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ComplimentaryMatrix
                  variants={variants}
                  setVariants={setVariants}
                  allDishes={allDishes}
                  editingDishId={editingDish?.id}
                  flat={isComboKind}
                  sizeOptions={sizeOptions}
                  unitOptions={unitOptions}
                />
              </CardContent>
            </Card>
          )}

          {/* Add On Items — only for full Dishes (not Add-ons themselves). The
              Add-on matrix prices each add-on per variant; the recommendation
              pickers below suggest related Dishes / Beverages / Combos. */}
          {dishForm.kind === 'dish' && activeTabKey === 'recommendations' && (
            <>
            <Card>
              <CardHeader>
                <CardTitle>Add-ons &amp; Pricing</CardTitle>
              </CardHeader>
              <CardContent>
                <AddonMatrix
                  variants={variants}
                  setVariants={setVariants}
                  addonDishes={allDishes.filter((d) => d.kind === 'addon' && d.id !== editingDish?.id)}
                  dishBaseDiet={dishBaseDiet}
                  sizeOptions={sizeOptions}
                  unitOptions={unitOptions}
                  restaurantId={restaurantId}
                  locationIds={selectedLocationIds}
                  onCreated={loadAllDishes}
                  toast={toast}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recommended pairings</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <RecommendationPicker
                  title="Dishes"
                  emptyHint="No other dishes yet."
                  items={allDishes.filter((d) => d.kind === 'dish' && d.dish_type !== 'beverage' && d.id !== editingDish?.id)}
                  selectedIds={selectedSideIds}
                  onChange={setSelectedSideIds}
                />
                <RecommendationPicker
                  title="Beverages"
                  emptyHint="No beverages yet."
                  items={allDishes.filter((d) => d.kind === 'dish' && d.dish_type === 'beverage' && d.id !== editingDish?.id)}
                  selectedIds={selectedBeverageIds}
                  onChange={setSelectedBeverageIds}
                />
                <RecommendationPicker
                  title="Combos"
                  emptyHint="No combos yet."
                  items={allDishes.filter((d) => d.kind === 'combo' && d.id !== editingDish?.id)}
                  selectedIds={selectedRecommendedComboIds}
                  onChange={setSelectedRecommendedComboIds}
                />
              </CardContent>
            </Card>
            </>
          )}

          <div className="flex items-center justify-between gap-3">
            <div>
              {prevTab && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => requestTabChange(prevTab.key)}
                  className="px-5"
                >
                  <ChevronLeft className="h-4 w-4 mr-1.5" />
                  Back to {prevTab.label}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Save as Draft — reachable from any tab; parks incomplete work.
                  Same rule as the header: not for an item already saved. */}
              {canSaveDraft && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={(ev) => handleSubmitDish(ev, { asDraft: true })}
                  disabled={isLoading}
                  className="px-5 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900 hover:border-amber-400"
                >
                  {t('addDish.formActions.saveDraft')}
                </Button>
              )}
              {nextTab ? (
                <Button
                  type="button"
                  onClick={() => requestTabChange(nextTab.key)}
                  className={`px-6 ${formAccent.btnPrimary}`}
                >
                  Go to {nextTab.label}
                  <ChevronRight className="h-4 w-4 ml-1.5" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isLoading}
                  className={`px-6 ${formAccent.btnPrimary}`}
                >
                  {isLoading
                    ? t('addDish.formActions.saving')
                    : editingDish
                      ? t('addDish.formActions.update', { type: kindNoun })
                      : t('addDish.formActions.save', { type: kindNoun })}
                </Button>
              )}
            </div>
          </div>
          </>
          )}
        </form>
      </div>

      <MasterCreateDialog
        spec={masterDialog}
        restaurantId={restaurantId}
        onClose={() => setMasterDialog(null)}
      />

      {/* Quick-create add-on — launched from the Variants tab's Add On columns.
          Creates a minimal add-on (name + optional seed price) that inherits this
          dish's locations, then refreshes the catalogue so it appears in the Add
          On 1 / Add On 2 pickers. The full add-on can be fleshed out later from
          All Dishes. */}
      <Dialog open={!!addonQuickDlg} onOpenChange={(o) => !o && setAddonQuickDlg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New add-on</DialogTitle>
          </DialogHeader>
          {addonQuickDlg && (
            <div className="space-y-4 py-1">
              <div className="space-y-1.5">
                <Label htmlFor="quick-addon-name" className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                  Name <span className="text-rose-500">*</span>
                </Label>
                <Input
                  id="quick-addon-name"
                  autoFocus
                  value={addonQuickDlg.name}
                  onChange={(e) => setAddonQuickDlg((p) => ({ ...p, name: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitQuickAddon(); } }}
                  placeholder="e.g. Extra Cheese"
                />
              </div>
              {/* Diet — Veg / Non-Veg / Vegan. Required: an add-on's diet drives
                  the effective diet of every variant it's attached to. Mirrors
                  the AddonMatrix create dialog. */}
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                  Diet <span className="text-rose-500">*</span>
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { value: 'veg', label: 'Veg', dot: 'bg-green-500', activeCls: 'border-green-400 bg-green-50 text-green-700' },
                    { value: 'nonveg', label: 'Non-Veg', dot: 'bg-rose-600', activeCls: 'border-rose-500 bg-rose-50 text-rose-800' },
                    { value: 'vegan', label: 'Vegan', dot: 'bg-emerald-800', activeCls: 'border-emerald-700 bg-emerald-50 text-emerald-900' },
                  ].map((opt) => {
                    const active = addonQuickDlg.diet === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAddonQuickDlg((p) => ({ ...p, diet: opt.value }))}
                        aria-pressed={active}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                          active
                            ? opt.activeCls
                            : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                        }`}
                      >
                        <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {!addonQuickDlg.diet && (
                  <p className="text-[11px] text-rose-600">Pick a diet for this add-on.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="quick-addon-price" className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                  Price (optional)
                </Label>
                <Input
                  id="quick-addon-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={addonQuickDlg.price}
                  onChange={(e) => setAddonQuickDlg((p) => ({ ...p, price: e.target.value }))}
                  placeholder="0.00"
                  className="font-mono"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddonQuickDlg(null)}>Cancel</Button>
            <Button type="button" onClick={submitQuickAddon} disabled={addonQuickSaving}>
              {addonQuickSaving ? 'Creating…' : 'Create add-on'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Map the dialog's frontend-facing kind to the backend's master_type slug.
const MASTER_KIND_TO_TYPE = {
  dishSizes:           'size',
  dishUnits:           'unit',
  dishTags:            'tag',
  dishCategories:      'category',
  dishSubCategories:   'sub_category',
  dishNonVeg:          'non_veg',
  dishBeverageType:    'beverage_type',
  dishMilkType:        'milk_type',
  coffeeRoasts:        'coffee_roast',
  dishAllergens:       'allergen',
  dishIngredients:     'ingredient',
  dishCookingStation:  'cooking_station',
  dishPlatingNote:     'plating_note',
  dishServingVessel:   'serving_vessel',
  dishParcelCutlery:   'parcel_cutlery',
  dishBrand:           'brand',
  dishLiquorType:      'liquor_type',
  dishLiquorRegion:    'liquor_region',
  dishLiquorPremiumLevel: 'liquor_premium_level',
  dishLiquorServingSize: 'liquor_serving_size',
};
const MASTER_DIALOG_LABELS = {
  dishSizes:           { title: 'Add Size',            cta: 'Create Size'            },
  dishUnits:           { title: 'Add Unit',            cta: 'Create Unit'            },
  dishTags:            { title: 'Add Tag',             cta: 'Create Tag'             },
  dishCategories:      { title: 'Add Category',        cta: 'Create Category'        },
  dishSubCategories:   { title: 'Add Sub Category',    cta: 'Create Sub Category'    },
  dishNonVeg:          { title: 'Add Non-Veg Type',    cta: 'Create Non-Veg Type'    },
  dishBeverageType:    { title: 'Add Beverage Type',   cta: 'Create Beverage Type'   },
  dishMilkType:        { title: 'Add Milk Type',       cta: 'Create Milk Type'       },
  coffeeRoasts:        { title: 'Add Coffee Roast',    cta: 'Create Coffee Roast'    },
  dishAllergens:       { title: 'Add Allergen',        cta: 'Create Allergen'        },
  dishIngredients:     { title: 'Add Ingredient',      cta: 'Create Ingredient'      },
  dishCookingStation:  { title: 'Add Cooking Station', cta: 'Create Cooking Station' },
  dishPlatingNote:     { title: 'Add Plating Note',    cta: 'Create Plating Note'    },
  dishServingVessel:   { title: 'Add Serving Vessel',  cta: 'Create Serving Vessel'  },
  dishParcelCutlery:   { title: 'Add Take-Away Cutlery',  cta: 'Create Take-Away Cutlery'  },
  dishBrand:           { title: 'Add Brand',             cta: 'Create Brand'             },
  dishLiquorType:      { title: 'Add Liquor Type',       cta: 'Create Liquor Type'       },
  dishLiquorRegion:    { title: 'Add Region',            cta: 'Create Region'            },
  dishLiquorPremiumLevel: { title: 'Add Premium Level',  cta: 'Create Premium Level'     },
  dishLiquorServingSize: { title: 'Add Serving Size',    cta: 'Create Serving Size'      },
};

function MasterCreateDialog({ spec, restaurantId, onClose }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', description: '', category_id: '', allows_milk: false, base_unit: '', default_ml: '' });
  const [saving, setSaving] = useState(false);

  const masterType = MASTER_KIND_TO_TYPE[spec?.kind];
  const isIngredient = masterType === 'ingredient';
  // Allergens are ingredients flagged is_allergen — created via the same
  // per-restaurant endpoint, not a standalone master.
  const isAllergen = masterType === 'allergen';
  const isSubCategory = masterType === 'sub_category';
  // Beverage types carry an allows_milk flag (drives the Milk question).
  const isBeverageType = masterType === 'beverage_type';
  // Liquor serving sizes carry a poured default_ml that seeds the variant's ML.
  const isServingSize = masterType === 'liquor_serving_size';
  // Inventory-tracked ingredients need a stock unit (kg, litre, units) so
  // purchases and consumption are comparable — shown only in that case.
  const isTrackedIngredient = isIngredient && !!spec?.trackInventory;

  useEffect(() => {
    if (spec) {
      setForm({
        name: '',
        description: '',
        // Pre-select whatever category the parent form has chosen; the
        // parent is optional — without one the sub-category shows under
        // all categories.
        category_id: spec.categoryId ? String(spec.categoryId) : '',
        allows_milk: false,
        base_unit: '',
        default_ml: '',
      });
    }
  }, [spec]);

  if (!spec) return null;
  const labels = MASTER_DIALOG_LABELS[spec.kind] || { title: 'Add', cta: 'Create' };
  const categoryOptions = spec.categoryOptions || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    if (!masterType) {
      toast({ title: 'Internal error', description: `Unknown master kind '${spec.kind}'`, variant: 'destructive' });
      return;
    }
    if ((isIngredient || isAllergen) && !restaurantId) {
      toast({ title: 'No restaurant selected', description: 'Cannot create an ingredient without restaurant context.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (isAllergen) {
        // An allergen is just an ingredient flagged is_allergen.
        await createIngredient(restaurantId, {
          name: form.name.trim(),
          description: form.description.trim() || '',
          is_allergen: true,
        });
      } else if (isIngredient) {
        // Dedicated per-restaurant ingredients endpoint.
        await createIngredient(restaurantId, {
          name: form.name.trim(),
          description: form.description.trim() || '',
          // When the dialog is opened from the inventory-tracked picker, flag the
          // new ingredient so it lands in "Select Ingredients" rather than "Other",
          // and carry the stock unit (kg, litre, units) inventory needs.
          ...(isTrackedIngredient ? { track_inventory: true, base_unit: form.base_unit.trim() } : {}),
        });
      } else {
        await dishMastersService.create(masterType, {
          name: form.name.trim(),
          description: form.description.trim() || '',
          // Sub-categories may carry a parent category_id; without one they
          // appear under all categories. Other masters ignore the field.
          ...(isSubCategory && form.category_id && form.category_id !== 'none'
            ? { category_id: Number(form.category_id) }
            : {}),
          // Beverage types carry the allows_milk flag.
          ...(isBeverageType ? { allows_milk: !!form.allows_milk } : {}),
          // Liquor serving sizes carry the poured default_ml (applies to any drink).
          ...(isServingSize ? { default_ml: form.default_ml === '' ? 0 : Number(form.default_ml), applies_to: 'any' } : {}),
        });
      }
      toast({ title: `${labels.title.replace('Add ', '')} created` });
      await spec.onCreated?.();
      // Cross-tab fan-out: tell every other RestoBoxx tab to re-fetch this
      // master so their picker picks up the new row in real time.
      try {
        if (typeof BroadcastChannel !== 'undefined') {
          const ch = new BroadcastChannel('restoboxx-masters');
          ch.postMessage({ kind: spec.kind, at: Date.now() });
          ch.close();
        }
      } catch { /* ignore — broadcast is best-effort */ }
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Could not create';
      toast({ title: 'Create failed', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!spec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="master-name">Name *</Label>
            <Input
              id="master-name"
              autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={`Enter ${labels.title.replace('Add ', '').toLowerCase()} name`}
            />
          </div>
          {isServingSize && (
            <div className="space-y-1.5">
              <Label htmlFor="master-default-ml">Default poured (ml)</Label>
              <Input
                id="master-default-ml"
                type="number"
                min="0"
                step="1"
                value={form.default_ml}
                onChange={(e) => setForm({ ...form, default_ml: e.target.value })}
                placeholder="e.g. 30 for a peg, 568 for a pint"
              />
            </div>
          )}
          {isTrackedIngredient && (
            <div className="space-y-1.5">
              <Label htmlFor="master-unit">Stock Unit</Label>
              <Input
                id="master-unit"
                value={form.base_unit}
                onChange={(e) => setForm({ ...form, base_unit: e.target.value })}
                placeholder="e.g., kg, litre, units"
              />
            </div>
          )}
          {isSubCategory && (
            <div className="space-y-1.5">
              <Label htmlFor="master-category">Category</Label>
              <Select
                value={form.category_id || 'none'}
                onValueChange={(value) => setForm({ ...form, category_id: value })}
              >
                <SelectTrigger id="master-category">
                  <SelectValue placeholder="No parent — shows under all categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent — show under all categories</SelectItem>
                  {categoryOptions.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="master-desc">Description</Label>
            <Textarea
              id="master-desc"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional"
            />
          </div>
          {isBeverageType && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!form.allows_milk}
                onChange={(e) => setForm({ ...form, allows_milk: e.target.checked })}
              />
              Offers a milk choice (like Coffee) — shows the Milk question on the beverage form
            </label>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Creating…' : labels.cta}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Bespoke picker for the Locations slot. Layout: a search input with a small
// tri-state select-all checkbox tucked to its right; below, each branch is a
// toggleable pill — selected ones highlighted, unselected ones subdued.
// Price · variant × location matrix — variants are columns, selected
// locations are rows. Each cell holds three prices (Normal, Take-Away,
// Delivery) so the same dish can scale per branch and per channel. Falls back
// to a friendly hint when no locations are picked yet, so the matrix doesn't
// silently collapse to zero rows.
function PriceMatrix({ variants, setVariants, kind, locations, selectedLocationIds, sizeOptions = [], unitOptions = [], sameForAll = false, takeawayAllowed = true, deliveryAllowed = true, taxRows = [], taxInclusive = false }) {
  const { t } = useT();
  const baseIdx = 0;
  // size/unit are stored as master codes (e.g. 'full_dcc1'); show their labels.
  const sizeLabel = (c) => sizeOptions.find((o) => o.value === c)?.label || c;
  const unitLabel = (c) => unitOptions.find((o) => o.value === c)?.label || c;

  // `sameForAll` and `enableSameForAll` are lifted to the parent so the tick can
  // live beside Seasonal in the Price card header. When on, the matrix collapses
  // to one "All locations" row and each edit fans out to all active branches.

  // Only the locations explicitly attached to this dish drive rows. We
  // preserve the order in which the user picked them so the matrix is stable
  // across renders.
  const activeLocations = (locations || []).filter((l) =>
    (selectedLocationIds || []).includes(l.id),
  );

  const getCell = (variant, locationId) =>
    (variant?.location_prices || []).find(
      (p) => Number(p.location_id) === Number(locationId),
    );

  const upsertCell = (variantIdx, locationId, patch) => {
    setVariants((prev) => {
      const next = [...prev];
      const rows = [...(next[variantIdx].location_prices || [])];
      const i = rows.findIndex(
        (r) => Number(r.location_id) === Number(locationId),
      );
      if (i >= 0) rows[i] = { ...rows[i], ...patch };
      else rows.push({
        location_id: Number(locationId),
        price: '',
        takeaway_price: '',
        delivery_price: '',
        ...patch,
      });
      next[variantIdx] = { ...next[variantIdx], location_prices: rows };
      return next;
    });
  };

  // "Same price for all locations" mode: one edit writes the same patch to
  // every active branch so they stay identical.
  const upsertCellAll = (variantIdx, patch) => {
    setVariants((prev) => {
      const next = [...prev];
      const rows = [...(next[variantIdx].location_prices || [])];
      activeLocations.forEach((loc) => {
        const i = rows.findIndex((r) => Number(r.location_id) === Number(loc.id));
        if (i >= 0) rows[i] = { ...rows[i], ...patch };
        else rows.push({
          location_id: Number(loc.id),
          price: '',
          takeaway_price: '',
          delivery_price: '',
          ...patch,
        });
      });
      next[variantIdx] = { ...next[variantIdx], location_prices: rows };
      return next;
    });
  };

  const setVariantSeasonal = (variantIdx, value) => {
    setVariants((prev) => {
      const next = [...prev];
      next[variantIdx] = { ...next[variantIdx], is_seasonal: value };
      return next;
    });
  };

  // Columns are the locations (one per branch). In "same price for all" mode
  // they collapse to a single "All locations" column whose edits fan out to
  // every branch; it reads/writes through the first active location.
  const locationColumns = activeLocations.length === 0
    ? []
    : (sameForAll
        ? [{ id: activeLocations[0].id, name: 'All locations', isAll: true }]
        : activeLocations);

  // 220px label · N location columns · 24px filler. Columns are wide because
  // each cell stacks the Normal / Take-Away / Delivery price sub-inputs.
  const gridTemplate = `220px repeat(${Math.max(locationColumns.length, 1)}, minmax(200px, 1fr)) 24px`;

  // Normal (dine-in) is always priced. Take-Away / Delivery cells only appear
  // when the dish allows that channel (toggles on Basic Information).
  const priceRows = [
    { key: 'price', label: 'NORM', tone: 'text-slate-500' },
    ...(takeawayAllowed ? [{ key: 'takeaway_price', label: 'T-AWAY', tone: 'text-slate-500' }] : []),
    ...(deliveryAllowed ? [{ key: 'delivery_price', label: 'DELIV', tone: 'text-slate-500' }] : []),
  ];
  const cellsHint = priceRows.map((r) => (
    r.key === 'price' ? 'Normal' : r.key === 'takeaway_price' ? 'Take-Away' : 'Delivery'
  )).join(' · ');

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      {/* Same-price-for-all toggle lives in the Price card header, beside the
          Seasonal tick (see AddEditDish). */}

      {/* Header row: location column titles */}
      <div
        className="grid items-end gap-0 px-3 py-3 bg-slate-50 border-b border-slate-200"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 font-mono">
            Variant ↓ · Location →
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Cells: {cellsHint}
          </div>
        </div>
        {locationColumns.map((col) => (
          <div key={col.id} className="px-2 min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate" title={col.isAll ? 'All locations' : col.name}>
              {col.isAll ? 'All locations' : col.name}
            </div>
            {col.isAll ? (
              <div className="text-[10px] text-slate-500 mt-0.5 truncate font-mono">
                {activeLocations.length} branch(es) · same price
              </div>
            ) : col.address && (
              <div className="text-[10px] text-slate-500 mt-0.5 truncate font-mono" title={col.address}>
                {col.address}
              </div>
            )}
          </div>
        ))}
        <div />
      </div>

      {/* Body: one row per variant */}
      {activeLocations.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-slate-500 italic">
          No locations selected for this dish yet — pick branches in the
          Locations card above, then price each variant per branch here.
        </div>
      ) : (
        variants.map((v, vi) => {
          const isBase = vi === baseIdx;
          const seasonal = !!v.is_seasonal;
          const label = (v.variant_name || '').trim() || `Variant ${vi + 1}`;
          const sharedInputCls = `w-full bg-transparent outline-none font-mono text-sm font-semibold tabular-nums ${
            seasonal ? 'text-slate-400' : 'text-slate-800'
          }`;
          const fieldBoxCls = `flex items-center gap-1 border rounded-md px-2 py-1 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-200 ${
            seasonal ? 'bg-slate-100 border-slate-200' : 'bg-white border-slate-200'
          }`;
          return (
            <div
              key={vi}
              className="grid items-stretch gap-0 px-3 py-2 border-b border-slate-100 hover:bg-slate-50/40"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              <div className="min-w-0 pr-2 self-center">
                <div className="flex items-baseline gap-1.5 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate" title={label}>
                    {label}
                  </div>
                  {isBase && (
                    <span className="text-[9px] font-bold tracking-wider px-1 py-0.5 rounded bg-blue-600 text-white font-mono shrink-0">
                      BASE
                    </span>
                  )}
                  {kind !== 'addon' && (
                    <button
                      type="button"
                      onClick={() => setVariantSeasonal(vi, !v.is_seasonal)}
                      className={`ml-auto text-[9px] font-mono px-1 py-0.5 rounded border ${
                        v.is_seasonal
                          ? 'bg-amber-100 text-amber-700 border-amber-300'
                          : 'bg-white text-slate-400 border-slate-200 hover:border-amber-200 hover:text-amber-600'
                      }`}
                      title="Seasonal — price set day-of for every location"
                    >
                      SEASONAL
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1 text-[10.5px] text-slate-500 mt-0.5 font-mono">
                  <span className="truncate" title={sizeLabel(v.size) || ''}>{sizeLabel(v.size) || '—'}</span>
                  <span>·</span>
                  <span className="truncate" title={unitLabel(v.unit) || ''}>{unitLabel(v.unit) || '—'}</span>
                </div>
              </div>
              {locationColumns.map((col) => {
                const cell = getCell(v, col.id) || {};
                return (
                  <div key={col.id} className="px-1.5">
                    <div className="space-y-1">
                      {priceRows.map((row) => {
                        /* What the typed figure comes to once the dish's taxes
                           are on it, read both ways round: the net the
                           restaurant keeps and the gross the guest pays. */
                        const typed = seasonal ? 0 : Number(cell[row.key]) || 0;
                        const split = taxRows.length && typed > 0 ? splitPrice(typed, taxRows, taxInclusive) : null;
                        return (
                          <div key={row.key}>
                            <div className={fieldBoxCls}>
                              <span className={`text-[9.5px] font-mono shrink-0 ${row.tone} w-10`}>{row.label}</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={seasonal ? '' : (cell[row.key] ?? '')}
                                onChange={(e) =>
                                  col.isAll
                                    ? upsertCellAll(vi, { [row.key]: e.target.value })
                                    : upsertCell(vi, col.id, { [row.key]: e.target.value })
                                }
                                disabled={seasonal}
                                placeholder={seasonal ? 'Seasonal' : '0.00'}
                                className={sharedInputCls}
                              />
                            </div>
                            {split && (
                              <div className="px-1 pt-0.5 text-[10px] font-mono text-slate-500 tabular-nums">
                                {t('addDish.tax.cellLine', { net: split.net.toFixed(2), gross: split.gross.toFixed(2) })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <div />
            </div>
          );
        })
      )}

    </div>
  );
}

// Macro split donut — protein / carbs / fat as a share of calories
// (protein & carbs = 4 kcal/g, fat = 9 kcal/g). Pure inline SVG, no chart dep.
function MacroPie({ nutrition = {} }) {
  const val = (v) => { const x = parseFloat(v); return Number.isFinite(x) && x > 0 ? x : 0; };
  const segs = [
    { key: 'protein', label: 'Protein', g: val(nutrition.protein), kcal: val(nutrition.protein) * 4, color: '#10b981' },
    { key: 'carbs',   label: 'Carbs',   g: val(nutrition.carbs),   kcal: val(nutrition.carbs) * 4,   color: '#f59e0b' },
    { key: 'fat',     label: 'Fat',     g: val(nutrition.fat),     kcal: val(nutrition.fat) * 9,     color: '#6366f1' },
  ];
  const total = segs.reduce((s, x) => s + x.kcal, 0);
  const r = 42;
  const C = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-2">
        Macro split <span className="text-slate-400 normal-case font-normal">(by calories)</span>
      </div>
      {total === 0 ? (
        <div className="text-xs text-slate-500 py-8 text-center">
          Enter protein, carbs &amp; fat to see the macro breakdown.
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <svg viewBox="0 0 100 100" className="h-28 w-28 -rotate-90 shrink-0" role="img" aria-label="Macronutrient calorie split">
            <circle cx="50" cy="50" r={r} fill="none" stroke="#f1f5f9" strokeWidth="14" />
            {segs.filter((s) => s.kcal > 0).map((s) => {
              const len = (s.kcal / total) * C;
              const el = (
                <circle
                  key={s.key}
                  cx="50" cy="50" r={r}
                  fill="none" stroke={s.color} strokeWidth="14"
                  strokeDasharray={`${len} ${C - len}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += len;
              return el;
            })}
          </svg>
          <div className="space-y-1 text-xs flex-1 min-w-0">
            <div className="font-mono text-slate-500 mb-1">{Math.round(total)} kcal from macros</div>
            {segs.map((s) => (
              <div key={s.key} className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
                <span className="font-medium text-slate-700 w-14">{s.label}</span>
                <span className="font-mono text-slate-600">{s.g} g</span>
                <span className="font-mono text-slate-400 ml-auto">{Math.round((s.kcal / total) * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Canonical form of a nutrition blob (sorted, blanks dropped) so two variants
// can be compared regardless of the order their keys were entered in.
const canonNutrition = (o = {}) => JSON.stringify(
  Object.keys(o)
    .filter((k) => o[k] !== '' && o[k] != null && !(Array.isArray(o[k]) && o[k].length === 0))
    .sort()
    .map((k) => [k, o[k]]),
);
// True when every variant already carries the same nutrition values — used to
// default the "Same for all" toggle on when re-editing such a dish.
const allVariantsShareNutrition = (vs = []) =>
  vs.length > 1 && vs.every((v) => canonNutrition(v.nutrition) === canonNutrition(vs[0].nutrition));

// Nutritional Info — per-variant nutrition entry + a live Health Meter.
// A variant selector switches which variant's values are being edited (Half and
// Full carry different nutrition); parameters are grouped (Basic, Macros,
// Micronutrients, Health scoring, Diet flags) and the parameter set depends on
// dishType (beverages use the drink set). Values persist in variant.nutrition.
// A "Same nutrition for all variants" toggle keeps one shared set across every
// variant when Half/Full/etc. don't differ nutritionally.
function NutritionPanel({ variants, setVariants, dishType, sizeOptions = [] }) {
  const sizeLabel = (c) => sizeOptions.find((o) => o.value === c)?.label || c;
  const groups = groupsForType(dishType);
  const isBeverage = dishType === 'beverage';
  const meterBasis = isBeverage ? BEVERAGE_METER_BASIS : FOOD_METER_BASIS;

  const [active, setActive] = useState(0);
  const idx = Math.min(active, Math.max(0, variants.length - 1));
  const variant = variants[idx] || {};
  const nutrition = variant.nutrition || {};

  // "Same nutrition for all variants" — one shared set instead of per-variant
  // entry. Auto-on when the variants already carry identical values (e.g.
  // re-editing a dish that was saved that way). While on, every edit fans out to
  // every variant so they never drift apart.
  const [sameForAll, setSameForAll] = useState(() => allVariantsShareNutrition(variants));

  const setField = (key, value) => {
    setVariants((prev) => prev.map((v, i) => (
      (sameForAll || i === idx)
        ? { ...v, nutrition: { ...(v.nutrition || {}), [key]: value } }
        : v
    )));
  };

  // Turning it on copies the variant currently in view to every variant so they
  // start identical; turning it off lets them diverge again.
  const toggleSameForAll = (on) => {
    setSameForAll(on);
    if (on) setVariants((prev) => prev.map((v) => ({ ...v, nutrition: { ...nutrition } })));
  };

  const meter = computeHealthScore(nutrition, dishType);

  const renderField = (f) => {
    const val = nutrition[f.key];
    if (f.type === 'bool') {
      const on = !!val;
      return (
        <label key={f.key} className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-slate-200 bg-white cursor-pointer text-xs hover:border-slate-300">
          <input type="checkbox" checked={on} onChange={() => setField(f.key, !on)} className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium truncate" title={f.label}>{f.label}</span>
        </label>
      );
    }
    if (f.type === 'level') {
      return (
        <div key={f.key} className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{f.label}</Label>
          <Select value={val || '_none'} onValueChange={(v) => setField(f.key, v === '_none' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">—</SelectItem>
              {LEVEL_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (f.type === 'multi') {
      // Stored as an array of value tokens; old free-text values (if any) are
      // ignored by the chip UI. Spans the full row so the chips have room.
      const selected = Array.isArray(val) ? val : [];
      const toggle = (v) => setField(
        f.key,
        selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v],
      );
      return (
        <div key={f.key} className="space-y-1 col-span-full">
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {f.label}{selected.length ? <span className="text-slate-400 normal-case font-normal"> · {selected.length} selected</span> : null}
          </Label>
          <div className="flex flex-wrap gap-1.5">
            {f.options.map((o) => {
              const on = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  aria-pressed={on}
                  className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                    on
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'border-slate-300 bg-white text-slate-700 hover:border-primary/50 hover:bg-accent'
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    // numeric
    return (
      <div key={f.key} className="space-y-1">
        <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          {f.label}{f.unit ? <span className="text-slate-400 normal-case font-normal"> ({f.unit})</span> : null}
        </Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={val ?? ''}
          onChange={(e) => setField(f.key, e.target.value)}
          placeholder="0"
          className="h-8 text-xs font-mono"
        />
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle>Nutritional Info</CardTitle>
          </div>
          {variants.length > 1 && (
            <label
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 bg-white cursor-pointer text-xs font-medium hover:border-slate-300"
              title="Use one shared set of nutrition values for every variant"
            >
              <input
                type="checkbox"
                checked={sameForAll}
                onChange={(e) => toggleSameForAll(e.target.checked)}
                className="h-3.5 w-3.5 shrink-0"
              />
              <span>Same nutrition for all variants</span>
            </label>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* When sharing one set across variants, the per-variant selector is
            hidden — edits apply everywhere. */}
        {variants.length > 1 && sameForAll && (
          <p className="text-xs text-muted-foreground">
            These values apply to all {variants.length} variants. Uncheck
            {' '}<span className="font-medium">Same nutrition for all variants</span> to set them per variant.
          </p>
        )}

        {/* Variant selector — only when there's more than one variant and they
            aren't being kept identical. */}
        {variants.length > 1 && !sameForAll && (
          <div className="flex flex-wrap gap-1.5">
            {variants.map((v, i) => {
              const on = i === idx;
              const label = (v.variant_name || '').trim() || sizeLabel(v.size) || `Variant ${i + 1}`;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                    on ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                       : 'border-slate-300 bg-white text-slate-700 hover:border-primary/50'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Health Meter + macro pie, side by side. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Health Meter — live rating from the key parameters. */}
        <div className={`rounded-lg border px-4 py-3 ${meter.ring}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`h-9 w-9 rounded-full text-white grid place-items-center font-bold ${meter.bar}`}>
                {meter.grade}
              </div>
              <div>
                <div className={`text-sm font-semibold ${meter.text}`}>Health Meter — {meter.label}</div>
              </div>
            </div>
            <div className={`text-2xl font-bold tabular-nums ${meter.text}`}>{meter.hasData ? meter.score : '—'}<span className="text-sm text-slate-400">/100</span></div>
          </div>
          <div className="mt-2 h-2 w-full rounded-full bg-white/70 overflow-hidden border border-black/5">
            <div className={`h-full ${meter.bar} transition-all`} style={{ width: `${meter.hasData ? meter.score : 0}%` }} />
          </div>
        </div>

        <MacroPie nutrition={nutrition} />
        </div>

        {/* Parameter groups. */}
        {groups.map((g) => (
          <div key={g.key} className="rounded-lg border border-slate-200 bg-white">
            <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">{g.title}</span>
            </div>
            <div className="p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
              {g.fields.map(renderField)}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// Add-on · variant matrix — variants are columns, chosen add-ons are rows, each
// cell holds the add-on's price for that variant (Cheese = 50 with Full, 30
// with Half). Stored per-variant in variant.addons[] = [{ addon_id, price }].
function AddonMatrix({
  variants, setVariants, addonDishes = [], dishBaseDiet = 'veg',
  sizeOptions = [], unitOptions = [],
  restaurantId, locationIds = [], onCreated, toast,
}) {
  const sizeLabel = (c) => sizeOptions.find((o) => o.value === c)?.label || c;
  const unitLabel = (c) => unitOptions.find((o) => o.value === c)?.label || c;

  // Inline "create add-on" dialog state.
  const [createDlg, setCreateDlg] = useState(null); // null | { name, price, diet }
  const [savingNew, setSavingNew] = useState(false);

  const addonById = useMemo(
    () => new Map(addonDishes.map((d) => [Number(d.id), d])),
    [addonDishes],
  );
  // Union of add-on ids across every variant, in order of first appearance.
  const addonIds = useMemo(() => {
    const seen = new Set();
    const order = [];
    variants.forEach((v) => {
      (v.addons || []).forEach((a) => {
        const id = Number(a.addon_id);
        if (id && !seen.has(id)) { seen.add(id); order.push(id); }
      });
    });
    return order;
  }, [variants]);

  const available = addonDishes.filter((d) => !addonIds.includes(Number(d.id)));

  const getPrice = (variant, addonId) => {
    const row = (variant.addons || []).find((a) => Number(a.addon_id) === Number(addonId));
    return row ? (row.price ?? '') : '';
  };

  // Strip leading zeros a number input leaves behind ("01212" → "1212",
  // "012" → "12") while keeping a lone "0", decimals ("0.5", "0.00") and ''.
  const normalizePrice = (raw) => {
    if (raw === '' || raw == null) return raw;
    return String(raw).replace(/^0+(?=\d)/, '');
  };

  const setPrice = (variantIdx, addonId, price) => {
    setVariants((prev) => prev.map((v, i) => {
      if (i !== variantIdx) return v;
      const rows = [...(v.addons || [])];
      const j = rows.findIndex((a) => Number(a.addon_id) === Number(addonId));
      if (j >= 0) rows[j] = { ...rows[j], price };
      else rows.push({ addon_id: Number(addonId), price });
      return { ...v, addons: rows };
    }));
  };

  // Adding an add-on adds the row to EVERY variant (price blank until entered).
  const addAddon = (addonId) => {
    if (!addonId) return;
    setVariants((prev) => prev.map((v) => {
      const rows = [...(v.addons || [])];
      if (rows.some((a) => Number(a.addon_id) === Number(addonId))) return v;
      return { ...v, addons: [...rows, { addon_id: Number(addonId), price: '' }] };
    }));
  };

  const removeAddon = (addonId) => {
    setVariants((prev) => prev.map((v) => ({
      ...v,
      addons: (v.addons || []).filter((a) => Number(a.addon_id) !== Number(addonId)),
    })));
  };

  // Add an add-on to every variant, pre-filling a price (used after inline create).
  const addAddonWithPrice = (addonId, price) => {
    setVariants((prev) => prev.map((v) => {
      const rows = [...(v.addons || [])];
      if (rows.some((a) => Number(a.addon_id) === Number(addonId))) return v;
      return { ...v, addons: [...rows, { addon_id: Number(addonId), price: price ?? '' }] };
    }));
  };

  // Create a brand-new add-on Dish (kind='addon') inline, then drop it into the
  // matrix. It inherits this dish's locations; an optional price seeds every
  // variant cell. The full add-on can be fleshed out later from All Menu Items.
  const submitNewAddon = async () => {
    const name = (createDlg?.name || '').trim();
    if (!name) {
      toast?.({ title: 'Add-on name is required', variant: 'destructive' });
      return;
    }
    // Add-ons must pick a diet deliberately (a Cheese add-on being veg vs the
    // same paneer being non-veg changes every variant it's attached to).
    const diet = createDlg?.diet || '';
    if (!diet) {
      toast?.({ title: 'Pick a diet (Veg / Non-Veg / Vegan) for this add-on', variant: 'destructive' });
      return;
    }
    const priceNum = parseFloat(createDlg?.price);
    const price = Number.isFinite(priceNum) && priceNum > 0 ? priceNum : 0;
    setSavingNew(true);
    try {
      const payload = {
        type: 'addon',
        name,
        description: '',
        // Diet booleans: veg → vegetarian, vegan → both, nonveg → neither.
        is_vegetarian: diet === 'veg' || diet === 'vegan',
        is_vegan: diet === 'vegan',
        location_ids: locationIds || [],
        variants: [{
          variant_name: name,
          size: '', unit: '', quantity: 1,
          is_available: true,
          display_order: 0,
          location_prices: (price > 0 ? (locationIds || []) : []).map((lid) => ({
            location_id: lid, price, takeaway_price: 0, delivery_price: 0,
          })),
        }],
      };
      const res = await createDish(restaurantId, payload);
      const newId = res?.data?.id ?? res?.id;
      if (!res?.success || !newId) {
        throw res?.error || 'Failed to create add-on';
      }
      await onCreated?.();                       // refresh the add-on catalogue
      addAddonWithPrice(Number(newId), price > 0 ? price : '');
      toast?.({ title: 'Add-on created', description: `"${name}" added to the matrix.` });
      setCreateDlg(null);
    } catch (e) {
      const msg = formatApiError(e, 'Failed to create add-on');
      toast?.({ title: 'Could not create add-on', description: msg, variant: 'destructive' });
    } finally {
      setSavingNew(false);
    }
  };

  const gridTemplate = `220px repeat(${Math.max(variants.length, 1)}, minmax(120px, 1fr)) 40px`;

  return (
    <div className="space-y-3">
      {/* Add-on picker — pick an existing add-on or create a new one inline. */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value="" onValueChange={(v) => addAddon(Number(v))} disabled={addonDishes.length === 0}>
          <SelectTrigger className="h-9 w-64 text-sm">
            <SelectValue placeholder={
              addonDishes.length === 0
                ? 'No add-ons yet — create one →'
                : (available.length ? 'Add an existing add-on…' : 'All add-ons added')
            } />
          </SelectTrigger>
          <SelectContent>
            {available.length === 0 ? (
              <SelectItem value="_none" disabled>All add-ons added</SelectItem>
            ) : (
              available.map((d) => (
                <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <Button type="button" size="sm" variant="outline" onClick={() => setCreateDlg({ name: '', price: '', diet: '' })}>
          <Plus className="h-4 w-4 mr-1.5" /> New add-on
        </Button>
      </div>

      {addonIds.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-slate-500 border border-dashed rounded-lg">
          No add-ons added yet — pick one above, or use <strong>New add-on</strong> to create one, then set its price per variant.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white overflow-x-auto">
          {/* Header: variant columns */}
          <div className="grid items-end gap-0 px-3 py-3 bg-slate-50 border-b border-slate-200" style={{ gridTemplateColumns: gridTemplate }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 font-mono">
              Add-on ↓ · Variant →
            </div>
            {variants.map((v, idx) => {
              const label = (v.variant_name || '').trim() || sizeLabel(v.size) || `Variant ${idx + 1}`;
              return (
                <div key={idx} className="px-2 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate" title={label}>{label}</div>
                  <div className="text-[10.5px] text-slate-500 font-mono truncate">
                    {sizeLabel(v.size) || '—'} · {unitLabel(v.unit) || '—'}
                  </div>
                </div>
              );
            })}
            <div />
          </div>

          {/* Body: one row per add-on */}
          {addonIds.map((addonId) => {
            const addon = addonById.get(Number(addonId));
            return (
              <div key={addonId} className="grid items-center gap-0 px-3 py-2 border-b border-slate-100 hover:bg-slate-50/40" style={{ gridTemplateColumns: gridTemplate }}>
                <div className="min-w-0 pr-2">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800" title={addon?.name || `Add-on #${addonId}`}>
                    <span
                      className={`h-2 w-2 rounded-full shrink-0 ${dietMeta(dietOf(addon)).dot}`}
                      title={`${dietMeta(dietOf(addon)).label} add-on`}
                    />
                    <span className="truncate">{addon?.name || `Add-on #${addonId}`}</span>
                  </div>
                </div>
                {variants.map((v, vi) => {
                  const cell = getPrice(v, addonId);
                  // A blank or zero cell is invalid — every add-on must be priced.
                  const invalid = cell === '' || cell == null || !(parseFloat(cell) > 0);
                  return (
                  <div key={vi} className="px-1.5">
                    <div className={`flex items-center gap-1 border rounded-md px-2 py-1 bg-white focus-within:ring-1 ${invalid ? 'border-rose-300 focus-within:border-rose-400 focus-within:ring-rose-200' : 'border-slate-200 focus-within:border-blue-400 focus-within:ring-blue-200'}`}>
                                            <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={cell}
                        onChange={(e) => setPrice(vi, addonId, normalizePrice(e.target.value))}
                        placeholder="0.00"
                        className="w-full bg-transparent outline-none font-mono text-sm font-semibold tabular-nums text-slate-800"
                      />
                    </div>
                  </div>
                  );
                })}
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => removeAddon(addonId)}
                    className="p-1 text-slate-400 hover:text-destructive"
                    title="Remove this add-on"
                    aria-label="Remove add-on"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Inline create-add-on dialog. */}
      <Dialog open={createDlg !== null} onOpenChange={(o) => !o && setCreateDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New add-on</DialogTitle>
          </DialogHeader>
          {createDlg && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                  Name <span className="text-rose-500">*</span>
                </Label>
                <Input
                  autoFocus
                  value={createDlg.name}
                  onChange={(e) => setCreateDlg({ ...createDlg, name: e.target.value })}
                  placeholder="e.g., Extra Cheese"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitNewAddon(); } }}
                />
              </div>
              {/* Diet — Veg / Non-Veg / Vegan. Required: an add-on's diet drives
                  the effective diet of every variant it's attached to. */}
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                  Diet <span className="text-rose-500">*</span>
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { value: 'veg', label: 'Veg', dot: 'bg-green-500', activeCls: 'border-green-400 bg-green-50 text-green-700' },
                    { value: 'nonveg', label: 'Non-Veg', dot: 'bg-rose-600', activeCls: 'border-rose-500 bg-rose-50 text-rose-800' },
                    { value: 'vegan', label: 'Vegan', dot: 'bg-emerald-800', activeCls: 'border-emerald-700 bg-emerald-50 text-emerald-900' },
                  ].map((opt) => {
                    const active = createDlg.diet === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setCreateDlg({ ...createDlg, diet: opt.value })}
                        aria-pressed={active}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs transition-colors ${
                          active
                            ? opt.activeCls
                            : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                        }`}
                      >
                        <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {!createDlg.diet && (
                  <p className="text-[11px] text-rose-600">Pick a diet for this add-on.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                  Default price <span className="text-slate-400 normal-case font-normal">(optional)</span>
                </Label>
                <div className="flex items-center gap-1 border rounded-md px-2 py-1.5 bg-white border-slate-200 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-200 w-40">
                                    <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={createDlg.price}
                    onChange={(e) => setCreateDlg({ ...createDlg, price: normalizePrice(e.target.value) })}
                    placeholder="0.00"
                    className="w-full bg-transparent outline-none font-mono text-sm font-semibold tabular-nums text-slate-800"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateDlg(null)}>Cancel</Button>
            <Button type="button" onClick={submitNewAddon} disabled={savingNew}>
              {savingNew ? 'Creating…' : 'Create add-on'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Recipe · variant matrix — single editor that replaces per-variant recipe
// modals. Variants are columns, ingredients are rows. Each cell is a qty +
// unit input; a ×ratio against the base (first) variant is shown when it
// differs, so size-scaling patterns are obvious at a glance.
function RecipeMatrix({ variants, setVariants, ingredientsMaster, setSelectedIngredientIds, unitOptions = [], sizeOptions = [] }) {
  // size/unit are stored as master codes (e.g. 'full_dcc1'); show their labels.
  const sizeLabel = (c) => sizeOptions.find((o) => o.value === c)?.label || c;
  const unitLabel = (c) => unitOptions.find((o) => o.value === c)?.label || c;
  // Union of ingredient_ids across all variants' recipes, in order of first
  // appearance — the visible row order in the matrix.
  const ingredientIds = useMemo(() => {
    const seen = new Set();
    const order = [];
    variants.forEach((v) => {
      (v.recipe || []).forEach((r) => {
        const id = Number(r.ingredient_id);
        if (id && !seen.has(id)) {
          seen.add(id);
          order.push(id);
        }
      });
    });
    return order;
  }, [variants]);

  const ingredientById = useMemo(() => {
    const m = new Map();
    (ingredientsMaster || []).forEach((i) => m.set(Number(i.id), i));
    return m;
  }, [ingredientsMaster]);

  const getRow = (variant, ingredientId) =>
    (variant?.recipe || []).find(
      (r) => Number(r.ingredient_id) === Number(ingredientId),
    );

  // Base = first variant (matches the design's first-column = BASE convention).
  const baseIdx = 0;
  const baseQtyFor = (ingredientId) => {
    const row = getRow(variants[baseIdx] || {}, ingredientId);
    const n = parseFloat(row?.quantity);
    return Number.isFinite(n) && n !== 0 ? n : null;
  };

  const upsertCell = (variantIdx, ingredientId, patch) => {
    setVariants((prev) => {
      const next = [...prev];
      const recipe = [...(next[variantIdx].recipe || [])];
      const i = recipe.findIndex(
        (r) => Number(r.ingredient_id) === Number(ingredientId),
      );
      if (i >= 0) recipe[i] = { ...recipe[i], ...patch };
      else recipe.push({
        ingredient_id: Number(ingredientId), quantity: '', unit: '', ...patch,
      });
      next[variantIdx] = { ...next[variantIdx], recipe };
      return next;
    });
  };

  // Unit is per-ingredient, not per-cell: an ingredient like "milk" is always
  // measured the same way across every variant. Read the row's unit from
  // whichever variant has it set first (legacy rows may diverge).
  const getRowUnit = (ingredientId) => {
    for (const v of variants) {
      const r = (v.recipe || []).find(
        (x) => Number(x.ingredient_id) === Number(ingredientId),
      );
      if (r?.unit) return r.unit;
    }
    return '';
  };

  const setRowUnit = (ingredientId, newUnit) => {
    setVariants((prev) =>
      prev.map((v) => ({
        ...v,
        recipe: (v.recipe || []).map((r) =>
          Number(r.ingredient_id) === Number(ingredientId)
            ? { ...r, unit: newUnit }
            : r,
        ),
      })),
    );
  };

  // Removing an ingredient drops it from every variant — the row leaves the
  // matrix entirely. Also unticks it in the Ingredients picker.
  const removeIngredient = (ingredientId) => {
    setVariants((prev) =>
      prev.map((v) => ({
        ...v,
        recipe: (v.recipe || []).filter(
          (r) => Number(r.ingredient_id) !== Number(ingredientId),
        ),
      })),
    );
    if (setSelectedIngredientIds) {
      setSelectedIngredientIds((prev) =>
        prev.filter((x) => Number(x) !== Number(ingredientId)),
      );
    }
  };

  // Add a new row: ingredient must come from the master and not already be
  // in the matrix. Seeds an empty cell into every variant. Also ticks it in
  // the Ingredients picker so the two stay in sync.
  const addIngredient = (id) => {
    if (!id) return;
    setVariants((prev) =>
      prev.map((v) => ({
        ...v,
        recipe: [
          ...(v.recipe || []),
          { ingredient_id: Number(id), quantity: '', unit: '' },
        ],
      })),
    );
    if (setSelectedIngredientIds) {
      setSelectedIngredientIds((prev) =>
        prev.includes(Number(id)) ? prev : [...prev, Number(id)],
      );
    }
  };

  const available = (ingredientsMaster || []).filter(
    (i) => !ingredientIds.includes(Number(i.id)),
  );

  // 190px ingredient · 120px unit · N variant columns · 40px row-action.
  const gridTemplate = `190px 120px repeat(${Math.max(variants.length, 1)}, minmax(140px, 1fr)) 40px`;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      {/* Sticky variant column header */}
      <div
        className="grid items-end gap-0 px-3 py-3 bg-slate-50 border-b border-slate-200"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 font-mono">
            Ingredient
          </div>
        </div>
        <div className="px-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 font-mono">
            Unit
          </div>
        </div>
        {variants.map((v, idx) => {
          const isBase = idx === baseIdx;
          const label = (v.variant_name || '').trim() || `Variant ${idx + 1}`;
          return (
            <div key={idx} className="px-2 min-w-0">
              <div className="flex items-baseline gap-1.5 min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate" title={label}>
                  {label}
                </div>
                {isBase && (
                  <span className="text-[9px] font-bold tracking-wider px-1 py-0.5 rounded bg-blue-600 text-white font-mono shrink-0">
                    BASE
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 text-[10.5px] text-slate-500 mt-0.5 font-mono">
                <span className="truncate" title={sizeLabel(v.size) || ''}>{sizeLabel(v.size) || '—'}</span>
                <span>·</span>
                <span className="truncate" title={unitLabel(v.unit) || ''}>{unitLabel(v.unit) || '—'}</span>
              </div>
            </div>
          );
        })}
        <div />
      </div>

      {/* Body */}
      {ingredientIds.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-slate-500 italic">
          No ingredients in the matrix yet — pick one below to start the recipe.
        </div>
      ) : (
        ingredientIds.map((id) => {
          const ing = ingredientById.get(Number(id));
          const base = baseQtyFor(id);
          const rowUnit = getRowUnit(id);
          return (
            <div
              key={id}
              className="grid items-center gap-0 px-3 py-2 border-b border-slate-100 hover:bg-slate-50/40 group"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {/* Ingredient name */}
              <div className="min-w-0 pr-2">
                <div
                  className="text-sm font-medium truncate text-slate-800"
                  title={ing?.name || `Ingredient #${id}`}
                >
                  {ing?.name || `Ingredient #${id}`}
                </div>
              </div>

              {/* Unit — its own column, applies to every variant in the row */}
              <div className="px-1.5">
                <select
                  value={rowUnit}
                  onChange={(e) => setRowUnit(id, e.target.value)}
                  className="w-full h-9 bg-white border border-slate-200 rounded-md px-2 font-mono text-[11px] text-slate-600 cursor-pointer outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                  aria-label={`Unit for ${ing?.name || 'ingredient'}`}
                >
                  <option value="">unit…</option>
                  {unitOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {variants.map((v, vi) => {
                const row = getRow(v, id);
                const qty = row?.quantity ?? '';
                const n = parseFloat(qty);
                const ratio = base != null && Number.isFinite(n) && n !== 0 ? n / base : null;
                const showRatio = vi !== baseIdx && ratio !== null && ratio !== 1;
                return (
                  <div key={vi} className="px-1.5">
                    <div className="flex items-center gap-1.5 h-9 border border-slate-200 rounded-md bg-white px-2 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-200">
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={qty}
                        onChange={(e) => upsertCell(vi, id, { quantity: e.target.value, unit: rowUnit })}
                        placeholder="0"
                        className="flex-1 min-w-0 bg-transparent outline-none font-mono text-sm font-semibold text-slate-800 tabular-nums"
                      />
                      {showRatio && (
                        <span className="text-[10px] text-slate-400 font-mono shrink-0 whitespace-nowrap border-l border-slate-200 pl-1.5">
                          ×{ratio.toFixed(ratio % 1 === 0 ? 0 : 2)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => removeIngredient(id)}
                  className="text-slate-300 hover:text-rose-600 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove this ingredient from every variant"
                  aria-label={`Remove ${ing?.name || 'ingredient'} from matrix`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })
      )}

      {/* Add-ingredient row */}
      <div className="px-3 py-3 bg-slate-50/60 border-t border-slate-200">
        {(ingredientsMaster || []).length === 0 ? (
          <div className="text-xs text-slate-500 italic text-center">
            No ingredients in the master yet — create them above first.
          </div>
        ) : available.length === 0 ? (
          <div className="text-xs text-slate-500 italic text-center">
            All available ingredients are in the matrix.
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <Select
              value=""
              onValueChange={(value) => addIngredient(value)}
            >
              <SelectTrigger className="h-8 text-xs w-64">
                <SelectValue placeholder="+ Add ingredient row…" />
              </SelectTrigger>
              <SelectContent>
                {available.map((i) => (
                  <SelectItem key={i.id} value={String(i.id)}>
                    {i.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}

// Complimentary · per-variant table — column 1 is the variant label, columns
// 2–5 are the Dishes / Beverages / Add-ons / Combos pickers. Each variant gets
// its own row so users can see and edit every bundle side-by-side. Dishes and
// Beverages allow multiple rows per item (different sizes / quantities); the
// other two are flat multi-selects.
function ComplimentaryMatrix({ variants, setVariants, allDishes, editingDishId, flat = false, sizeOptions = [], unitOptions = [] }) {
  // size/unit are stored as master codes (e.g. 'full_dcc1'); show their labels.
  const sizeLabel = (c) => sizeOptions.find((o) => o.value === c)?.label || c;
  const unitLabel = (c) => unitOptions.find((o) => o.value === c)?.label || c;
  // Bucket master items by category, excluding the dish being edited (a dish
  // can't be complimentary to itself). Iteration order drives column order.
  const buckets = useMemo(() => {
    const others = (allDishes || []).filter((d) => d.id !== editingDishId);
    return {
      // "Dishes" = every non-beverage dish (food / main course / appetizer /
      // dessert …), matching the Recommendations picker. Filtering on
      // dish_type === 'food' was too strict and hid everything but plain 'food'.
      'Dishes':    others.filter((d) => d.kind === 'dish' && d.dish_type !== 'beverage'),
      'Beverages': others.filter((d) => d.kind === 'dish' && d.dish_type === 'beverage'),
      'Add-ons':   others.filter((d) => d.kind === 'addon'),
      'Combos':    others.filter((d) => d.kind === 'combo'),
    };
  }, [allDishes, editingDishId]);

  // In flat mode (combos) every column is a plain multi-select — combos carry
  // no variants themselves, so we don't ask "which size?" for bundled items.
  const supportsVariants = (group) => !flat && (group === 'Dishes' || group === 'Beverages');

  // Complimentary items are stored PER VARIANT — Half and Full can bundle
  // different free items. setVariantItems edits a single variant's set (used for
  // removals and qty/size tweaks, which stay local to that variant).
  const setVariantItems = (variantIdx, items) => {
    setVariants((prev) =>
      prev.map((vv, idx) => (idx === variantIdx ? { ...vv, complimentary_items: items } : vv)),
    );
  };

  // Convenience default: adding a complimentary item to ONE variant adds it to
  // every other variant too (the common case is "same freebie for all sizes").
  // The user can then remove it from individual variants so they differ.
  const addItemToAllVariants = (dishId, defaultVariantId) => {
    const id = Number(dishId);
    setVariants((prev) =>
      prev.map((vv) => {
        const active = Array.isArray(vv.complimentary_items) ? vv.complimentary_items : [];
        if (active.some((it) => Number(it.dish_id) === id)) return vv;
        return {
          ...vv,
          complimentary_items: [...active, { dish_id: id, variant_id: defaultVariantId ?? null, qty: 1 }],
        };
      }),
    );
  };

  const emptyHintFor = (group) => ({
    'Add-ons': 'Create add-on items first.',
    'Dishes': 'No other dishes yet.',
    'Beverages': 'No beverages yet.',
    'Combos': 'No combos yet.',
  }[group]);

  if (variants.length === 0) {
    return (
      <div className="border border-slate-200 rounded-lg p-4 text-center text-xs text-slate-500 italic bg-white">
        Add a variant first — complimentary items are configured per variant.
      </div>
    );
  }

  // In flat mode the variant label column is dropped — just the 4 pickers.
  const gridTemplate = flat
    ? `repeat(4, minmax(240px, 1fr))`
    : `200px repeat(4, minmax(240px, 1fr))`;

  return (
    // NOTE: no `overflow-hidden` here — it clipped the column pickers'
    // absolutely-positioned dropdown panels (the option list got cut off at the
    // matrix edge). The header carries its own top rounding instead.
    <div className="border border-slate-200 rounded-lg bg-white">
      {/* Header row */}
      <div
        className="grid items-end gap-0 px-3 py-3 bg-slate-50 border-b border-slate-200 rounded-t-lg"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        {!flat && (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 font-mono">
              Variant ↓
            </div>
          </div>
        )}
        {['Dishes', 'Beverages', 'Add-ons', 'Combos'].map((group) => (
          <div key={group} className="px-2">
            <div className="text-sm font-semibold text-slate-800">{group}</div>
            <div className="text-[10.5px] text-slate-500 mt-0.5 font-mono">
              {buckets[group].length} item{buckets[group].length === 1 ? '' : 's'} in master
            </div>
          </div>
        ))}
      </div>

      {/* Body: one row per variant */}
      {variants.map((v, vi) => {
        const isBase = vi === 0;
        const label = (v.variant_name || '').trim() || `Variant ${vi + 1}`;
        const activeItems = Array.isArray(v.complimentary_items) ? v.complimentary_items : [];
        const totalForVariant = activeItems.length;
        return (
          <div
            key={vi}
            className="grid items-start gap-0 px-3 py-3 border-b border-slate-100"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            {/* Variant label column — hidden in flat mode */}
            {!flat && (
              <div className="min-w-0 pr-3 self-start pt-1.5">
                <div className="flex items-baseline gap-1.5 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate" title={label}>
                    {label}
                  </div>
                  {isBase && (
                    <span className="text-[9px] font-bold tracking-wider px-1 py-0.5 rounded bg-blue-600 text-white font-mono shrink-0">
                      BASE
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-[10.5px] text-slate-500 mt-0.5 font-mono">
                  <span className="truncate" title={sizeLabel(v.size) || ''}>{sizeLabel(v.size) || '—'}</span>
                  <span>·</span>
                  <span className="truncate" title={unitLabel(v.unit) || ''}>{unitLabel(v.unit) || '—'}</span>
                </div>
                <div className="mt-2 text-[10.5px] text-slate-500">
                  {totalForVariant === 0
                    ? 'No bundles yet.'
                    : `${totalForVariant} bundled free.`}
                </div>
              </div>
            )}

            {/* 4 picker columns */}
            {Object.entries(buckets).map(([group, items]) => {
              const bucketIds = new Set(items.map((d) => Number(d.id)));
              const rowsInBucket = activeItems.filter((it) => bucketIds.has(Number(it.dish_id)));
              const rowsOutsideBucket = activeItems.filter((it) => !bucketIds.has(Number(it.dish_id)));

              const updateBucket = (nextRows) => {
                setVariantItems(vi, [...rowsOutsideBucket, ...nextRows]);
              };

              return (
                <div key={group} className="px-1.5">
                  {supportsVariants(group) ? (
                    <ComplimentaryVariantQtyPicker
                      items={items}
                      rows={rowsInBucket}
                      onChange={updateBucket}
                      onAdd={addItemToAllVariants}
                      emptyHint={emptyHintFor(group)}
                    />
                  ) : (
                    <RecommendationPicker
                      title={group}
                      items={items}
                      selectedIds={rowsInBucket.map((it) => it.dish_id)}
                      onChange={(picked) => {
                        // Adds mirror to every variant (same freebie for all
                        // sizes by default); removals stay local to this variant.
                        const pickedSet = new Set(picked.map(Number));
                        const currentSet = new Set(rowsInBucket.map((it) => Number(it.dish_id)));
                        picked
                          .map(Number)
                          .filter((id) => !currentSet.has(id))
                          .forEach((id) => addItemToAllVariants(id, null));
                        const removed = [...currentSet].filter((id) => !pickedSet.has(id));
                        if (removed.length) {
                          const keep = rowsInBucket.filter(
                            (it) => !removed.includes(Number(it.dish_id)),
                          );
                          updateBucket(keep);
                        }
                      }}
                      emptyHint={emptyHintFor(group)}
                      hideTitle
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// Variant + qty picker for the Dishes and Beverages columns of the
// Complimentary matrix. Each row carries { dish_id, variant_id, qty } so the
// same item can be bundled in multiple sizes / quantities.
function ComplimentaryVariantQtyPicker({ items, rows, onChange, onAdd, emptyHint }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const trimmed = search.trim();
  const itemsById = useMemo(
    () => Object.fromEntries((items || []).map((i) => [Number(i.id), i])),
    [items],
  );

  // While the dropdown is open, show pickable items — all of them before the
  // user types, then filtered by the query. Items already bundled for this
  // variant column are excluded (edit the row's variant selector for sizes).
  const pickedIds = new Set(rows.map((r) => Number(r.dish_id)));
  const matches = open
    ? (items || [])
        .filter((d) => !pickedIds.has(Number(d.id)))
        .filter((d) => !trimmed || d.name.toLowerCase().includes(trimmed.toLowerCase()))
    : [];

  // Close the dropdown when clicking outside the picker.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const addRow = (dishIdRaw) => {
    const dishId = Number(dishIdRaw);
    const dish = itemsById[dishId];
    if (!dish) return;
    const firstVariant = (dish.variants && dish.variants[0]) || null;
    // Adding mirrors to every variant by default (same freebie for all sizes);
    // the user can remove it from individual variants afterwards so they differ.
    if (onAdd) {
      onAdd(dishId, firstVariant?.id ?? null);
    } else {
      onChange([...rows, { dish_id: dishId, variant_id: firstVariant?.id ?? null, qty: 1 }]);
    }
    setSearch('');
  };

  const updateRow = (index, patch) => {
    const next = rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange(next);
  };

  const removeRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const variantLabel = (variant) =>
    (variant?.variant_name || '').trim() || variant?.size || `V${variant?.id || ''}`;

  return (
    <div className="space-y-2" ref={rootRef}>
      {/* Count header — mirrors the Add-ons / Combos columns so all four
          picker columns share the same top-right "X selected" affordance. */}
      <div className="flex items-center justify-end">
        <span className="text-[11px] text-slate-500">{rows.length} selected</span>
      </div>

      {/* Dropdown: select-style trigger that opens the list of items with a
          search box at the top — same pattern as the Recommendations pickers.
          Adds a row with the first variant + qty=1 when an item is clicked. */}
      <div className="relative">
        <button
          type="button"
          onClick={() => items.length > 0 && setOpen((o) => !o)}
          disabled={items.length === 0}
          className="w-full flex items-center justify-between gap-2 rounded-md border border-input bg-background px-3 h-8 text-sm text-muted-foreground hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="truncate">{items.length === 0 ? emptyHint : 'Select item…'}</span>
          <ChevronDown className={`h-4 w-4 shrink-0 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute z-30 mt-1 w-full rounded-md border bg-white shadow-md overflow-hidden">
            <div className="relative border-b">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
              <input
                autoFocus
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-2 h-8 text-sm bg-transparent outline-none"
              />
            </div>
            <div className="max-h-[170px] overflow-y-auto divide-y">
              {matches.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-500 text-center italic">
                  {(items || []).length === 0 ? 'Nothing to add.' : (trimmed ? 'No matches.' : 'All added.')}
                </div>
              ) : (
                matches.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => addRow(d.id)}
                    className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-slate-50"
                  >
                    <Plus className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{d.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="text-[10.5px] text-slate-400 italic px-1">No rows yet.</div>
      ) : (
        rows.map((row, i) => {
          const dish = itemsById[Number(row.dish_id)];
          const variants = (dish?.variants) || [];
          return (
            <div
              key={`${row.dish_id}-${i}`}
              className="flex items-center gap-1 px-1.5 py-1 rounded-md border border-slate-200 bg-white"
            >
              <div
                className="text-[11px] font-medium text-slate-800 truncate flex-1 min-w-0"
                title={dish?.name || `Dish #${row.dish_id}`}
              >
                {dish?.name || `Dish #${row.dish_id}`}
              </div>
              {variants.length > 0 ? (
                <Select
                  value={row.variant_id != null ? String(row.variant_id) : ''}
                  onValueChange={(value) => updateRow(i, { variant_id: Number(value) })}
                >
                  <SelectTrigger className="h-6 w-20 text-[10.5px] px-1.5">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {variants.map((va) => (
                      <SelectItem key={va.id} value={String(va.id)}>
                        {variantLabel(va)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-[10px] text-slate-400 italic">no sizes</span>
              )}
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={row.qty}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/[^0-9]/g, '');
                  updateRow(i, { qty: cleaned === '' ? '' : Math.max(1, parseInt(cleaned, 10) || 1) });
                }}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value, 10) < 1) {
                    updateRow(i, { qty: 1 });
                  }
                }}
                className="h-6 w-12 text-[11px] text-center px-1"
                aria-label="Quantity"
                title="Quantity"
              />
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="p-1 text-slate-400 hover:text-rose-600"
                aria-label="Remove row"
                title="Remove"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          );
        })
      )}
    </div>
  );
}

function LocationsPicker({ locations, selectedIds, onChange }) {
  const [search, setSearch] = useState('');
  const allIds = locations.map((l) => l.id);
  const allSelected = locations.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const someSelected = !allSelected && selectedIds.length > 0;
  const filtered = search.trim()
    ? locations.filter((l) => l.name.toLowerCase().includes(search.toLowerCase()))
    : locations;

  const toggle = (id) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  const toggleAll = () => onChange(allSelected ? [] : allIds);

  return (
    <div className="space-y-2">
      {/* Search + tri-state select-all tick (no text label on the tick) */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search branches…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-xs w-full pl-8"
            disabled={locations.length === 0}
          />
        </div>
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = someSelected; }}
          onChange={toggleAll}
          disabled={locations.length === 0}
          title={allSelected ? 'Deselect all branches' : 'Select all branches'}
          aria-label="Select all branches"
          className="h-4 w-4 accent-blue-600 cursor-pointer disabled:cursor-not-allowed shrink-0"
        />
      </div>

      {/* Branch chips — selected highlighted, unselected subdued */}
      <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
        {locations.length === 0 ? (
          <div className="text-xs text-slate-500 italic py-2 px-1">
            No branches yet — add them under Settings → Locations.
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-xs text-slate-500 italic py-2 px-1">
            No matches.
          </div>
        ) : (
          filtered.map((l) => {
            const on = selectedIds.includes(l.id);
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => toggle(l.id)}
                aria-pressed={on}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs transition-colors ${
                  on
                    ? 'border-blue-500 bg-blue-100 text-blue-800 font-medium shadow-sm'
                    : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                }`}
              >
                <span className="truncate max-w-[180px]" title={l.name}>{l.name}</span>
                {l.is_primary && (
                  <span className={`text-[9px] font-semibold px-1 py-0.5 rounded-full shrink-0 ${
                    on
                      ? 'bg-white/70 text-amber-700 border border-amber-300'
                      : 'bg-amber-100 text-amber-700 border border-amber-200'
                  }`}>
                    PRIMARY
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// Search-driven multi-pick: chips for what's already picked, then a search
// box that surfaces matches as you type. The full unselected list is never
// rendered up-front (keeps long catalogues from blowing up vertical space).
// `emptyHint` is intentionally not surfaced as a card-filling empty state —
// the absence of matches just shows nothing, which keeps the layout tight.
function RecommendationPicker({ title, items, selectedIds, onChange, hideTitle = false }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const trimmed = search.trim();
  const itemById = new Map(items.map((d) => [d.id, d]));

  // Selected chips reflect picks in the order the user added them.
  const selectedItems = selectedIds
    .map((id) => itemById.get(id))
    .filter(Boolean);

  // While the dropdown is open, show unselected items — all of them before the
  // user types, then filtered by the query.
  const matches = open
    ? items
        .filter((d) => !selectedIds.includes(d.id))
        .filter((d) => !trimmed || d.name.toLowerCase().includes(trimmed.toLowerCase()))
    : [];

  // Close the dropdown when clicking outside the picker.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const add = (id) => {
    if (!selectedIds.includes(id)) onChange([...selectedIds, id]);
    setSearch('');  // keep the dropdown open so several can be added in a row
  };
  const remove = (id) => onChange(selectedIds.filter((x) => x !== id));

  return (
    <div className="space-y-2" ref={rootRef}>
      <div className={`flex items-center ${hideTitle ? 'justify-end' : 'justify-between'}`}>
        {!hideTitle && <Label className="text-sm font-medium">{title}</Label>}
        <span className="text-[11px] text-slate-500">{selectedIds.length} selected</span>
      </div>

      {/* Dropdown: a select-style trigger that opens the list of this dish
          type, with a search box at the top of the panel. */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-2 rounded-md border border-input bg-background px-3 h-8 text-sm text-muted-foreground hover:bg-slate-50"
        >
          <span>Select {(title || 'item').toLowerCase()}…</span>
          <ChevronDown className={`h-4 w-4 shrink-0 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute z-30 mt-1 w-full rounded-md border bg-white shadow-md overflow-hidden">
            <div className="relative border-b">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
              <input
                autoFocus
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-2 h-8 text-sm bg-transparent outline-none"
              />
            </div>
            <div className="max-h-[170px] overflow-y-auto divide-y">
              {matches.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-500 text-center italic">
                  {items.length === 0 ? 'Nothing to add.' : (trimmed ? 'No matches.' : 'All added.')}
                </div>
              ) : (
                matches.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => add(d.id)}
                    className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-slate-50"
                  >
                    <Plus className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{d.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected chips sit below the search to mirror the Dishes / Beverages
          columns. When empty, the same "No rows yet." italic hint stands in
          so all four picker columns share one empty state. */}
      {selectedItems.length === 0 ? (
        <div className="text-[10.5px] text-slate-400 italic px-1">No rows yet.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {selectedItems.map((d) => (
            <span
              key={d.id}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-accent border border-primary/30 text-xs text-foreground"
              title={d.name}
            >
              <span className="truncate max-w-[140px]">{d.name}</span>
              <button
                type="button"
                onClick={() => remove(d.id)}
                className="h-4 w-4 inline-flex items-center justify-center rounded-full text-slate-500 hover:text-rose-600 hover:bg-rose-50"
                aria-label={`Remove ${d.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


export default AddEditDish;
